/* eslint-disable */
// ============================================================
// El Campesino - Recipe Cost Calculator (approved mockup, verbatim)
// ============================================================
// This IS the approved mockup source, unchanged, except for a thin, additive
// BeeNexa integration layer:
//   - lucide-react is imported from its UMD build (CRA 3.4 / webpack 4 cannot
//     consume lucide's strict-ESM .mjs entry).
//   - a scoped Tailwind stylesheet is imported (utilities scoped under
//     #ec-recipe-cost-root so they can't leak into the rest of BeeNexa).
//   - the export is RecipeCostApp({ locationId }); RecipeCostCalculatorInner
//     runs a fire-and-forget backend sync (Master List + recipes) per tenant.
//     Offline / no locationId => pure in-memory seed data (demo mode).
import React, { useState, useMemo, useEffect, useRef, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import {
  Search, Plus, Copy, Trash2, ArrowLeft, ChefHat,
  Flame, Users, ChevronRight, ChevronDown, FileText, CheckCircle2, Check, X,
  Sparkles, LayoutDashboard, BookOpen, BarChart3,
  TrendingUp, TrendingDown, AlertTriangle, DollarSign,
  Target, Activity, Package, ShoppingBag, Layers, Leaf,
  Download, Loader2, Globe
} from 'lucide-react/dist/umd/lucide-react.js';
import './recipeCost.tailwind.css';
import RecipeCostApi, { serverToClientIngredient, clientToServerIngredient } from './recipeCostApi';

// ============================================================
// I18N — INTERNATIONALIZATION
// ============================================================
// Pattern matches react-i18next so Hasnain's production swap is mechanical:
// - This file's TRANSLATIONS object → split into en.json / es.json
// - useT() hook → swap to useTranslation() from react-i18next
// - Every t('key') call works identically in both implementations
// Language preference is persisted to localStorage so Don Nacho's choice sticks.

const TRANSLATIONS = {
  en: {
    // App identity
    'app.brand': 'EL CAMPESINO',
    'app.title': 'Recipe Cost Calculator',

    // Tabs
    'tab.recipeCosts': 'Recipe Costs',
    'tab.recipes': 'Recipes',
    'tab.ingredients': 'Ingredients',
    'tab.menu': 'Menu',
    'tab.steps': 'Steps',
    'tab.analytics': 'Analytics',

    // Module subtitles
    'module.recipeCosts.subtitle': 'Per-recipe cost, margin, and profitability snapshot.',
    'dashboard.recipePerformance': 'Recipe Performance',
    'dashboard.sellingAt': 'Selling at',
    'dashboard.actualMargin': 'Actual margin',
    'dashboard.profit': 'profit',
    'dashboard.loss': 'loss',
    'dashboard.setCurrentPrice': 'Set current price to track profitability',
    'dashboard.suggestedPrices': 'Suggested Prices by Margin',
    'dashboard.alertRaise': 'Margin {m} — raise to {p} min',
    'dashboard.alertConsider': 'Margin {m} — consider {p}',
    'module.recipes.subtitle': 'Production formulas, ingredients, yields, and real costs.',
    'module.ingredients.subtitle': 'Master list of all purchasable ingredients with vendor, price, and merma.',
    'module.menu.subtitle': 'Menu items, combos, and tacos with computed margins.',
    'module.steps.subtitle': 'Cooking playbooks — bilingual step-by-step instructions.',
    'module.analytics.subtitle': 'Business overview, key metrics, alerts, and pricing strategy.',

    // Common actions
    'action.new': 'New',
    'action.add': 'Add',
    'action.edit': 'Edit',
    'action.save': 'Save',
    'action.cancel': 'Cancel',
    'action.delete': 'Delete',
    'action.duplicate': 'Duplicate',
    'action.close': 'Close',
    'action.create': 'Create',
    'action.search': 'Search',
    'action.change': 'Change',
    'action.skip': 'Skip',
    'action.accept': 'Accept',
    'action.apply': 'Apply',
    'action.revert': 'Revert',
    'action.confirm': 'Confirm',
    'action.print': 'Print',
    'action.download': 'Download',
    'action.back': 'Back',
    'action.viewAll': 'View All',
    'action.openInEditor': 'Open in Editor',
    'action.autoSaved': 'Auto-saved',

    // Recipe-specific
    'recipe.new': 'New Recipe',
    'recipe.addNew': 'Add new recipe',
    'recipe.filteredFrom': 'filtered from {n} total',
    'recipe.allRecipes': 'All Recipes',
    'recipe.searchPlaceholder': 'Search recipes...',
    'recipe.recipesCount': '{n} recipes',
    'recipe.recipesCountFiltered': '{n} recipes, filtered from {total}',
    'recipe.ingredients': 'Ingredients',
    'recipe.ingredientsCount': '{n} ingredients',
    'recipe.yield': 'Yield',
    'recipe.realCost': 'Real Cost',
    'recipe.realCostPerUnit': 'Real Cost / Unit',
    'recipe.margin': 'Margin',
    'recipe.profit': 'Profit',
    'recipe.profitPerUnit': 'Profit / Unit',
    'recipe.sellingPrice': 'Selling Price',
    'recipe.currentPrice': 'Current Price',
    'recipe.costBreakdown': 'Cost Breakdown',
    'recipe.pricingTiers': 'Pricing Tiers',
    'recipe.currentPerformance': 'Current Performance',
    'recipe.healthStatus': 'Health Status',
    'recipe.targetMargin': 'Target Margin',
    'recipe.qty': 'Qty',
    'recipe.quantity': 'Quantity',
    'recipe.unit': 'Unit',
    'recipe.name': 'Name',
    'recipe.totalCost': 'Total Cost',
    'recipe.unitCost': 'Unit Cost',
    'recipe.addIngredient': 'Add Ingredient',
    'recipe.deleteRecipeConfirm': 'Delete this recipe?',
    'recipe.deleteMenuItemConfirm': 'Delete this menu item?',
    'recipe.removeEquipmentConfirm': 'Remove this equipment?',
    'recipe.removeStepConfirm': 'Remove this step?',
    'recipe.ingredientNamePlaceholder': 'Ingredient name',
    'recipe.fromMasterList': 'from Master List',
    'recipe.masterList': 'Master List',
    'recipe.operationalCost': 'Operational Cost',
    'recipe.operationalCostSub': 'Labor, gas, electricity, packaging. Prorated across yield.',
    'recipe.realCostFinal': 'Real Cost Final',
    'recipe.realCostFinalSub': 'Ingredients + operational combined',
    'recipe.sellingByMargin': 'Selling Price by Margin',
    'recipe.sellingByMarginSub': 'Price = Cost ÷ (1 − margin). Select a target margin.',
    'recipe.marketPricing': 'Market Pricing',
    'recipe.marketPricingSub': 'Compare your price vs real cost and competitors',

    // Categories (recipe categories)
    'category.all': 'All',
    'category.carnes': 'Carnes',
    'category.aves': 'Aves',
    'category.salsas': 'Salsas',
    'category.guarniciones': 'Guarniciones',
    'category.bebidas': 'Bebidas',
    'category.postres': 'Postres',
    'category.tortillas': 'Tortillas',
    'category.otros': 'Otros',

    // Health/Status
    'health.healthy': 'Healthy',
    'health.tight': 'Tight Margin',
    'health.losing': 'Losing Money',
    'health.none': 'No Price Set',
    'health.healthyShort': 'healthy',
    'health.tightShort': 'tight',
    'health.losingShort': 'losing',

    // Ingredients Master List
    'ingredient.new': 'New Ingredient',
    'ingredient.editIngredient': 'Edit Ingredient',
    'ingredient.newIngredient': 'New Ingredient',
    'ingredient.searchPlaceholder': 'Search ingredients...',
    'ingredient.count': '{n} ingredients',
    'ingredient.unused': 'UNUSED',
    'ingredient.realCostShort': 'Real',
    'ingredient.buy': 'Buy',
    'ingredient.use': 'Use',
    'ingredient.purchasePrice': 'Purchase Price',
    'ingredient.purchaseQty': 'Purchase Qty',
    'ingredient.containsUnit': 'Contains Unit',
    'ingredient.recipeUnit': 'Recipe Unit',
    'ingredient.recipeUnitHelp': 'Unit used in recipes',
    'ingredient.merma': 'Merma % (waste)',
    'ingredient.mermaHelp': '% lost in cleaning/prep',
    'ingredient.notes': 'Notes (optional)',
    'ingredient.notesPlaceholder': 'Vendor, preferred brand, prep tips...',
    'ingredient.howRecipesUseIt': 'How recipes use it',
    'ingredient.purchaseInfo': 'Purchase information',
    'ingredient.autoCalculated': 'Auto-calculated',
    'ingredient.costPer': 'Cost per',
    'ingredient.nameEn': 'Name (English)',
    'ingredient.nameEs': 'Name (Spanish)',
    'ingredient.nameEsHelp': 'Used for migration matching and Spanish UI',
    'ingredient.category': 'Category',
    'ingredient.example': 'e.g.',
    'ingredient.bagContains': 'bag contains',
    'ingredient.noneFound': 'No ingredients found',
    'ingredient.tryDifferent': 'Try a different search term.',
    'ingredient.addFirst': 'Add your first ingredient to get started.',
    'ingredient.deleteUsageConfirm': '{name} is used in {n} recipe(s). Are you sure you want to delete it?',
    'ingredient.revertConfirm': 'Revert all recipes to the pre-migration backup? Migrated ingredient links will be removed.',

    // Ingredient categories (Master List buckets)
    'ingCat.meats': 'Meats',
    'ingCat.produce': 'Produce',
    'ingCat.spices': 'Spices',
    'ingCat.dairy': 'Dairy',
    'ingCat.dryGoods': 'Dry Goods',
    'ingCat.liquids': 'Liquids',
    'ingCat.other': 'Other',

    // Migration
    'migration.migrateRecipes': 'Migrate Recipes',
    'migration.migrate': 'Migrate',
    'migration.title': 'Migrate Recipes to Master List',
    'migration.subtitle': 'Review proposed mappings · No changes applied until you approve.',
    'migration.backupCreated': 'Backup created.',
    'migration.backupNotice': 'Your original recipes are saved before any changes are applied. You can Revert Migration from the Ingredients tab at any time.',
    'migration.backupBadge': 'BACKUP',
    'migration.revert': 'Revert Migration',
    'migration.revertShort': 'Revert',
    'migration.progress': 'Migration Progress',
    'migration.progressOf': '{resolved} of {total} resolved',
    'migration.accepted': 'accepted',
    'migration.pending': 'pending',
    'migration.skipped': 'skipped',
    'migration.new': 'new',
    'migration.focusPending': 'Focus on {n} pending',
    'migration.allRecipes': 'All Recipes ({n})',
    'migration.allStatuses': 'All Statuses',
    'migration.acceptedOnly': 'Accepted only',
    'migration.pendingOnly': 'Pending review only',
    'migration.needsReview': 'Needs Review (low confidence)',
    'migration.currentInRecipe': 'Current in recipe',
    'migration.masterListMatch': 'Master List match',
    'migration.diff': 'Diff',
    'migration.noMatchFound': 'No match found in Master List',
    'migration.noMatchHelp': 'Create <b>"{name}"</b> as a new ingredient, search the Master List, or skip to keep it as-is.',
    'migration.skipped.kept': 'Skipped — kept as-is',
    'migration.unitMismatch': '⚠ Unit mismatch: {from} → {to}',
    'migration.inclMerma': 'incl. {n}% merma',
    'migration.acceptedBadge': 'Accepted',
    'migration.skippedBadge': 'Skipped',
    'migration.createNew': 'Create New',
    'migration.createNewTitle': 'Create "{name}" as a new Master List ingredient',
    'migration.renamesTo': 'Renames to',
    'migration.renameToHeader': 'Rename to:',
    'migration.searchOrCreate': 'Search Master List or create a new ingredient',
    'migration.applyMigration': 'Apply Migration',
    'migration.pendingFooter': '{n} pending review.',
    'migration.pendingFooterAction': 'Resolve all pending decisions to enable Apply.',
    'migration.readyFooter': 'Ready to apply.',
    'migration.readyFooterDetails': '{accepted} accepted · {skipped} skipped. Backup is saved.',
    'migration.confirmTitle': 'Confirm Migration',
    'migration.confirmIntro': 'You\'re about to apply <b>{n} migration{s}</b> to your recipes.',
    'migration.confirmCounters.accepted': 'Accepted:',
    'migration.confirmCounters.skipped': 'Skipped (kept as-is):',
    'migration.confirmCounters.created': 'New ingredients created:',
    'migration.confirmHelp': 'Recipe costs will be recalculated from the Master List for migrated ingredients. Your backup is saved — you can <b>Revert</b> at any time from the Ingredients tab.',
    'migration.confirmYes': 'Yes, Apply Migration',
    'migration.pendingMustResolve': '{n} pending decisions must be resolved first',
    'migration.applyTooltip': 'Apply migration to recipes',
    'migration.skipTooltip': 'Keep this ingredient hardcoded in the recipe',
    'migration.allDone.title': 'All pending resolved! 🎉',
    'migration.allDone.body': 'Every mapping has a decision. You can review the others or click <b>Apply Migration</b> below to commit.',
    'migration.allDone.viewAll': 'View All Mappings',
    'migration.allDone.reviewAccepted': 'Review Accepted',
    'migration.noMappingsFilter': 'No mappings match your filters.',
    'migration.clearFilters': 'Clear filters',
    'migration.revertTitle': 'Restore original recipes from the backup created before migration',
    'migration.totalMappings': 'Total mappings:',
    'migration.quickPresets': 'Quick:',
    'migration.quickEN': 'EN',
    'migration.quickES': 'ES',
    'migration.quickOrig': 'Orig',
    'migration.useMasterEn': 'Use Master English name',
    'migration.useMasterEs': 'Use Master Spanish name',
    'migration.useOriginal': 'Use original recipe name',
    'migration.willCreateNew': 'Create new ingredient from this recipe ingredient',
    'migration.noMatchesFor': 'No matches for "{q}"',
    'migration.useGreenButton': 'Use the green button above to create it as a new ingredient.',
    'migration.pickADifferent': 'Pick a different ingredient',
    'migration.orPickExisting': 'OR PICK EXISTING',
    'migration.searchMasterList': 'Search Master List...',
    'migration.createAsNew': 'Create "{name}" as new ingredient',
    'migration.createGeneric': 'Create new ingredient',

    // Confidence levels
    'confidence.exact': 'Exact Match',
    'confidence.high': 'High Confidence',
    'confidence.medium': 'Needs Review',
    'confidence.low': 'Needs Review',
    'confidence.none': 'No Match',

    // Analytics
    'analytics.avgMargin': 'Avg Margin',
    'analytics.recipes': 'Recipes',
    'analytics.losingMoney': 'Losing Money',
    'analytics.topEarner': 'Top Earner',
    'analytics.acrossPriced': 'Across {n} priced {label}',
    'analytics.recipe': 'recipe',
    'analytics.recipeP': 'recipes',
    'analytics.healthy': '{n} healthy',
    'analytics.tight': '{n} tight',
    'analytics.noIssues': 'No critical issues 🎉',
    'analytics.marginBelow': 'Margin below 25%',
    'analytics.noData': 'No data yet',
    'analytics.needsAttention': 'Needs Attention',
    'analytics.itemRequires': '{n} item requires your review',
    'analytics.itemsRequire': '{n} items require your review',
    'analytics.critical': 'Critical',
    'analytics.warning': 'Warning',
    'analytics.info': 'Info',
    'analytics.moreAlerts': '+ {n} more alerts — review individual recipes for details',
    'analytics.moreAlert': '+ {n} more alert — review individual recipes for details',
    'analytics.allHealthy': 'All recipes look healthy 🎉',
    'analytics.allHealthyBody': 'No items losing money, no tight margins, all priced.',
    'analytics.noneYet': 'No analytics available yet',
    'analytics.addPrices': 'Add current prices to recipes to unlock analytics.',
    'analytics.marginRanking': 'Margin Ranking',
    'analytics.priceOptimization': 'Price Optimization',
    'analytics.printReport': 'Print Report',
    'analytics.alert.losing': 'Losing money — margin is {m}% (below 25% threshold)',
    'analytics.alert.losingDetail': 'Profit: {sign}${amt}/unit',
    'analytics.alert.tight': 'Tight margin at {m}% — below healthy 35% threshold',
    'analytics.alert.tightDetail': 'Profit: ${amt}/unit',
    'analytics.alert.noPrice': 'No selling price set — can\'t calculate margin or profit',
    'analytics.alert.noPriceDetail': 'Add a price in the recipe to unlock analytics',

    // PDF
    'pdf.preview': 'PDF Preview',
    'pdf.downloadTitle': 'Download PDF file',
    'pdf.printTitle': 'Send to printer',
    'pdf.closeTitle': 'Close preview',
    'pdf.loading': 'Loading PDF tools...',
    'pdf.errorTitle': 'Could not generate PDF',
    'pdf.helpMobile': 'Tap Download to save · Tap Print to send to printer',
    'pdf.docHeader.recipeBreakdown': 'Recipe Cost Breakdown',
    'pdf.docHeader.menuItem': 'Menu Item Cost Sheet',
    'pdf.docHeader.businessOverview': 'Business Overview & Recipe Profitability',
    'pdf.docType.costReport': 'COST REPORT',
    'pdf.docType.menuItem': 'MENU ITEM',
    'pdf.docType.analytics': 'ANALYTICS',
    'pdf.section.ingredients': 'Ingredients',
    'pdf.section.ingredientsTotal': 'Ingredients Total',
    'pdf.section.costBreakdownPerUnit': 'Cost Breakdown (per unit)',
    'pdf.section.pricingTiers': 'Pricing Tiers',
    'pdf.section.currentPerformance': 'Current Performance',
    'pdf.section.components': 'Components',
    'pdf.section.pricingPerformance': 'Pricing Performance',
    'pdf.section.marginRanking': 'Margin Ranking',
    'pdf.section.losing': '🔴 Losing Money — Critical',
    'pdf.section.losingNote': 'These recipes are pricing below their real cost. Review pricing or cost structure immediately.',
    'pdf.col.ingredient': 'Ingredient',
    'pdf.col.qty': 'Qty',
    'pdf.col.unit': 'Unit',
    'pdf.col.totalCost': 'Total Cost',
    'pdf.col.targetMargin': 'Target Margin',
    'pdf.col.sellingPrice': 'Selling Price',
    'pdf.col.profitPerUnit': 'Profit / unit',
    'pdf.col.recipe': 'Recipe',
    'pdf.col.margin': 'Margin',
    'pdf.col.status': 'Status',
    'pdf.col.component': 'Component',
    'pdf.col.cost': 'Cost',
    'pdf.col.lossPerUnit': 'Loss/Unit',
    'pdf.field.ingredientCost': 'Ingredient Cost',
    'pdf.field.labor': 'Labor Cost',
    'pdf.field.utilities': 'Utilities (gas/elec)',
    'pdf.field.packaging': 'Packaging',
    'pdf.field.operational': 'Operational',
    'pdf.field.realCostPerUnit': 'Real Cost per Unit',
    'pdf.field.currentSellingPrice': 'Current Selling Price',
    'pdf.field.totalCost': 'Total Cost',
    'pdf.brandTagline': 'El Campesino · Tortillería',
    'pdf.footerLeft': 'El Campesino · Recipe Cost Calculator',
    'pdf.footerRight': 'Generated {date} · Internal use only',
    'pdf.printGenerate': 'Generate PDF — preview, download, or print',
    'pdf.analyticsReport': 'Analytics Report',
    'pdf.totalRecipes': '{n} total recipes · {m} with current pricing',
    'pdf.noPriced': 'No priced recipes available',

    // Misc
    'misc.units': 'Units',
    'misc.fontMono': 'Mono',
    'misc.from': 'from',
    'misc.to': 'to',
    'misc.of': 'of',
  },

  es: {
    // App identity
    'app.brand': 'EL CAMPESINO',
    'app.title': 'Calculadora de Costo de Recetas',

    // Tabs
    'tab.recipeCosts': 'Costos de Recetas',
    'tab.recipes': 'Recetas',
    'tab.ingredients': 'Ingredientes',
    'tab.menu': 'Menú',
    'tab.steps': 'Pasos',
    'tab.analytics': 'Analíticas',

    // Module subtitles
    'module.recipeCosts.subtitle': 'Costo por receta, margen y vista de rentabilidad.',
    'dashboard.recipePerformance': 'Desempeño por Receta',
    'dashboard.sellingAt': 'Vendiendo a',
    'dashboard.actualMargin': 'Margen actual',
    'dashboard.profit': 'ganancia',
    'dashboard.loss': 'pérdida',
    'dashboard.setCurrentPrice': 'Asigna un precio actual para ver rentabilidad',
    'dashboard.suggestedPrices': 'Precios Sugeridos por Margen',
    'dashboard.alertRaise': 'Margen {m} — sube a {p} mínimo',
    'dashboard.alertConsider': 'Margen {m} — considera {p}',
    'module.recipes.subtitle': 'Fórmulas de producción, ingredientes, rendimientos y costos reales.',
    'module.ingredients.subtitle': 'Lista maestra de todos los ingredientes con proveedor, precio y merma.',
    'module.menu.subtitle': 'Platillos, combos y tacos con márgenes calculados.',
    'module.steps.subtitle': 'Manuales de cocina — instrucciones bilingües paso a paso.',
    'module.analytics.subtitle': 'Vista general del negocio, métricas clave, alertas y estrategia de precios.',

    // Common actions
    'action.new': 'Nuevo',
    'action.add': 'Agregar',
    'action.edit': 'Editar',
    'action.save': 'Guardar',
    'action.cancel': 'Cancelar',
    'action.delete': 'Eliminar',
    'action.duplicate': 'Duplicar',
    'action.close': 'Cerrar',
    'action.create': 'Crear',
    'action.search': 'Buscar',
    'action.change': 'Cambiar',
    'action.skip': 'Omitir',
    'action.accept': 'Aceptar',
    'action.apply': 'Aplicar',
    'action.revert': 'Revertir',
    'action.confirm': 'Confirmar',
    'action.print': 'Imprimir',
    'action.download': 'Descargar',
    'action.back': 'Volver',
    'action.viewAll': 'Ver Todo',
    'action.openInEditor': 'Abrir en Editor',
    'action.autoSaved': 'Auto-guardado',

    // Recipe-specific
    'recipe.new': 'Nueva Receta',
    'recipe.addNew': 'Agregar nueva receta',
    'recipe.filteredFrom': 'filtradas de {n} totales',
    'recipe.allRecipes': 'Todas las Recetas',
    'recipe.searchPlaceholder': 'Buscar recetas...',
    'recipe.recipesCount': '{n} recetas',
    'recipe.recipesCountFiltered': '{n} recetas, filtradas de {total}',
    'recipe.ingredients': 'Ingredientes',
    'recipe.ingredientsCount': '{n} ingredientes',
    'recipe.yield': 'Rendimiento',
    'recipe.realCost': 'Costo Real',
    'recipe.realCostPerUnit': 'Costo Real / Unidad',
    'recipe.margin': 'Margen',
    'recipe.profit': 'Ganancia',
    'recipe.profitPerUnit': 'Ganancia / Unidad',
    'recipe.sellingPrice': 'Precio de Venta',
    'recipe.currentPrice': 'Precio Actual',
    'recipe.costBreakdown': 'Desglose de Costos',
    'recipe.pricingTiers': 'Niveles de Precio',
    'recipe.currentPerformance': 'Desempeño Actual',
    'recipe.healthStatus': 'Estado de Salud',
    'recipe.targetMargin': 'Margen Objetivo',
    'recipe.qty': 'Cant.',
    'recipe.quantity': 'Cantidad',
    'recipe.unit': 'Unidad',
    'recipe.name': 'Nombre',
    'recipe.totalCost': 'Costo Total',
    'recipe.unitCost': 'Costo Unitario',
    'recipe.addIngredient': 'Agregar Ingrediente',
    'recipe.deleteRecipeConfirm': '¿Eliminar esta receta?',
    'recipe.deleteMenuItemConfirm': '¿Eliminar este platillo?',
    'recipe.removeEquipmentConfirm': '¿Quitar este equipo?',
    'recipe.removeStepConfirm': '¿Quitar este paso?',
    'recipe.ingredientNamePlaceholder': 'Nombre del ingrediente',
    'recipe.fromMasterList': 'de Lista Maestra',
    'recipe.masterList': 'Lista Maestra',
    'recipe.operationalCost': 'Costo Operativo',
    'recipe.operationalCostSub': 'Mano de obra, gas, luz, empaque. Prorrateado por rendimiento.',
    'recipe.realCostFinal': 'Costo Real Final',
    'recipe.realCostFinalSub': 'Ingredientes + operativo combinados',
    'recipe.sellingByMargin': 'Precio de Venta por Margen',
    'recipe.sellingByMarginSub': 'Precio = Costo ÷ (1 − margen). Selecciona un margen objetivo.',
    'recipe.marketPricing': 'Precio de Mercado',
    'recipe.marketPricingSub': 'Compara tu precio con el costo real y competidores',

    // Categories
    'category.all': 'Todas',
    'category.carnes': 'Carnes',
    'category.aves': 'Aves',
    'category.salsas': 'Salsas',
    'category.guarniciones': 'Guarniciones',
    'category.bebidas': 'Bebidas',
    'category.postres': 'Postres',
    'category.tortillas': 'Tortillas',
    'category.otros': 'Otros',

    // Health/Status
    'health.healthy': 'Saludable',
    'health.tight': 'Margen Apretado',
    'health.losing': 'Perdiendo Dinero',
    'health.none': 'Sin Precio',
    'health.healthyShort': 'saludable',
    'health.tightShort': 'apretado',
    'health.losingShort': 'perdiendo',

    // Ingredients Master List
    'ingredient.new': 'Nuevo Ingrediente',
    'ingredient.editIngredient': 'Editar Ingrediente',
    'ingredient.newIngredient': 'Nuevo Ingrediente',
    'ingredient.searchPlaceholder': 'Buscar ingredientes...',
    'ingredient.count': '{n} ingredientes',
    'ingredient.unused': 'SIN USO',
    'ingredient.realCostShort': 'Real',
    'ingredient.buy': 'Compra',
    'ingredient.use': 'Uso',
    'ingredient.purchasePrice': 'Precio de Compra',
    'ingredient.purchaseQty': 'Cantidad de Compra',
    'ingredient.containsUnit': 'Contiene Unidad',
    'ingredient.recipeUnit': 'Unidad de Receta',
    'ingredient.recipeUnitHelp': 'Unidad usada en las recetas',
    'ingredient.merma': 'Merma % (desperdicio)',
    'ingredient.mermaHelp': '% perdido en limpieza/preparación',
    'ingredient.notes': 'Notas (opcional)',
    'ingredient.notesPlaceholder': 'Proveedor, marca preferida, tips de preparación...',
    'ingredient.howRecipesUseIt': 'Cómo lo usan las recetas',
    'ingredient.purchaseInfo': 'Información de compra',
    'ingredient.autoCalculated': 'Calculado automáticamente',
    'ingredient.costPer': 'Costo por',
    'ingredient.nameEn': 'Nombre (Inglés)',
    'ingredient.nameEs': 'Nombre (Español)',
    'ingredient.nameEsHelp': 'Usado para emparejar en migración y UI en español',
    'ingredient.category': 'Categoría',
    'ingredient.example': 'ej.',
    'ingredient.bagContains': 'la bolsa contiene',
    'ingredient.noneFound': 'Sin ingredientes',
    'ingredient.tryDifferent': 'Intenta con otra búsqueda.',
    'ingredient.addFirst': 'Agrega tu primer ingrediente para empezar.',
    'ingredient.deleteUsageConfirm': '{name} se usa en {n} receta(s). ¿Seguro que quieres eliminarlo?',
    'ingredient.revertConfirm': '¿Revertir todas las recetas al respaldo previo a la migración? Se eliminarán los enlaces de ingredientes migrados.',

    // Ingredient categories
    'ingCat.meats': 'Carnes',
    'ingCat.produce': 'Frutas y Verduras',
    'ingCat.spices': 'Especias',
    'ingCat.dairy': 'Lácteos',
    'ingCat.dryGoods': 'Abarrotes',
    'ingCat.liquids': 'Líquidos',
    'ingCat.other': 'Otros',

    // Migration
    'migration.migrateRecipes': 'Migrar Recetas',
    'migration.migrate': 'Migrar',
    'migration.title': 'Migrar Recetas a Lista Maestra',
    'migration.subtitle': 'Revisa los emparejamientos · No se aplica nada hasta que apruebes.',
    'migration.backupCreated': 'Respaldo creado.',
    'migration.backupNotice': 'Tus recetas originales se guardan antes de aplicar cualquier cambio. Puedes Revertir Migración desde la pestaña de Ingredientes en cualquier momento.',
    'migration.backupBadge': 'RESPALDO',
    'migration.revert': 'Revertir Migración',
    'migration.revertShort': 'Revertir',
    'migration.progress': 'Progreso de Migración',
    'migration.progressOf': '{resolved} de {total} resueltos',
    'migration.accepted': 'aceptados',
    'migration.pending': 'pendientes',
    'migration.skipped': 'omitidos',
    'migration.new': 'nuevos',
    'migration.focusPending': 'Enfocar en {n} pendientes',
    'migration.allRecipes': 'Todas las Recetas ({n})',
    'migration.allStatuses': 'Todos los Estados',
    'migration.acceptedOnly': 'Solo aceptados',
    'migration.pendingOnly': 'Solo pendientes',
    'migration.needsReview': 'Necesita Revisión (baja confianza)',
    'migration.currentInRecipe': 'Actual en receta',
    'migration.masterListMatch': 'Coincidencia Lista Maestra',
    'migration.diff': 'Dif',
    'migration.noMatchFound': 'Sin coincidencia en Lista Maestra',
    'migration.noMatchHelp': 'Crea <b>"{name}"</b> como ingrediente nuevo, busca en la Lista Maestra, u omítelo para dejarlo como está.',
    'migration.skipped.kept': 'Omitido — se mantiene como está',
    'migration.unitMismatch': '⚠ Unidades incompatibles: {from} → {to}',
    'migration.inclMerma': 'incluye {n}% merma',
    'migration.acceptedBadge': 'Aceptado',
    'migration.skippedBadge': 'Omitido',
    'migration.createNew': 'Crear Nuevo',
    'migration.createNewTitle': 'Crear "{name}" como ingrediente nuevo en Lista Maestra',
    'migration.renamesTo': 'Se renombra a',
    'migration.renameToHeader': 'Renombrar a:',
    'migration.searchOrCreate': 'Busca en Lista Maestra o crea un ingrediente nuevo',
    'migration.applyMigration': 'Aplicar Migración',
    'migration.pendingFooter': '{n} pendientes de revisión.',
    'migration.pendingFooterAction': 'Resuelve todas las decisiones pendientes para habilitar Aplicar.',
    'migration.readyFooter': 'Listo para aplicar.',
    'migration.readyFooterDetails': '{accepted} aceptados · {skipped} omitidos. Respaldo guardado.',
    'migration.confirmTitle': 'Confirmar Migración',
    'migration.confirmIntro': 'Estás a punto de aplicar <b>{n} migración{s}</b> a tus recetas.',
    'migration.confirmCounters.accepted': 'Aceptados:',
    'migration.confirmCounters.skipped': 'Omitidos (se mantienen igual):',
    'migration.confirmCounters.created': 'Ingredientes nuevos creados:',
    'migration.confirmHelp': 'Los costos de receta se recalcularán desde la Lista Maestra para los ingredientes migrados. Tu respaldo está guardado — puedes <b>Revertir</b> en cualquier momento desde la pestaña de Ingredientes.',
    'migration.confirmYes': 'Sí, Aplicar Migración',
    'migration.pendingMustResolve': '{n} decisiones pendientes deben resolverse primero',
    'migration.applyTooltip': 'Aplicar migración a las recetas',
    'migration.skipTooltip': 'Mantener este ingrediente fijo en la receta',
    'migration.allDone.title': '¡Todo resuelto! 🎉',
    'migration.allDone.body': 'Cada emparejamiento tiene una decisión. Puedes revisar los demás o hacer clic en <b>Aplicar Migración</b> abajo para confirmar.',
    'migration.allDone.viewAll': 'Ver Todos los Emparejamientos',
    'migration.allDone.reviewAccepted': 'Revisar Aceptados',
    'migration.noMappingsFilter': 'Ningún emparejamiento coincide con tus filtros.',
    'migration.clearFilters': 'Limpiar filtros',
    'migration.revertTitle': 'Restaurar recetas originales desde el respaldo creado antes de la migración',
    'migration.totalMappings': 'Emparejamientos totales:',
    'migration.quickPresets': 'Rápido:',
    'migration.quickEN': 'EN',
    'migration.quickES': 'ES',
    'migration.quickOrig': 'Orig',
    'migration.useMasterEn': 'Usar nombre inglés de la Lista Maestra',
    'migration.useMasterEs': 'Usar nombre español de la Lista Maestra',
    'migration.useOriginal': 'Usar nombre original de la receta',
    'migration.willCreateNew': 'Crear ingrediente nuevo a partir de este ingrediente de receta',
    'migration.noMatchesFor': 'Sin coincidencias para "{q}"',
    'migration.useGreenButton': 'Usa el botón verde de arriba para crearlo como ingrediente nuevo.',
    'migration.pickADifferent': 'Elige un ingrediente diferente',
    'migration.orPickExisting': 'O ELIGE UNO EXISTENTE',
    'migration.searchMasterList': 'Buscar en Lista Maestra...',
    'migration.createAsNew': 'Crear "{name}" como ingrediente nuevo',
    'migration.createGeneric': 'Crear ingrediente nuevo',

    // Confidence levels
    'confidence.exact': 'Coincidencia Exacta',
    'confidence.high': 'Confianza Alta',
    'confidence.medium': 'Necesita Revisión',
    'confidence.low': 'Necesita Revisión',
    'confidence.none': 'Sin Coincidencia',

    // Analytics
    'analytics.avgMargin': 'Margen Promedio',
    'analytics.recipes': 'Recetas',
    'analytics.losingMoney': 'Perdiendo Dinero',
    'analytics.topEarner': 'Más Rentable',
    'analytics.acrossPriced': 'En {n} {label} con precio',
    'analytics.recipe': 'receta',
    'analytics.recipeP': 'recetas',
    'analytics.healthy': '{n} saludables',
    'analytics.tight': '{n} apretadas',
    'analytics.noIssues': 'Sin problemas críticos 🎉',
    'analytics.marginBelow': 'Margen menor a 25%',
    'analytics.noData': 'Sin datos aún',
    'analytics.needsAttention': 'Necesita Atención',
    'analytics.itemRequires': '{n} artículo requiere tu revisión',
    'analytics.itemsRequire': '{n} artículos requieren tu revisión',
    'analytics.critical': 'Crítico',
    'analytics.warning': 'Advertencia',
    'analytics.info': 'Aviso',
    'analytics.moreAlerts': '+ {n} alertas más — revisa las recetas individuales para detalles',
    'analytics.moreAlert': '+ {n} alerta más — revisa las recetas individuales para detalles',
    'analytics.allHealthy': 'Todas las recetas están sanas 🎉',
    'analytics.allHealthyBody': 'Nada perdiendo dinero, sin márgenes apretados, todo con precio.',
    'analytics.noneYet': 'Sin analíticas todavía',
    'analytics.addPrices': 'Agrega precios actuales a las recetas para desbloquear las analíticas.',
    'analytics.marginRanking': 'Ranking de Márgenes',
    'analytics.priceOptimization': 'Optimización de Precio',
    'analytics.printReport': 'Imprimir Reporte',
    'analytics.alert.losing': 'Perdiendo dinero — el margen es {m}% (debajo del umbral de 25%)',
    'analytics.alert.losingDetail': 'Ganancia: {sign}${amt}/unidad',
    'analytics.alert.tight': 'Margen apretado en {m}% — debajo del umbral saludable de 35%',
    'analytics.alert.tightDetail': 'Ganancia: ${amt}/unidad',
    'analytics.alert.noPrice': 'Sin precio de venta — no se puede calcular margen ni ganancia',
    'analytics.alert.noPriceDetail': 'Agrega un precio a la receta para desbloquear las analíticas',

    // PDF
    'pdf.preview': 'Vista Previa PDF',
    'pdf.downloadTitle': 'Descargar archivo PDF',
    'pdf.printTitle': 'Enviar a impresora',
    'pdf.closeTitle': 'Cerrar vista previa',
    'pdf.loading': 'Cargando herramientas PDF...',
    'pdf.errorTitle': 'No se pudo generar el PDF',
    'pdf.helpMobile': 'Toca Descargar para guardar · Toca Imprimir para enviar a la impresora',
    'pdf.docHeader.recipeBreakdown': 'Desglose de Costo de Receta',
    'pdf.docHeader.menuItem': 'Hoja de Costo de Platillo',
    'pdf.docHeader.businessOverview': 'Vista General del Negocio y Rentabilidad',
    'pdf.docType.costReport': 'REPORTE DE COSTO',
    'pdf.docType.menuItem': 'PLATILLO',
    'pdf.docType.analytics': 'ANALÍTICAS',
    'pdf.section.ingredients': 'Ingredientes',
    'pdf.section.ingredientsTotal': 'Total de Ingredientes',
    'pdf.section.costBreakdownPerUnit': 'Desglose de Costo (por unidad)',
    'pdf.section.pricingTiers': 'Niveles de Precio',
    'pdf.section.currentPerformance': 'Desempeño Actual',
    'pdf.section.components': 'Componentes',
    'pdf.section.pricingPerformance': 'Desempeño de Precios',
    'pdf.section.marginRanking': 'Ranking de Márgenes',
    'pdf.section.losing': '🔴 Perdiendo Dinero — Crítico',
    'pdf.section.losingNote': 'Estas recetas tienen precio debajo de su costo real. Revisa el precio o la estructura de costos de inmediato.',
    'pdf.col.ingredient': 'Ingrediente',
    'pdf.col.qty': 'Cant.',
    'pdf.col.unit': 'Unidad',
    'pdf.col.totalCost': 'Costo Total',
    'pdf.col.targetMargin': 'Margen Objetivo',
    'pdf.col.sellingPrice': 'Precio de Venta',
    'pdf.col.profitPerUnit': 'Ganancia / unidad',
    'pdf.col.recipe': 'Receta',
    'pdf.col.margin': 'Margen',
    'pdf.col.status': 'Estado',
    'pdf.col.component': 'Componente',
    'pdf.col.cost': 'Costo',
    'pdf.col.lossPerUnit': 'Pérdida/Unidad',
    'pdf.field.ingredientCost': 'Costo de Ingredientes',
    'pdf.field.labor': 'Mano de Obra',
    'pdf.field.utilities': 'Servicios (gas/luz)',
    'pdf.field.packaging': 'Empaque',
    'pdf.field.operational': 'Operacional',
    'pdf.field.realCostPerUnit': 'Costo Real por Unidad',
    'pdf.field.currentSellingPrice': 'Precio de Venta Actual',
    'pdf.field.totalCost': 'Costo Total',
    'pdf.brandTagline': 'El Campesino · Tortillería',
    'pdf.footerLeft': 'El Campesino · Calculadora de Costo de Recetas',
    'pdf.footerRight': 'Generado el {date} · Uso interno',
    'pdf.printGenerate': 'Generar PDF — vista previa, descargar o imprimir',
    'pdf.analyticsReport': 'Reporte de Analíticas',
    'pdf.totalRecipes': '{n} recetas totales · {m} con precio actual',
    'pdf.noPriced': 'No hay recetas con precio disponibles',

    // Misc
    'misc.units': 'Unidades',
    'misc.fontMono': 'Mono',
    'misc.from': 'de',
    'misc.to': 'a',
    'misc.of': 'de',
  },
};

// Simple template interpolation: replaces {key} placeholders with values.
// Mirrors react-i18next's {{key}} pattern (we use single braces to keep it simple).
function interpolate(str, vars) {
  if (!vars || typeof str !== 'string') return str;
  return str.replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m));
}

// Language Context — global state + localStorage persistence.
const LanguageContext = createContext({
  lang: 'en',
  setLang: () => {},
  t: (k) => k,
});

function LanguageProvider({ children }) {
  // Try to load persisted preference; fallback to English.
  const [lang, setLangState] = useState(() => {
    try {
      const saved = typeof window !== 'undefined' && window.localStorage
        ? window.localStorage.getItem('ec_lang')
        : null;
      return saved === 'es' ? 'es' : 'en';
    } catch (e) {
      return 'en';
    }
  });

  const setLang = (next) => {
    setLangState(next);
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('ec_lang', next);
      }
    } catch (e) { /* ignore */ }
  };

  const t = useMemo(() => {
    const dict = TRANSLATIONS[lang] || TRANSLATIONS.en;
    return (key, vars) => {
      const raw = dict[key] || TRANSLATIONS.en[key] || key;
      return interpolate(raw, vars);
    };
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, t]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

// Hook — matches react-i18next's useTranslation() shape closely.
// Production swap: change this body to `return useTranslation();` and import from react-i18next.
const useT = () => useContext(LanguageContext);

// Bilingual ingredient name helper — when language is ES and a Spanish name exists,
// shows "Cachete de Res · Beef Cheek". Otherwise just the English name.
// (Option B per Liz's call: active language big, secondary muted.)
function bilingualName(ingredient, lang) {
  if (!ingredient) return '';
  const en = ingredient.name || '';
  const es = ingredient.nameEs || '';
  if (lang === 'es' && es && es !== en) return { primary: es, secondary: en };
  return { primary: en, secondary: lang === 'es' ? '' : (es && es !== en ? es : '') };
}

// Language Selector — modern compact dropdown button.
// Trigger: pill-shaped button showing 🌐 + current language code + chevron.
// Menu: clean dropdown with flag + full language name, checkmark on active.
// Tap outside, Escape, or selecting an option closes the menu.
// Animation: fade + slight upward slide on open. Mobile-friendly tap target.
// localStorage persistence is handled by LanguageProvider (key: 'ec_lang').
//
// NOTE: there is a separate LanguageToggle for the Steps tab (per-recipe playbook
// language) — that one is unrelated and predates this i18n system.
function AppLanguageToggle() {
  const { lang, setLang } = useT();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const options = [
    { code: 'en', flag: '🇺🇸', label: 'English' },
    { code: 'es', flag: '🇲🇽', label: 'Español' },
  ];

  const select = (code) => {
    setLang(code);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative inline-block">
      {/* Trigger button — pill-shaped, soft shadow, subtle border */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select language"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-full text-[12px] font-semibold text-slate-700 shadow-sm hover:shadow-md hover:border-slate-300 hover:bg-slate-50 active:scale-[0.97] transition-all duration-150 ease-out"
      >
        <Globe className="w-3.5 h-3.5 text-slate-500" strokeWidth={2.2} />
        <span className="uppercase tracking-wide">{lang === 'es' ? 'ES' : 'EN'}</span>
        <ChevronDown
          className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          strokeWidth={2.5}
        />
      </button>

      {/* Dropdown menu — fades in, slight upward slide */}
      {open && (
        <div
          role="listbox"
          className="absolute right-0 mt-2 w-44 bg-white rounded-xl border border-slate-200 shadow-lg shadow-slate-900/10 overflow-hidden z-50 origin-top-right"
          style={{
            animation: 'ec-lang-menu-enter 160ms cubic-bezier(0.16, 1, 0.3, 1) both',
          }}
        >
          {/* Inline keyframes — keeps the component self-contained */}
          <style>{`
            @keyframes ec-lang-menu-enter {
              from { opacity: 0; transform: translateY(-4px) scale(0.98); }
              to   { opacity: 1; transform: translateY(0)    scale(1); }
            }
          `}</style>
          {options.map((opt) => {
            const active = lang === opt.code;
            return (
              <button
                key={opt.code}
                role="option"
                aria-selected={active}
                onClick={() => select(opt.code)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-[13px] transition-colors duration-100 ${
                  active
                    ? 'bg-emerald-50/60 text-slate-900 font-semibold'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                {/* Check column — fixed width so flags align across rows */}
                <span className="w-4 flex-shrink-0 flex items-center justify-center">
                  {active && <Check className="w-3.5 h-3.5 text-emerald-600" strokeWidth={3} />}
                </span>
                <span className="text-base leading-none" aria-hidden="true">{opt.flag}</span>
                <span className="flex-1 text-left">{opt.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// UNIT CONVERSION ENGINE
// Supports automatic conversion between purchase units and recipe units
// ============================================================

// Base units used internally for math
// Weight base: gram (g)
// Volume base: milliliter (ml)
// Count base: each
const UNIT_DEFS = {
  // Weight
  g:   { base: 'g',  toBase: 1 },
  kg:  { base: 'g',  toBase: 1000 },
  oz:  { base: 'g',  toBase: 28.3495 },
  lb:  { base: 'g',  toBase: 453.592 },
  // Volume
  ml:  { base: 'ml', toBase: 1 },
  l:   { base: 'ml', toBase: 1000 },
  tsp: { base: 'ml', toBase: 4.92892 },
  tbsp:{ base: 'ml', toBase: 14.7868 },
  cup: { base: 'ml', toBase: 236.588 },
  fl_oz:{ base: 'ml', toBase: 29.5735 },
  qt:  { base: 'ml', toBase: 946.353 },
  gal: { base: 'ml', toBase: 3785.41 },
  // Count
  each:   { base: 'each', toBase: 1 },
  dozen:  { base: 'each', toBase: 12 },
  case:   { base: 'each', toBase: 1 }, // depends on contents (qty defines)
  bag:    { base: 'each', toBase: 1 }, // depends on contents (qty defines)
  box:    { base: 'each', toBase: 1 }, // depends on contents (qty defines)
};

// Spanish-to-English unit aliases for Ingredient Master List
// (separate from UNIT_ALIASES used by the legacy recipe unit system)
const ING_UNIT_ALIASES = {
  'libras':'lb', 'libra':'lb', 'lbs':'lb',
  'gramos':'g', 'gramo':'g', 'gr':'g',
  'kilos':'kg', 'kilo':'kg',
  'onzas':'oz', 'onza':'oz',
  'galones':'gal', 'galón':'gal', 'galon':'gal',
  'cuartos':'qt', 'cuarto':'qt',
  'tazas':'cup', 'taza':'cup',
  'cucharadas':'tbsp', 'cucharada':'tbsp',
  'cucharaditas':'tsp', 'cucharadita':'tsp',
  'unidades':'each', 'unidad':'each', 'pza':'each', 'pieza':'each', 'piezas':'each',
  'docenas':'dozen', 'docena':'dozen',
  'latas':'each', 'lata':'each',
  'cabezas':'each', 'cabeza':'each',
  'tandas':'each', 'tanda':'each',
  'mililitros':'ml', 'mililitro':'ml',
  'litros':'l', 'litro':'l',
};

function normalizeUnit(u) {
  if (!u) return 'each';
  const lower = String(u).toLowerCase().trim();
  if (UNIT_DEFS[lower]) return lower;
  if (ING_UNIT_ALIASES[lower]) return ING_UNIT_ALIASES[lower];
  return lower;
}

// Convert qty from `fromUnit` to `toUnit`. Returns null if incompatible.
function convertUnits(qty, fromUnit, toUnit) {
  const f = UNIT_DEFS[normalizeUnit(fromUnit)];
  const t = UNIT_DEFS[normalizeUnit(toUnit)];
  if (!f || !t) return null;
  if (f.base !== t.base) return null; // incompatible (e.g., g → ml)
  return (qty * f.toBase) / t.toBase;
}

// Calculate cost per recipe unit, given purchase info
// e.g. bag of 50 lb costs $42.50 → recipeUnit 'g' → cost per g
function calcCostPerRecipeUnit(purchasePrice, purchaseQty, purchaseQtyUnit, recipeUnit) {
  const converted = convertUnits(purchaseQty, purchaseQtyUnit, recipeUnit);
  if (!converted || converted === 0) return 0;
  return purchasePrice / converted;
}

// Apply merma (waste %) to a cost
function applyMerma(cost, mermaPct) {
  const yieldFactor = 1 - (mermaPct || 0) / 100;
  if (yieldFactor <= 0) return cost; // safety
  return cost / yieldFactor;
}

// Format cost compactly: $0.0019/g, $4.99/lb, etc.
function fmtCostPerUnit(cost, unit) {
  if (!cost || cost === 0) return `—`;
  if (cost < 0.01) return `$${cost.toFixed(4)}/${unit}`;
  if (cost < 1)    return `$${cost.toFixed(3)}/${unit}`;
  return `$${cost.toFixed(2)}/${unit}`;
}

// ============================================================
// INGREDIENT MASTER LIST — SEED DATA
// Auto-extracted from existing recipes (Phase 1: standalone, no migration yet)
// ============================================================

const INGREDIENT_CATEGORIES = [
  { id: 'meats',    labelKey: 'ingCat.meats',    emoji: '🥩' },
  { id: 'produce',  labelKey: 'ingCat.produce',  emoji: '🥬' },
  { id: 'spices',   labelKey: 'ingCat.spices',   emoji: '🌶️' },
  { id: 'dairy',    labelKey: 'ingCat.dairy',    emoji: '🥛' },
  { id: 'dry',      labelKey: 'ingCat.dryGoods', emoji: '🌾' },
  { id: 'liquids',  labelKey: 'ingCat.liquids',  emoji: '🍶' },
  { id: 'other',    labelKey: 'ingCat.other',    emoji: '📦' },
];

const SEED_INGREDIENTS = [
  // ─── MEATS ───────────────────────────────────────────────────
  {
    id: 'ing_beef_cheek',
    name: 'Beef Cheek', nameEs: 'Cachete de Res',
    category: 'meats',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 4.89, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
    notes: 'Used in Barbacoa. Yields ~56% after slow-cooking.',
  },
  {
    id: 'ing_pork_back',
    name: 'Pork Back (with bone)', nameEs: 'Pork Back con Hueso',
    category: 'meats',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 1.49, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
    notes: 'Used in Carnitas. Yields ~52% after cooking.',
  },
  {
    id: 'ing_pork_belly',
    name: 'Pork Belly', nameEs: 'Pork Belly',
    category: 'meats',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 3.19, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
    notes: 'Used in Chicharrón Carnudo.',
  },
  {
    id: 'ing_beef_chuck',
    name: 'Beef Chuck', nameEs: 'Carne Chuck',
    category: 'meats',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 5.29, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
    notes: 'Used in Birria de Res.',
  },
  {
    id: 'ing_beef_bone',
    name: 'Beef Ring Bone', nameEs: 'Hueso de Anillo',
    category: 'meats',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 5.49, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
    notes: 'Used in Birria for flavor base.',
  },
  {
    id: 'ing_chicken_whole',
    name: 'Whole Chicken', nameEs: 'Pollos Enteros',
    category: 'meats',
    purchaseUnit: 'each', purchaseUnitLabel: 'each', purchasePrice: 3.57, purchaseQty: 1, purchaseQtyUnit: 'each',
    recipeUnit: 'each', mermaPercent: 0,
    notes: 'Used in Pollo Rostizado.',
  },
  {
    id: 'ing_chicken_breast',
    name: 'Chicken Breast', nameEs: 'Pechuga de Pollo',
    category: 'meats',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 2.50, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
    notes: 'Used in Fajita de Pollo. ESTIMATE.',
  },
  {
    id: 'ing_bacon',
    name: 'Bacon', nameEs: 'Tocino',
    category: 'meats',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 4.50, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
    notes: 'Used in Frijoles Charros.',
  },
  {
    id: 'ing_chorizo',
    name: 'Chorizo', nameEs: 'Chorizo',
    category: 'meats',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 5.00, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
    notes: 'Used in Frijoles Charros.',
  },

  // ─── PRODUCE ──────────────────────────────────────────────────
  {
    id: 'ing_onion',
    name: 'Onion (granulated)', nameEs: 'Cebolla Granulada',
    category: 'produce',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 3.00, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
    notes: 'Dried granulated. Used in Barbacoa.',
  },
  {
    id: 'ing_onion_fresh',
    name: 'Onion (fresh, large)', nameEs: 'Cebolla Grande Fresca',
    category: 'produce',
    purchaseUnit: 'each', purchaseUnitLabel: 'each', purchasePrice: 0.47, purchaseQty: 1, purchaseQtyUnit: 'each',
    recipeUnit: 'each', mermaPercent: 8,
    notes: '8% merma (peels). Used in Carnitas.',
  },
  {
    id: 'ing_onion_bulk',
    name: 'Onion (bulk)', nameEs: 'Cebolla Bulk',
    category: 'produce',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 0.80, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 8,
    notes: 'For salsas, arroz, frijoles, fajitas. 8% merma (peels).',
  },
  {
    id: 'ing_garlic_granulated',
    name: 'Garlic (granulated)', nameEs: 'Ajo Granulado',
    category: 'produce',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 4.99, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
    notes: 'Dried granulated.',
  },
  {
    id: 'ing_garlic_fresh',
    name: 'Garlic (fresh head)', nameEs: 'Cabeza de Ajo Fresco',
    category: 'produce',
    purchaseUnit: 'each', purchaseUnitLabel: 'head', purchasePrice: 0.33, purchaseQty: 1, purchaseQtyUnit: 'each',
    recipeUnit: 'each', mermaPercent: 10,
    notes: '10% merma (peels). Used in carnitas.',
  },
  {
    id: 'ing_garlic_bulk',
    name: 'Garlic (bulk)', nameEs: 'Ajo Bulk',
    category: 'produce',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 5.00, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 10,
    notes: 'For arroz, frijoles, salsas, fajitas.',
  },
  {
    id: 'ing_tomato',
    name: 'Tomato', nameEs: 'Jitomate',
    category: 'produce',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 1.20, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 5,
    notes: '5% merma (core/spoilage).',
  },
  {
    id: 'ing_tomato_each',
    name: 'Tomato (each)', nameEs: 'Jitomate Pieza',
    category: 'produce',
    purchaseUnit: 'each', purchaseUnitLabel: 'each', purchasePrice: 0.20, purchaseQty: 1, purchaseQtyUnit: 'each',
    recipeUnit: 'each', mermaPercent: 5,
    notes: 'Used in Birria (count-based).',
  },
  {
    id: 'ing_lime',
    name: 'Lime', nameEs: 'Limón',
    category: 'produce',
    purchaseUnit: 'each', purchaseUnitLabel: 'each', purchasePrice: 0.25, purchaseQty: 1, purchaseQtyUnit: 'each',
    recipeUnit: 'each', mermaPercent: 0,
    notes: 'Used in Carnitas.',
  },
  {
    id: 'ing_orange',
    name: 'Orange', nameEs: 'Naranja',
    category: 'produce',
    purchaseUnit: 'each', purchaseUnitLabel: 'each', purchasePrice: 0.67, purchaseQty: 1, purchaseQtyUnit: 'each',
    recipeUnit: 'each', mermaPercent: 0,
    notes: 'Used in Carnitas.',
  },
  {
    id: 'ing_bell_pepper',
    name: 'Bell Pepper', nameEs: 'Pimiento Morrón',
    category: 'produce',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 3.00, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 10,
    notes: '10% merma (seeds/stems). Used in Fajita.',
  },
  {
    id: 'ing_jalapeno',
    name: 'Jalapeño', nameEs: 'Chile Jalapeño',
    category: 'produce',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 2.00, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 5,
    notes: 'Used in Frijoles Charros.',
  },
  {
    id: 'ing_cilantro',
    name: 'Cilantro', nameEs: 'Cilantro',
    category: 'produce',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 3.00, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 20,
    notes: '20% merma (stems trimmed).',
  },
  {
    id: 'ing_bay_leaves',
    name: 'Bay Leaves', nameEs: 'Hojas de Laurel',
    category: 'produce',
    purchaseUnit: 'each', purchaseUnitLabel: 'each', purchasePrice: 0.003, purchaseQty: 1, purchaseQtyUnit: 'each',
    recipeUnit: 'each', mermaPercent: 0,
    notes: 'Dried. Used in Carnitas.',
  },

  // ─── SPICES ──────────────────────────────────────────────────
  {
    id: 'ing_pepper_ground',
    name: 'Black Pepper (ground)', nameEs: 'Pimienta Molida',
    category: 'spices',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 7.32, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
  },
  {
    id: 'ing_cumin',
    name: 'Cumin (ground)', nameEs: 'Comino Molido',
    category: 'spices',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 0.80, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
  },
  {
    id: 'ing_cumin_whole',
    name: 'Cumin (whole)', nameEs: 'Cominos Enteros',
    category: 'spices',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 4.08, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'g', mermaPercent: 0,
    notes: 'Birria uses whole cumin (in grams).',
  },
  {
    id: 'ing_salt',
    name: 'Salt', nameEs: 'Sal',
    category: 'spices',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 0.61, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
  },
  {
    id: 'ing_prague_salt',
    name: 'Prague Salt (#1 curing)', nameEs: 'Sal Praga',
    category: 'spices',
    purchaseUnit: 'tbsp', purchaseUnitLabel: 'tbsp', purchasePrice: 2.60, purchaseQty: 1, purchaseQtyUnit: 'tbsp',
    recipeUnit: 'tbsp', mermaPercent: 0,
    notes: 'Curing salt. Used in Carnitas.',
  },
  {
    id: 'ing_oregano',
    name: 'Oregano', nameEs: 'Orégano',
    category: 'spices',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 3.63, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'g', mermaPercent: 0,
  },
  {
    id: 'ing_turmeric',
    name: 'Turmeric', nameEs: 'Cúrcuma',
    category: 'spices',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 3.80, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
  },
  {
    id: 'ing_achiote',
    name: 'Achiote (ground)', nameEs: 'Achiote Molido',
    category: 'spices',
    purchaseUnit: 'oz', purchaseUnitLabel: 'oz', purchasePrice: 0.73, purchaseQty: 1, purchaseQtyUnit: 'oz',
    recipeUnit: 'oz', mermaPercent: 0,
  },
  {
    id: 'ing_chile_guajillo_per',
    name: 'Chile Guajillo (Peruvian)', nameEs: 'Chile Guajillo Peruano',
    category: 'spices',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 3.18, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'g', mermaPercent: 0,
  },
  {
    id: 'ing_chile_guajillo_mx',
    name: 'Chile Guajillo (Mexican)', nameEs: 'Chile Guajillo Mexicano',
    category: 'spices',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 4.08, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'g', mermaPercent: 0,
  },
  {
    id: 'ing_chile_arbol',
    name: 'Chile de Árbol', nameEs: 'Chile de Árbol',
    category: 'spices',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 6.00, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
  },
  {
    id: 'ing_chile_guajillo',
    name: 'Chile Guajillo', nameEs: 'Chile Guajillo',
    category: 'spices',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 5.00, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
  },
  {
    id: 'ing_fajita_seasoning',
    name: 'Fajita Seasoning', nameEs: 'Sazonador Fajita',
    category: 'spices',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 6.00, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
  },
  {
    id: 'ing_chicken_seasoning',
    name: 'Chicken Seasoning', nameEs: 'Sazón de Pollo',
    category: 'spices',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 3.24, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
  },
  {
    id: 'ing_lemon_herb',
    name: 'Lemon Herb Seasoning', nameEs: 'Lemon Herb',
    category: 'spices',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 3.05, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
  },

  // ─── DRY GOODS ───────────────────────────────────────────────
  {
    id: 'ing_maseca',
    name: 'Maseca (Corn flour)', nameEs: 'Maseca',
    category: 'dry',
    purchaseUnit: 'bag', purchaseUnitLabel: 'bag 50 lb', purchasePrice: 28.00, purchaseQty: 50, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
    notes: 'Bag of 50 lb. Used for tortillas.',
  },
  {
    id: 'ing_rice',
    name: 'Rice (raw)', nameEs: 'Arroz Crudo',
    category: 'dry',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 1.00, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
  },
  {
    id: 'ing_pinto_beans',
    name: 'Pinto Beans (dry)', nameEs: 'Frijol Pinto',
    category: 'dry',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 1.30, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
  },
  {
    id: 'ing_chicken_consome',
    name: 'Chicken Consommé', nameEs: 'Consomé de Pollo',
    category: 'dry',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 2.00, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
    notes: 'Powdered seasoning base.',
  },
  {
    id: 'ing_chicken_broth',
    name: 'Chicken Broth (concentrate)', nameEs: 'Caldo de Pollo',
    category: 'dry',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 3.00, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
  },
  {
    id: 'ing_mustard',
    name: 'Mustard', nameEs: 'Mostaza',
    category: 'dry',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 0.88, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
  },

  // ─── LIQUIDS ──────────────────────────────────────────────────
  {
    id: 'ing_pineapple_juice',
    name: 'Pineapple Juice', nameEs: 'Jugo de Piña',
    category: 'liquids',
    purchaseUnit: 'gal', purchaseUnitLabel: 'gallon', purchasePrice: 12.58, purchaseQty: 1, purchaseQtyUnit: 'gal',
    recipeUnit: 'gal', mermaPercent: 0,
  },
  {
    id: 'ing_moho_criollo',
    name: 'Mojo Criollo', nameEs: 'Mojo Criollo',
    category: 'liquids',
    purchaseUnit: 'gal', purchaseUnitLabel: 'gallon', purchasePrice: 5.30, purchaseQty: 1, purchaseQtyUnit: 'gal',
    recipeUnit: 'gal', mermaPercent: 0,
  },
  {
    id: 'ing_vinegar',
    name: 'Vinegar', nameEs: 'Vinagre',
    category: 'liquids',
    purchaseUnit: 'gal', purchaseUnitLabel: 'gallon', purchasePrice: 3.00, purchaseQty: 1, purchaseQtyUnit: 'gal',
    recipeUnit: 'gal', mermaPercent: 0,
  },
  {
    id: 'ing_oil',
    name: 'Cooking Oil', nameEs: 'Aceite',
    category: 'liquids',
    purchaseUnit: 'qt', purchaseUnitLabel: 'quart', purchasePrice: 2.40, purchaseQty: 1, purchaseQtyUnit: 'qt',
    recipeUnit: 'qt', mermaPercent: 0,
  },
  {
    id: 'ing_lard',
    name: 'Lard', nameEs: 'Manteca',
    category: 'liquids',
    purchaseUnit: 'lb', purchaseUnitLabel: 'lb', purchasePrice: 0.04, purchaseQty: 1, purchaseQtyUnit: 'lb',
    recipeUnit: 'lb', mermaPercent: 0,
    notes: 'Prorrated cost across multiple recipes.',
  },
  {
    id: 'ing_honey',
    name: 'Honey', nameEs: 'Miel Natural',
    category: 'liquids',
    purchaseUnit: 'tbsp', purchaseUnitLabel: 'tbsp', purchasePrice: 7.98, purchaseQty: 1, purchaseQtyUnit: 'tbsp',
    recipeUnit: 'tbsp', mermaPercent: 0,
  },
  {
    id: 'ing_water',
    name: 'Water', nameEs: 'Agua',
    category: 'liquids',
    purchaseUnit: 'qt', purchaseUnitLabel: 'quart', purchasePrice: 0, purchaseQty: 1, purchaseQtyUnit: 'qt',
    recipeUnit: 'qt', mermaPercent: 0,
    notes: 'Tap water — no cost.',
  },
  {
    id: 'ing_coca_lata',
    name: 'Coca-Cola (can)', nameEs: 'Coca-Cola Lata',
    category: 'liquids',
    purchaseUnit: 'each', purchaseUnitLabel: 'can', purchasePrice: 0.60, purchaseQty: 1, purchaseQtyUnit: 'each',
    recipeUnit: 'each', mermaPercent: 0,
    notes: 'Used as ingredient in Carnitas.',
  },
];

// Live calculation: cost per recipe unit (with conversion + merma)
function calcIngredientCost(ing) {
  if (!ing) return { perUnit: 0, perUnitWithMerma: 0 };
  const perUnit = calcCostPerRecipeUnit(
    ing.purchasePrice,
    ing.purchaseQty,
    ing.purchaseQtyUnit,
    ing.recipeUnit
  );
  const perUnitWithMerma = applyMerma(perUnit, ing.mermaPercent);
  return { perUnit, perUnitWithMerma };
}


const SEED_RECIPES = [
  {
    id: 'r1',
    name: 'Barbacoa', category: 'Carnes',
    yield: { value: 66.99, unit: 'Libras', rawQty: 120, rawUnit: 'Libras' }, status: 'active',
    ingredients: [
      { id: 'i1', name: 'Cachete', qty: 120, unit: 'Libras', totalCost: 586.80, unitCost: 4.89 },
      { id: 'i2', name: 'Consome de pollo', qty: 5, unit: 'Libras', totalCost: 10.00, unitCost: 2.00 },
      { id: 'i3', name: 'Pimienta molida', qty: 1, unit: 'Libra', totalCost: 7.32, unitCost: 7.32 },
      { id: 'i4', name: 'Ajo granulado', qty: 1.3, unit: 'Libras', totalCost: 6.49, unitCost: 4.99 },
      { id: 'i5', name: 'Cebolla Granulada', qty: 1.3, unit: 'Libras', totalCost: 3.90, unitCost: 3.00 },
      { id: 'i6', name: 'Comino molido', qty: 1.42, unit: 'Libras', totalCost: 1.14, unitCost: 0.80 },
      { id: 'i7', name: 'Sal', qty: 3, unit: 'Libras', totalCost: 1.84, unitCost: 0.61 },
    ],
    prepHours: 5, cookHours: 9, laborRate: 12,
    workers: 1,
    overheadPct: 12,
    packaging: { container: 0, bag: 0.05, wrap: 0, other: 0 },
    includeCookInLabor: false,
    currentPrice: 16.99, competitorPriceLow: 15.99, competitorPriceHigh: 18.99,
    notes: 'Por cada 120 lb crudas de cachete, rinden 66.99 lb finales.',
  },
  {
    id: 'r2',
    name: 'Pollo Rostizado', category: 'Aves',
    yield: { value: 132, unit: 'Unidades' }, status: 'active',
    ingredients: [
      { id: 'i1', name: 'Pollos', qty: 132, unit: 'Unidades', totalCost: 471.00, unitCost: 3.57 },
      { id: 'i2', name: 'Sazon de pollo', qty: 5, unit: 'Libras', totalCost: 16.20, unitCost: 3.24 },
      { id: 'i3', name: 'Lemon Herb', qty: 5, unit: 'Libras', totalCost: 15.25, unitCost: 3.05 },
      { id: 'i4', name: 'Jugo de Piña', qty: 1.5, unit: 'Galones', totalCost: 18.87, unitCost: 12.58 },
      { id: 'i5', name: 'Moho criollo', qty: 2, unit: 'Galones', totalCost: 10.60, unitCost: 5.30 },
      { id: 'i6', name: 'Mostaza', qty: 1.7, unit: 'Libras', totalCost: 1.50, unitCost: 0.88 },
      { id: 'i7', name: 'Vinagre', qty: 0.2, unit: 'Galón', totalCost: 0.60, unitCost: 3.00 },
      { id: 'i8', name: 'Tumeric', qty: 0.1, unit: 'Libras', totalCost: 0.38, unitCost: 3.80 },
      { id: 'i9', name: 'Achiote molido', qty: 0.22, unit: 'Oz', totalCost: 0.16, unitCost: 0.73 },
    ],
    prepHours: 2, cookHours: 2, laborRate: 12,
    workers: 1,
    overheadPct: 12,
    packaging: { container: 0.40, bag: 0.10, wrap: 0, other: 0 },
    includeCookInLabor: false,
    currentPrice: 9.99, competitorPriceLow: 8.99, competitorPriceHigh: 12.99,
    notes: 'Mano de obra se prorratea por producción completa. Rostizado 2 hrs.',
  },
  {
    id: 'r3',
    name: 'Birria de Res', category: 'Carnes',
    yield: { value: 7, unit: 'Unidades' }, status: 'active',
    ingredients: [
      { id: 'i1', name: 'Carne Chuck', qty: 10, unit: 'Libras', totalCost: 51.90, unitCost: 5.29 },
      { id: 'i2', name: 'Hueso de Anillo', qty: 3, unit: 'Libras', totalCost: 19.25, unitCost: 5.49 },
      { id: 'i3', name: 'Chile Guajillo Peruano', qty: 45, unit: 'Gramos', totalCost: 0.30, unitCost: 0.007 },
      { id: 'i4', name: 'Chile Guajillo Mexicano', qty: 90, unit: 'Gramos', totalCost: 0.80, unitCost: 0.009 },
      { id: 'i5', name: 'Orégano', qty: 9, unit: 'Gramos', totalCost: 0.07, unitCost: 0.008 },
      { id: 'i6', name: 'Cominos', qty: 18, unit: 'Gramos', totalCost: 0.16, unitCost: 0.009 },
      { id: 'i7', name: 'Jitomates', qty: 20, unit: 'Unidades', totalCost: 4.00, unitCost: 0.20 },
    ],
    prepHours: 2, cookHours: 0, laborRate: 12,
    workers: 1,
    overheadPct: 12,
    packaging: { container: 0.50, bag: 0.05, wrap: 0.10, other: 0 },
    includeCookInLabor: false,
    currentPrice: 18.99, competitorPriceLow: 16.99, competitorPriceHigh: 22.99,
    notes: 'Cocción ya incluida en merma del rendimiento.',
  },
  {
    id: 'r4',
    name: 'Carnitas', category: 'Carnes',
    yield: { value: 18.67, unit: 'Libras', rawQty: 36, rawUnit: 'Libras' }, status: 'active',
    ingredients: [
      { id: 'i1', name: 'Pork back con hueso', qty: 36, unit: 'Libras', totalCost: 53.64, unitCost: 1.49 },
      { id: 'i2', name: 'Cebollas Grandes', qty: 2, unit: 'Unidades', totalCost: 0.94, unitCost: 0.47 },
      { id: 'i3', name: 'Cabezas de ajo fresca', qty: 3, unit: 'Cabezas', totalCost: 1.00, unitCost: 0.33 },
      { id: 'i4', name: 'Hojas de laurel', qty: 7, unit: 'Unidades', totalCost: 0.02, unitCost: 0.003 },
      { id: 'i5', name: 'Sal', qty: 1.5, unit: 'Libras', totalCost: 0.42, unitCost: 0.28 },
      { id: 'i6', name: 'Miel natural', qty: 0.5, unit: 'Cucharada', totalCost: 0.03, unitCost: 7.98 },
      { id: 'i7', name: 'Limones', qty: 6, unit: 'Unidades', totalCost: 1.50, unitCost: 0.25 },
      { id: 'i8', name: 'Naranjas', qty: 3, unit: 'Unidades', totalCost: 2.00, unitCost: 0.67 },
      { id: 'i9', name: 'Sal Praga', qty: 3, unit: 'Cucharada', totalCost: 0.18, unitCost: 2.60 },
      { id: 'i10', name: 'Coca-cola', qty: 1, unit: 'Lata', totalCost: 0.60, unitCost: 0.60 },
      { id: 'i11', name: 'Manteca para freir', qty: 75, unit: 'Libras', totalCost: 3.30, unitCost: 0.04 },
    ],
    prepHours: 1, cookHours: 3, laborRate: 12,
    workers: 1,
    overheadPct: 12,
    packaging: { container: 0, bag: 0.05, wrap: 0, other: 0 },
    includeCookInLabor: true,
    currentPrice: 10.99, competitorPriceLow: 9.99, competitorPriceHigh: 12.99,
    notes: 'De 36 lb crudas rinden 18.67 lb. Manteca prorrateada $3.30.',
  },
  {
    id: 'r5',
    name: 'Chicharrón Carnudo', category: 'Carnes',
    yield: { value: 30.92, unit: 'Libras', rawQty: 75.1, rawUnit: 'Libras' }, status: 'active',
    ingredients: [
      { id: 'i1', name: 'Pork Belly', qty: 75.1, unit: 'Libras', totalCost: 239.57, unitCost: 3.19 },
      { id: 'i2', name: 'Manteca prorrateada', qty: 1, unit: 'Tanda', totalCost: 27.94, unitCost: 27.94 },
    ],
    prepHours: 0, cookHours: 2.5, laborRate: 12,
    workers: 1,
    overheadPct: 12,
    packaging: { container: 0, bag: 0.05, wrap: 0, other: 0 },
    includeCookInLabor: true,
    currentPrice: 14.99, competitorPriceLow: 12.99, competitorPriceHigh: 16.99,
    notes: 'De 75.10 lb crudas rinden 30.92 lb finales.',
  },
  {
    id: 'r6',
    name: 'Tortillas', category: 'Tortillería',
    yield: { value: 75, unit: 'Libras', rawQty: 50, rawUnit: 'Libras' }, status: 'active',
    ingredients: [
      { id: 'i1', name: 'Maseca', qty: 50, unit: 'Libras', totalCost: 28.00, unitCost: 0.56 },
      { id: 'i2', name: 'Agua', qty: 28, unit: 'Cuartos', totalCost: 0, unitCost: 0 },
    ],
    prepHours: 0, cookHours: 3, laborRate: 12,
    workers: 1,
    overheadPct: 12,
    packaging: { container: 0, bag: 0.03, wrap: 0, other: 0 },
    includeCookInLabor: true,
    currentPrice: 1.99, competitorPriceLow: 1.79, competitorPriceHigh: 2.49,
    notes: 'Por bulto de 50 lb de maseca salen ~75 lb de tortillas.',
  },
  {
    id: 'r7',
    name: 'Fajita de Pollo', category: 'Aves',
    yield: { value: 8, unit: 'Libras', rawQty: 12, rawUnit: 'Libras' }, status: 'active',
    ingredients: [
      { id: 'i1', name: 'Pechuga de pollo', qty: 12, unit: 'Libras', totalCost: 30.00, unitCost: 2.50 },
      { id: 'i2', name: 'Cebolla en tiras', qty: 2, unit: 'Libras', totalCost: 1.50, unitCost: 0.75 },
      { id: 'i3', name: 'Pimiento morrón', qty: 1.5, unit: 'Libras', totalCost: 4.50, unitCost: 3.00 },
      { id: 'i4', name: 'Aceite', qty: 0.5, unit: 'Cuartos', totalCost: 1.20, unitCost: 2.40 },
      { id: 'i5', name: 'Sazonador fajita', qty: 0.3, unit: 'Libras', totalCost: 1.80, unitCost: 6.00 },
      { id: 'i6', name: 'Ajo', qty: 0.1, unit: 'Libras', totalCost: 0.50, unitCost: 5.00 },
      { id: 'i7', name: 'Sal', qty: 0.2, unit: 'Libras', totalCost: 0.12, unitCost: 0.61 },
    ],
    prepHours: 1, cookHours: 1, laborRate: 12,
    workers: 1,
    overheadPct: 12,
    packaging: { container: 0, bag: 0.05, wrap: 0, other: 0 },
    includeCookInLabor: true,
    currentPrice: 0, competitorPriceLow: 0, competitorPriceHigh: 0,
    notes: 'ESTIMADO INICIAL — refinar valores reales después. De 12 lb crudas rinden ~8 lb listas a la fajita.',
  },
  {
    id: 'r8',
    name: 'Arroz Rojo', category: 'Sides',
    yield: { value: 12, unit: 'Libras', rawQty: 4, rawUnit: 'Libras' }, status: 'active',
    ingredients: [
      { id: 'i1', name: 'Arroz crudo', qty: 4, unit: 'Libras', totalCost: 4.00, unitCost: 1.00 },
      { id: 'i2', name: 'Jitomate', qty: 1, unit: 'Libras', totalCost: 1.20, unitCost: 1.20 },
      { id: 'i3', name: 'Cebolla', qty: 0.5, unit: 'Libras', totalCost: 0.40, unitCost: 0.80 },
      { id: 'i4', name: 'Ajo', qty: 0.1, unit: 'Libras', totalCost: 0.50, unitCost: 5.00 },
      { id: 'i5', name: 'Aceite', qty: 0.25, unit: 'Cuartos', totalCost: 0.60, unitCost: 2.40 },
      { id: 'i6', name: 'Caldo de pollo', qty: 0.5, unit: 'Libras', totalCost: 1.50, unitCost: 3.00 },
      { id: 'i7', name: 'Sal', qty: 0.1, unit: 'Libras', totalCost: 0.06, unitCost: 0.61 },
    ],
    prepHours: 0.5, cookHours: 1, laborRate: 12,
    workers: 1,
    overheadPct: 12,
    packaging: { container: 0.15, bag: 0, wrap: 0, other: 0 },
    includeCookInLabor: true,
    currentPrice: 2.49, competitorPriceLow: 2.00, competitorPriceHigh: 3.50,
    notes: 'ESTIMADO INICIAL — refinar valores reales después. De 4 lb crudas rinden ~12 lb cocidas.',
  },
  {
    id: 'r9',
    name: 'Frijoles Charros', category: 'Sides',
    yield: { value: 15, unit: 'Libras', rawQty: 5, rawUnit: 'Libras' }, status: 'active',
    ingredients: [
      { id: 'i1', name: 'Frijol pinto', qty: 5, unit: 'Libras', totalCost: 6.50, unitCost: 1.30 },
      { id: 'i2', name: 'Tocino', qty: 1, unit: 'Libras', totalCost: 4.50, unitCost: 4.50 },
      { id: 'i3', name: 'Chorizo', qty: 0.5, unit: 'Libras', totalCost: 2.50, unitCost: 5.00 },
      { id: 'i4', name: 'Jitomate', qty: 1, unit: 'Libras', totalCost: 1.20, unitCost: 1.20 },
      { id: 'i5', name: 'Cebolla', qty: 0.5, unit: 'Libras', totalCost: 0.40, unitCost: 0.80 },
      { id: 'i6', name: 'Chile jalapeño', qty: 0.25, unit: 'Libras', totalCost: 0.50, unitCost: 2.00 },
      { id: 'i7', name: 'Cilantro', qty: 0.1, unit: 'Libras', totalCost: 0.30, unitCost: 3.00 },
      { id: 'i8', name: 'Ajo', qty: 0.1, unit: 'Libras', totalCost: 0.50, unitCost: 5.00 },
      { id: 'i9', name: 'Sal', qty: 0.15, unit: 'Libras', totalCost: 0.09, unitCost: 0.61 },
    ],
    prepHours: 1, cookHours: 4, laborRate: 12,
    workers: 1,
    overheadPct: 12,
    packaging: { container: 0.15, bag: 0, wrap: 0, other: 0 },
    includeCookInLabor: false,
    currentPrice: 2.49, competitorPriceLow: 2.00, competitorPriceHigh: 3.50,
    notes: 'ESTIMADO INICIAL — refinar valores reales después. Cocción pasiva (frijoles a fuego lento). De 5 lb crudas rinden ~15 lb cocidas con caldo.',
  },
  {
    id: 'r10',
    name: 'Salsa Roja', category: 'Salsas',
    yield: { value: 8, unit: 'Libras', rawQty: 6, rawUnit: 'Libras' }, status: 'active',
    ingredients: [
      { id: 'i1', name: 'Jitomate', qty: 5, unit: 'Libras', totalCost: 6.00, unitCost: 1.20 },
      { id: 'i2', name: 'Chile de árbol', qty: 0.25, unit: 'Libras', totalCost: 1.50, unitCost: 6.00 },
      { id: 'i3', name: 'Chile guajillo', qty: 0.25, unit: 'Libras', totalCost: 1.25, unitCost: 5.00 },
      { id: 'i4', name: 'Ajo', qty: 0.15, unit: 'Libras', totalCost: 0.75, unitCost: 5.00 },
      { id: 'i5', name: 'Cebolla', qty: 0.5, unit: 'Libras', totalCost: 0.40, unitCost: 0.80 },
      { id: 'i6', name: 'Sal', qty: 0.1, unit: 'Libras', totalCost: 0.06, unitCost: 0.61 },
    ],
    prepHours: 0.5, cookHours: 0.5, laborRate: 12,
    workers: 1,
    overheadPct: 12,
    packaging: { container: 0, bag: 0, wrap: 0, other: 0 },
    includeCookInLabor: true,
    currentPrice: 3.50, competitorPriceLow: 2.99, competitorPriceHigh: 4.99,
    notes: 'ESTIMADO INICIAL — refinar valores reales después. Salsa roja molida.',
  },
  {
    id: 'r11',
    name: 'Salsa Verde', category: 'Salsas',
    yield: { value: 8, unit: 'Libras', rawQty: 6, rawUnit: 'Libras' }, status: 'active',
    ingredients: [
      { id: 'i1', name: 'Tomatillo', qty: 5, unit: 'Libras', totalCost: 5.50, unitCost: 1.10 },
      { id: 'i2', name: 'Chile serrano', qty: 0.5, unit: 'Libras', totalCost: 1.25, unitCost: 2.50 },
      { id: 'i3', name: 'Cilantro', qty: 0.25, unit: 'Libras', totalCost: 0.75, unitCost: 3.00 },
      { id: 'i4', name: 'Ajo', qty: 0.1, unit: 'Libras', totalCost: 0.50, unitCost: 5.00 },
      { id: 'i5', name: 'Cebolla', qty: 0.5, unit: 'Libras', totalCost: 0.40, unitCost: 0.80 },
      { id: 'i6', name: 'Sal', qty: 0.1, unit: 'Libras', totalCost: 0.06, unitCost: 0.61 },
    ],
    prepHours: 0.5, cookHours: 0.5, laborRate: 12,
    workers: 1,
    overheadPct: 12,
    packaging: { container: 0, bag: 0, wrap: 0, other: 0 },
    includeCookInLabor: true,
    currentPrice: 3.50, competitorPriceLow: 2.99, competitorPriceHigh: 4.99,
    notes: 'ESTIMADO INICIAL — refinar valores reales después. Salsa verde con tomatillo.',
  },
  {
    id: 'r12',
    name: 'Chiles Rellenos', category: 'Sides',
    yield: { value: 10, unit: 'Unidades', rawQty: 10, rawUnit: 'Unidades' }, status: 'active',
    ingredients: [
      { id: 'i1', name: 'Chile poblano', qty: 10, unit: 'Unidades', totalCost: 5.00, unitCost: 0.50 },
      { id: 'i2', name: 'Queso Oaxaca', qty: 1.5, unit: 'Libras', totalCost: 9.00, unitCost: 6.00 },
      { id: 'i3', name: 'Huevos', qty: 6, unit: 'Unidades', totalCost: 1.80, unitCost: 0.30 },
      { id: 'i4', name: 'Harina', qty: 0.5, unit: 'Libras', totalCost: 0.40, unitCost: 0.80 },
      { id: 'i5', name: 'Aceite (freir)', qty: 0.5, unit: 'Cuartos', totalCost: 1.20, unitCost: 2.40 },
      { id: 'i6', name: 'Sal', qty: 0.05, unit: 'Libras', totalCost: 0.03, unitCost: 0.61 },
    ],
    prepHours: 1, cookHours: 1, laborRate: 12,
    workers: 1,
    overheadPct: 12,
    packaging: { container: 0.20, bag: 0, wrap: 0, other: 0 },
    includeCookInLabor: true,
    currentPrice: 5.98, competitorPriceLow: 4.99, competitorPriceHigh: 7.99,
    notes: 'ESTIMADO INICIAL — refinar valores reales después. Vendidos en par ($5.98 = 2 chiles).',
  },
];

// ============================================================
// LAYER 1.5 — RECIPE PLAYBOOKS (cooking guides, bilingual)
// ============================================================
// Keyed by recipe id. Each playbook has:
//   - equipment: free-text list, en/es
//   - steps: ordered list, each with title + description in en/es
//   - tips: free-text in en/es
//
// Steps marked "DRAFT" need to be reviewed and refined by Liz.

const PLAYBOOKS = {
  r1: {
    // Barbacoa
    photoUrl: null,
    equipment: [
      { id: 'e1', en: '50 qt steam pot', es: 'Olla de vapor 50 qt' },
      { id: 'e2', en: 'Long tongs', es: 'Pinzas largas' },
      { id: 'e3', en: 'Cutting board', es: 'Tabla de cortar' },
      { id: 'e4', en: 'Sharp knife', es: 'Cuchillo filoso' },
      { id: 'e5', en: 'Heavy aluminum tray', es: 'Charola de aluminio resistente' },
      { id: 'e6', en: 'Banana leaves (optional)', es: 'Hojas de plátano (opcional)' },
    ],
    steps: [
      {
        id: 's1',
        equipmentIds: ['e3', 'e4'],
        title: { en: 'Receive and inspect the meat', es: 'Recibir e inspeccionar la carne' },
        description: {
          en: 'Check that the cachete and beef cheek arrived fresh and at proper temperature. Trim excess fat but leave a thin layer for flavor.',
          es: 'Verifica que el cachete y la carne llegaron frescos y a temperatura correcta. Quita el exceso de grasa pero deja una capa fina para sabor.',
        },
      },
      {
        id: 's2',
        equipmentIds: ['e3'],
        title: { en: 'Season the meat', es: 'Sazonar la carne' },
        description: {
          en: 'Mix salt, garlic, cumin, and bay leaves. Cover the meat completely on all sides. Let it rest for 15 minutes.',
          es: 'Mezcla sal, ajo, comino y hojas de laurel. Cubre la carne completamente por todos lados. Deja reposar 15 minutos.',
        },
      },
      {
        id: 's3',
        equipmentIds: ['e1', 'e6'],
        title: { en: 'Set up the steamer', es: 'Preparar la vaporera' },
        description: {
          en: 'Add water to the bottom of the steam pot. Place the rack and line with banana leaves if using.',
          es: 'Agrega agua al fondo de la olla de vapor. Coloca la rejilla y forra con hojas de plátano si las vas a usar.',
        },
      },
      {
        id: 's4',
        equipmentIds: ['e1'],
        title: { en: 'Begin slow cooking', es: 'Comenzar cocción lenta' },
        description: {
          en: 'Place the meat on the rack. Cover tightly. Cook on medium-low heat for 8-9 hours. Check water level every 2 hours.',
          es: 'Coloca la carne en la rejilla. Tapa muy bien. Cocina a fuego medio-bajo por 8-9 horas. Revisa el nivel del agua cada 2 horas.',
        },
        timerMinutes: 540,
      },
      {
        id: 's5',
        equipmentIds: ['e2'],
        title: { en: 'Check doneness', es: 'Verificar cocción' },
        description: {
          en: 'The meat should pull apart easily with a fork. If still tough, continue cooking 30 more minutes.',
          es: 'La carne debe deshebrarse fácilmente con un tenedor. Si está dura, sigue cocinando 30 minutos más.',
        },
      },
      {
        id: 's6',
        equipmentIds: ['e2', 'e5'],
        title: { en: 'Shred and rest', es: 'Deshebrar y reposar' },
        description: {
          en: 'Remove the meat carefully. Shred while still warm. Cover and keep warm at serving temperature (above 140°F).',
          es: 'Saca la carne con cuidado. Deshebra cuando todavía esté caliente. Tapa y mantén caliente a temperatura de servicio (más de 140°F).',
        },
      },
      {
        id: 's7',
        equipmentIds: ['e5'],
        title: { en: 'Plate and serve', es: 'Servir' },
        description: {
          en: 'Serve in 1/2 lb or 1 lb portions. Always include red salsa, onion, and cilantro on the side.',
          es: 'Sirve en porciones de 1/2 lb o 1 lb. Siempre acompaña con salsa roja, cebolla y cilantro al lado.',
        },
      },
    ],
    tips: {
      en: 'Best made the night before for service the next day. The flavor improves overnight. Keep it covered and refrigerated, then reheat gently.',
      es: 'Mejor hacerla la noche anterior al servicio. El sabor mejora de un día para otro. Tápala y refrigérala, luego recalienta a fuego suave.',
    },
  },

  r2: {
    // Pollo Rostizado
    photoUrl: null,
    equipment: [
      { id: 'e1', en: 'Rotisserie oven', es: 'Horno rosticero' },
      { id: 'e2', en: 'Tongs', es: 'Pinzas' },
      { id: 'e3', en: 'Brining container', es: 'Recipiente para marinar' },
      { id: 'e4', en: 'Sharp knife', es: 'Cuchillo filoso' },
      { id: 'e5', en: 'Cutting board', es: 'Tabla de cortar' },
      { id: 'e6', en: 'Aluminum trays', es: 'Charolas de aluminio' },
    ],
    steps: [
      {
        id: 's1',
        equipmentIds: [],
        title: { en: 'Inspect chickens', es: 'Inspeccionar los pollos' },
        description: {
          en: 'Verify chickens are fresh, properly cleaned, and weigh between 3-4 lbs each. Remove giblets if present.',
          es: 'Verifica que los pollos estén frescos, bien limpios, y pesen entre 3-4 lbs cada uno. Quita las menudencias si las traen.',
        },
      },
      {
        id: 's2',
        equipmentIds: [],
        title: { en: 'Prepare marinade', es: 'Preparar el marinado' },
        description: {
          en: 'Mix achiote, garlic, orange juice, vinegar, oregano, cumin, salt, and pepper into a smooth paste.',
          es: 'Mezcla achiote, ajo, jugo de naranja, vinagre, orégano, comino, sal y pimienta hasta formar una pasta lisa.',
        },
      },
      {
        id: 's3',
        equipmentIds: [],
        title: { en: 'Marinate the chickens', es: 'Marinar los pollos' },
        description: {
          en: 'Rub marinade thoroughly inside and outside each chicken. Refrigerate for at least 4 hours, ideally overnight.',
          es: 'Unta el marinado bien por dentro y por fuera de cada pollo. Refrigera mínimo 4 horas, idealmente toda la noche.',
        },
        timerMinutes: 240,
      },
      {
        id: 's4',
        equipmentIds: [],
        title: { en: 'Tie the legs and load', es: 'Atar las patas y cargar' },
        description: {
          en: 'Tie the legs together with butcher twine. Skewer chickens onto rotisserie spits, leaving space between each one.',
          es: 'Ata las patas con hilo de carnicero. Ensarta los pollos en las varillas del rosticero, dejando espacio entre cada uno.',
        },
      },
      {
        id: 's5',
        equipmentIds: [],
        title: { en: 'Roast', es: 'Rostizar' },
        description: {
          en: 'Set rotisserie to 350°F. Cook for 1.5-2 hours until internal temperature reaches 165°F at the thickest part.',
          es: 'Pon el rosticero a 350°F. Cocina 1.5-2 horas hasta que la temperatura interna llegue a 165°F en la parte más gruesa.',
        },
        timerMinutes: 105,
      },
      {
        id: 's6',
        equipmentIds: [],
        title: { en: 'Rest and serve', es: 'Reposar y servir' },
        description: {
          en: 'Remove chickens. Rest for 5 minutes before serving. Pack hot in insulated bag with sides and salsa.',
          es: 'Saca los pollos. Deja reposar 5 minutos antes de servir. Empaca caliente en bolsa térmica con guarniciones y salsa.',
        },
      },
    ],
    tips: {
      en: 'Rotate spits halfway through if your rotisserie has uneven heat. The skin should be deep golden, not burnt.',
      es: 'Rota las varillas a la mitad del cocimiento si tu rosticero calienta disparejo. La piel debe estar dorada oscura, no quemada.',
    },
  },

  r3: {
    // Birria
    photoUrl: null,
    equipment: [
      { id: 'e1', en: 'Large stock pot', es: 'Olla grande' },
      { id: 'e2', en: 'Blender', es: 'Licuadora' },
      { id: 'e3', en: 'Strainer', es: 'Colador' },
      { id: 'e4', en: 'Cutting board', es: 'Tabla de cortar' },
      { id: 'e5', en: 'Sharp knife', es: 'Cuchillo filoso' },
      { id: 'e6', en: 'Wooden spoon', es: 'Cuchara de madera' },
    ],
    steps: [
      {
        id: 's1',
        equipmentIds: [],
        title: { en: 'Toast the chiles', es: 'Tostar los chiles' },
        description: {
          en: 'Lightly toast guajillo, ancho, and pasilla chiles on a hot comal until fragrant. Do not burn.',
          es: 'Tuesta ligeramente los chiles guajillo, ancho y pasilla en un comal caliente hasta que suelten aroma. No los quemes.',
        },
      },
      {
        id: 's2',
        equipmentIds: [],
        title: { en: 'Hydrate the chiles', es: 'Hidratar los chiles' },
        description: {
          en: 'Soak toasted chiles in hot water for 20 minutes until soft.',
          es: 'Remoja los chiles tostados en agua caliente por 20 minutos hasta que se ablanden.',
        },
        timerMinutes: 20,
      },
      {
        id: 's3',
        equipmentIds: [],
        title: { en: 'Blend the adobo', es: 'Licuar el adobo' },
        description: {
          en: 'Blend hydrated chiles with garlic, vinegar, oregano, cumin, cinnamon, cloves, and salt until smooth. Strain.',
          es: 'Licúa los chiles hidratados con ajo, vinagre, orégano, comino, canela, clavo y sal hasta que quede liso. Cuela.',
        },
      },
      {
        id: 's4',
        equipmentIds: [],
        title: { en: 'Sear the meat', es: 'Sellar la carne' },
        description: {
          en: 'In a hot pot with a bit of oil, brown the beef on all sides. Work in batches if needed.',
          es: 'En una olla caliente con poco aceite, dora la carne por todos lados. Trabaja por tandas si es necesario.',
        },
      },
      {
        id: 's5',
        equipmentIds: [],
        title: { en: 'Combine and simmer', es: 'Combinar y hervir lento' },
        description: {
          en: 'Pour adobo over the meat. Add water to cover. Bring to a boil, then reduce to low heat. Cover and cook 3-4 hours.',
          es: 'Vierte el adobo sobre la carne. Agrega agua para cubrir. Lleva a hervir, luego baja a fuego lento. Tapa y cocina 3-4 horas.',
        },
        timerMinutes: 210,
      },
      {
        id: 's6',
        equipmentIds: [],
        title: { en: 'Check tenderness', es: 'Verificar suavidad' },
        description: {
          en: 'Meat should be fork-tender. If not, continue cooking. Adjust salt and consistency of the broth.',
          es: 'La carne debe deshacerse con tenedor. Si no, sigue cocinando. Ajusta sal y consistencia del caldo.',
        },
      },
      {
        id: 's7',
        equipmentIds: [],
        title: { en: 'Plate', es: 'Servir' },
        description: {
          en: 'Serve in a deep bowl with broth (consomé). Top with onion, cilantro, and lime. Tortillas on the side.',
          es: 'Sirve en plato hondo con su caldo (consomé). Decora con cebolla, cilantro y limón. Tortillas al lado.',
        },
      },
    ],
    tips: {
      en: 'The longer it simmers, the better. Save the consomé — it sells well separately and is essential for quesabirrias.',
      es: 'Entre más tiempo hierva, mejor sabe. Guarda el consomé — se vende bien por separado y es esencial para quesabirrias.',
    },
  },

  r4: {
    // Carnitas
    photoUrl: null,
    equipment: [
      { id: 'e1', en: 'Large copper or heavy-bottom pot', es: 'Cazo de cobre u olla de fondo grueso' },
      { id: 'e2', en: 'Long wooden spoon', es: 'Cuchara de madera larga' },
      { id: 'e3', en: 'Slotted spoon', es: 'Espumadera' },
      { id: 'e4', en: 'Sharp knife', es: 'Cuchillo filoso' },
      { id: 'e5', en: 'Cutting board', es: 'Tabla de cortar' },
    ],
    steps: [
      {
        id: 's1',
        equipmentIds: ['e4', 'e5'],
        title: { en: 'Cut the pork', es: 'Cortar el cerdo' },
        description: {
          en: 'Cut pork shoulder into 2-3 inch chunks. Keep some fat for flavor. Mix cuts (loin, rib, belly) for variety.',
          es: 'Corta el espaldilla en trozos de 2-3 pulgadas. Deja algo de grasa para sabor. Mezcla cortes (lomo, costilla, panza) para variedad.',
        },
      },
      {
        id: 's2',
        equipmentIds: ['e1', 'e2'],
        title: { en: 'Render the lard', es: 'Derretir la manteca' },
        description: {
          en: 'In the pot, melt lard over medium heat. There should be enough to almost cover the meat.',
          es: 'En el cazo, derrite la manteca a fuego medio. Debe haber suficiente para casi cubrir la carne.',
        },
      },
      {
        id: 's3',
        equipmentIds: ['e1'],
        title: { en: 'Add the meat', es: 'Agregar la carne' },
        description: {
          en: 'Carefully add pork chunks. Add salt, garlic, bay leaves, orange peel, and a splash of milk for browning.',
          es: 'Con cuidado, agrega los trozos de cerdo. Añade sal, ajo, hojas de laurel, cáscara de naranja y un chorrito de leche para dorar.',
        },
      },
      {
        id: 's4',
        equipmentIds: ['e1', 'e2'],
        title: { en: 'Slow cook', es: 'Cocción lenta' },
        description: {
          en: 'Cook on medium-low for about 2 hours. Stir occasionally. The meat should bubble gently in the lard.',
          es: 'Cocina a fuego medio-bajo unas 2 horas. Mueve de vez en cuando. La carne debe burbujear suave en la manteca.',
        },
        timerMinutes: 120,
      },
      {
        id: 's5',
        equipmentIds: ['e1', 'e2'],
        title: { en: 'Crisp the exterior', es: 'Dorar el exterior' },
        description: {
          en: 'Raise heat slightly during last 30 minutes to crisp the outside. The lard will become more clear.',
          es: 'Sube el fuego un poco los últimos 30 minutos para dorar el exterior. La manteca se va a ver más clara.',
        },
        timerMinutes: 30,
      },
      {
        id: 's6',
        equipmentIds: ['e3'],
        title: { en: 'Drain and serve', es: 'Escurrir y servir' },
        description: {
          en: 'Remove with slotted spoon. Drain on rack. Chop or shred lightly before serving in 1/2 lb or 1 lb portions.',
          es: 'Saca con espumadera. Escurre sobre rejilla. Pica o deshebra ligeramente antes de servir en porciones de 1/2 lb o 1 lb.',
        },
      },
    ],
    tips: {
      en: 'Save the cooled lard — it can be reused 2-3 times and adds incredible flavor to the next batch.',
      es: 'Guarda la manteca cuando se enfríe — se puede reusar 2-3 veces y le da sabor increíble al siguiente lote.',
    },
  },

  r5: {
    // Chicharrón Carnudo
    photoUrl: null,
    equipment: [
      { id: 'e1', en: 'Large copper pot or fryer', es: 'Cazo de cobre grande o freidora' },
      { id: 'e2', en: 'Long tongs', es: 'Pinzas largas' },
      { id: 'e3', en: 'Slotted spoon', es: 'Espumadera' },
      { id: 'e4', en: 'Sharp knife', es: 'Cuchillo filoso' },
      { id: 'e5', en: 'Drainage rack', es: 'Rejilla para escurrir' },
    ],
    steps: [
      {
        id: 's1',
        equipmentIds: [],
        title: { en: 'Score the pork belly', es: 'Marcar la panza de cerdo' },
        description: {
          en: 'Score the skin in a diamond pattern, cutting through the skin and into the fat layer but not the meat.',
          es: 'Marca la piel en forma de diamantes, cortando la piel y la grasa pero sin llegar a la carne.',
        },
      },
      {
        id: 's2',
        equipmentIds: [],
        title: { en: 'Salt and rest', es: 'Salar y reposar' },
        description: {
          en: 'Salt the skin generously. Let rest at room temperature for 30 minutes to dry the surface.',
          es: 'Sala la piel generosamente. Deja reposar a temperatura ambiente 30 minutos para secar la superficie.',
        },
        timerMinutes: 30,
      },
      {
        id: 's3',
        equipmentIds: [],
        title: { en: 'Heat the lard', es: 'Calentar la manteca' },
        description: {
          en: 'Heat lard to 250°F (120°C). It should be deep enough to half-submerge the pork belly.',
          es: 'Calienta la manteca a 250°F (120°C). Debe estar suficientemente profunda para cubrir la mitad de la panza.',
        },
      },
      {
        id: 's4',
        equipmentIds: [],
        title: { en: 'First cook (low heat)', es: 'Primera cocción (fuego bajo)' },
        description: {
          en: 'Place pork belly skin-side down. Cook 1 hour to render the fat slowly. Do not let it brown yet.',
          es: 'Coloca la panza con la piel hacia abajo. Cocina 1 hora para derretir la grasa lentamente. No dejes que se dore todavía.',
        },
        timerMinutes: 60,
      },
      {
        id: 's5',
        equipmentIds: [],
        title: { en: 'Increase heat', es: 'Subir el fuego' },
        description: {
          en: 'Raise temperature to 375°F (190°C). The skin will start to bubble and crackle. This is the chicharrón forming.',
          es: 'Sube la temperatura a 375°F (190°C). La piel empieza a burbujear y truena. Así se forma el chicharrón.',
        },
      },
      {
        id: 's6',
        equipmentIds: [],
        title: { en: 'Final crisp', es: 'Dorado final' },
        description: {
          en: 'Cook 30-40 more minutes until skin is fully puffed and golden brown. Watch closely to avoid burning.',
          es: 'Cocina 30-40 minutos más hasta que la piel esté inflada y dorada. Vigila para que no se queme.',
        },
        timerMinutes: 35,
      },
      {
        id: 's7',
        equipmentIds: [],
        title: { en: 'Drain and chop', es: 'Escurrir y picar' },
        description: {
          en: 'Lift carefully onto the rack to drain. Once cooled slightly, chop into serving pieces.',
          es: 'Levanta con cuidado a la rejilla para escurrir. Cuando se enfríe un poco, pica en piezas para servir.',
        },
      },
    ],
    tips: {
      en: 'The two-temperature method is the secret. Low first to render fat, high second to crisp the skin. Never rush it.',
      es: 'El método de dos temperaturas es el secreto. Bajo primero para derretir grasa, alto después para dorar la piel. Nunca apresures.',
    },
  },

  r6: {
    // Tortillas de Maíz
    photoUrl: null,
    equipment: [
      { id: 'e1', en: 'Tortilla press', es: 'Prensa para tortillas' },
      { id: 'e2', en: 'Comal or flat griddle', es: 'Comal o plancha' },
      { id: 'e3', en: 'Plastic sheets (for press)', es: 'Plásticos (para la prensa)' },
      { id: 'e4', en: 'Mixing bowl', es: 'Tazón para mezclar' },
      { id: 'e5', en: 'Damp cloth', es: 'Trapo húmedo' },
    ],
    steps: [
      {
        id: 's1',
        equipmentIds: ['e4'],
        title: { en: 'Mix the masa', es: 'Mezclar la masa' },
        description: {
          en: 'Add water to maseca gradually. Knead until smooth and not sticky. The dough should hold its shape but not crack.',
          es: 'Agrega agua a la maseca poco a poco. Amasa hasta que esté lisa y no pegajosa. La masa debe sostener su forma pero no agrietarse.',
        },
      },
      {
        id: 's2',
        equipmentIds: ['e4'],
        title: { en: 'Test the consistency', es: 'Probar la consistencia' },
        description: {
          en: 'Form a small ball and press it. If edges crack, add a bit more water. If it sticks, add more masa.',
          es: 'Forma una bolita y aplástala. Si los bordes se agrietan, agrega un poco más de agua. Si se pega, agrega más masa.',
        },
      },
      {
        id: 's3',
        equipmentIds: ['e2'],
        title: { en: 'Heat the comal', es: 'Calentar el comal' },
        description: {
          en: 'Heat comal over medium-high heat. Test by sprinkling water — it should sizzle and evaporate quickly.',
          es: 'Calienta el comal a fuego medio-alto. Prueba salpicando agua — debe chisporrotear y evaporarse rápido.',
        },
      },
      {
        id: 's4',
        equipmentIds: ['e4', 'e5'],
        title: { en: 'Form the balls', es: 'Hacer las bolitas' },
        description: {
          en: 'Roll masa into balls roughly the size of a golf ball. Cover with damp cloth to prevent drying.',
          es: 'Haz bolitas de masa del tamaño de una pelota de golf. Tápalas con trapo húmedo para que no se sequen.',
        },
      },
      {
        id: 's5',
        equipmentIds: ['e1', 'e3'],
        title: { en: 'Press the tortillas', es: 'Prensar las tortillas' },
        description: {
          en: 'Place ball between two plastic sheets in the press. Press firmly. Tortilla should be about 6 inches in diameter.',
          es: 'Pon la bolita entre dos plásticos en la prensa. Presiona fuerte. La tortilla debe quedar de unas 6 pulgadas.',
        },
      },
      {
        id: 's6',
        equipmentIds: ['e2'],
        title: { en: 'Cook on the comal', es: 'Cocer en el comal' },
        description: {
          en: 'Cook 30 seconds, flip, cook 1 minute, flip again, cook 30 seconds. The tortilla should puff up.',
          es: 'Cuece 30 segundos, voltea, cuece 1 minuto, voltea otra vez, cuece 30 segundos. La tortilla debe inflarse.',
        },
        timerMinutes: 2,
      },
      {
        id: 's7',
        equipmentIds: ['e5'],
        title: { en: 'Stack and keep warm', es: 'Apilar y mantener caliente' },
        description: {
          en: 'Stack cooked tortillas in a cloth-lined tortillero to keep warm and soft.',
          es: 'Apila las tortillas cocidas en un tortillero forrado con trapo para mantenerlas calientes y suaves.',
        },
      },
    ],
    tips: {
      en: 'Fresh tortillas are best within 2 hours. The puff (when it inflates) is the sign of a properly cooked tortilla.',
      es: 'Las tortillas frescas son mejores dentro de 2 horas. El inflado es la señal de que la tortilla está bien cocida.',
    },
  },

  r7: {
    // Fajita de Pollo
    photoUrl: null,
    equipment: [
      { id: 'e1', en: 'Large flat-top griddle or comal', es: 'Plancha grande o comal' },
      { id: 'e2', en: 'Cutting board', es: 'Tabla de cortar' },
      { id: 'e3', en: 'Sharp knife', es: 'Cuchillo filoso' },
      { id: 'e4', en: 'Mixing bowl', es: 'Tazón' },
      { id: 'e5', en: 'Spatula', es: 'Espátula' },
    ],
    steps: [
      {
        id: 's1',
        equipmentIds: [],
        title: { en: 'Slice the chicken', es: 'Rebanar el pollo' },
        description: {
          en: 'Cut chicken breast into thin strips, about 1/4 inch thick. Cut against the grain for tenderness.',
          es: 'Corta la pechuga en tiras delgadas, de un cuarto de pulgada. Corta en contra de la fibra para que quede suave.',
        },
      },
      {
        id: 's2',
        equipmentIds: [],
        title: { en: 'Season the meat', es: 'Sazonar la carne' },
        description: {
          en: 'In a bowl, mix chicken with fajita seasoning, garlic, salt, and a touch of oil. Toss to coat evenly.',
          es: 'En un tazón, mezcla el pollo con sazonador de fajita, ajo, sal y un poco de aceite. Revuelve para cubrir parejo.',
        },
      },
      {
        id: 's3',
        equipmentIds: [],
        title: { en: 'Slice vegetables', es: 'Rebanar las verduras' },
        description: {
          en: 'Cut bell peppers and onions into thin strips. Keep them separate from the chicken until cooking time.',
          es: 'Corta los pimientos y cebollas en tiras delgadas. Mantenlos separados del pollo hasta el momento de cocinar.',
        },
      },
      {
        id: 's4',
        equipmentIds: [],
        title: { en: 'Heat the griddle', es: 'Calentar la plancha' },
        description: {
          en: 'Heat griddle to high heat. Add oil. It should shimmer but not smoke.',
          es: 'Calienta la plancha a fuego alto. Agrega aceite. Debe brillar pero no echar humo.',
        },
      },
      {
        id: 's5',
        equipmentIds: [],
        title: { en: 'Cook the chicken', es: 'Cocinar el pollo' },
        description: {
          en: 'Spread chicken on the hot griddle in a single layer. Cook 3-4 minutes without stirring to get color.',
          es: 'Extiende el pollo en la plancha caliente en una sola capa. Cocina 3-4 minutos sin mover para que tome color.',
        },
        timerMinutes: 4,
      },
      {
        id: 's6',
        equipmentIds: [],
        title: { en: 'Add the vegetables', es: 'Agregar las verduras' },
        description: {
          en: 'Add peppers and onions. Stir together with chicken. Cook another 3-4 minutes until vegetables are tender-crisp.',
          es: 'Agrega los pimientos y cebollas. Revuelve con el pollo. Cocina otros 3-4 minutos hasta que las verduras queden tiernas pero firmes.',
        },
        timerMinutes: 4,
      },
      {
        id: 's7',
        equipmentIds: [],
        title: { en: 'Adjust and serve', es: 'Ajustar y servir' },
        description: {
          en: 'Taste and adjust salt. Serve hot with tortillas, salsa, and cilantro.',
          es: 'Prueba y ajusta la sal. Sirve caliente con tortillas, salsa y cilantro.',
        },
      },
    ],
    tips: {
      en: 'Do not overcrowd the griddle — the chicken will steam instead of sear. Cook in batches if needed.',
      es: 'No amontones la plancha — el pollo se cocina al vapor en lugar de dorarse. Cocina por tandas si es necesario.',
    },
  },

  r8: {
    // Arroz Rojo
    photoUrl: null,
    equipment: [
      { id: 'e1', en: 'Heavy-bottom pot with lid', es: 'Olla de fondo grueso con tapa' },
      { id: 'e2', en: 'Wooden spoon', es: 'Cuchara de madera' },
      { id: 'e3', en: 'Mesh strainer', es: 'Colador de malla' },
      { id: 'e4', en: 'Cutting board', es: 'Tabla de cortar' },
      { id: 'e5', en: 'Sharp knife', es: 'Cuchillo filoso' },
    ],
    steps: [
      {
        id: 's1',
        equipmentIds: [],
        title: { en: 'Rinse the rice', es: 'Enjuagar el arroz' },
        description: {
          en: 'Rinse rice 2-3 times in cold water until water runs almost clear. Drain well.',
          es: 'Enjuaga el arroz 2-3 veces en agua fría hasta que el agua salga casi clara. Escurre bien.',
        },
      },
      {
        id: 's2',
        equipmentIds: [],
        title: { en: 'Make the tomato base', es: 'Preparar la base de jitomate' },
        description: {
          en: 'Blend tomato, garlic, and onion with a little water until smooth. Strain to remove seeds and pulp.',
          es: 'Licúa el jitomate, ajo y cebolla con un poco de agua hasta que quede liso. Cuela para quitar semillas y pulpa.',
        },
      },
      {
        id: 's3',
        equipmentIds: [],
        title: { en: 'Toast the rice', es: 'Tostar el arroz' },
        description: {
          en: 'Heat oil in pot. Add rice and stir constantly until grains are golden and fragrant, about 5 minutes.',
          es: 'Calienta el aceite en la olla. Agrega el arroz y mueve constantemente hasta que los granos estén dorados y aromáticos, unos 5 minutos.',
        },
        timerMinutes: 5,
      },
      {
        id: 's4',
        equipmentIds: [],
        title: { en: 'Add the base', es: 'Agregar la base' },
        description: {
          en: 'Pour in the tomato mixture. Stir and cook 2-3 minutes until it darkens and reduces slightly.',
          es: 'Vierte la mezcla de jitomate. Revuelve y cocina 2-3 minutos hasta que se oscurezca y reduzca un poco.',
        },
      },
      {
        id: 's5',
        equipmentIds: [],
        title: { en: 'Add broth and simmer', es: 'Agregar caldo y hervir' },
        description: {
          en: 'Add chicken broth and salt. Bring to a boil, then reduce to low heat and cover. Cook 20 minutes without lifting the lid.',
          es: 'Agrega el caldo de pollo y la sal. Lleva a hervor, luego baja a fuego lento y tapa. Cocina 20 minutos sin destapar.',
        },
        timerMinutes: 20,
      },
      {
        id: 's6',
        equipmentIds: [],
        title: { en: 'Rest the rice', es: 'Reposar el arroz' },
        description: {
          en: 'Turn off heat. Let stand covered for 5 more minutes. Then fluff with a fork.',
          es: 'Apaga el fuego. Deja reposar tapado 5 minutos más. Luego esponja con un tenedor.',
        },
        timerMinutes: 5,
      },
    ],
    tips: {
      en: 'Never lift the lid while cooking — it releases the steam needed to cook the rice properly.',
      es: 'Nunca destapes durante la cocción — se sale el vapor necesario para que el arroz se cocine bien.',
    },
  },

  r9: {
    // Frijoles Charros
    photoUrl: null,
    equipment: [
      { id: 'e1', en: 'Large pot', es: 'Olla grande' },
      { id: 'e2', en: 'Wooden spoon', es: 'Cuchara de madera' },
      { id: 'e3', en: 'Cutting board', es: 'Tabla de cortar' },
      { id: 'e4', en: 'Sharp knife', es: 'Cuchillo filoso' },
      { id: 'e5', en: 'Skillet for bacon and chorizo', es: 'Sartén para tocino y chorizo' },
    ],
    steps: [
      {
        id: 's1',
        equipmentIds: [],
        title: { en: 'Soak the beans', es: 'Remojar los frijoles' },
        description: {
          en: 'Rinse and soak pinto beans in water overnight (8 hours). This shortens cooking time and improves digestion.',
          es: 'Enjuaga y remoja los frijoles pintos en agua toda la noche (8 horas). Esto acorta el tiempo de cocción y mejora la digestión.',
        },
        timerMinutes: 480,
      },
      {
        id: 's2',
        equipmentIds: [],
        title: { en: 'Cook the beans', es: 'Cocer los frijoles' },
        description: {
          en: 'Drain beans. Add fresh water, cover, and cook on low for 2-3 hours until tender. Add salt only at the end.',
          es: 'Escurre los frijoles. Agrega agua nueva, tapa y cocina a fuego lento 2-3 horas hasta que estén suaves. Agrega sal solo al final.',
        },
        timerMinutes: 150,
      },
      {
        id: 's3',
        equipmentIds: [],
        title: { en: 'Cook the bacon', es: 'Cocinar el tocino' },
        description: {
          en: 'In a separate skillet, fry bacon until crispy. Reserve some fat. Set bacon aside.',
          es: 'En un sartén aparte, fríe el tocino hasta que esté crujiente. Reserva algo de grasa. Aparta el tocino.',
        },
      },
      {
        id: 's4',
        equipmentIds: [],
        title: { en: 'Cook the chorizo', es: 'Cocinar el chorizo' },
        description: {
          en: 'In the same fat, cook chorizo until browned, breaking it up with the spoon.',
          es: 'En la misma grasa, cocina el chorizo hasta dorar, desmenuzándolo con la cuchara.',
        },
      },
      {
        id: 's5',
        equipmentIds: [],
        title: { en: 'Add aromatics', es: 'Agregar los aromáticos' },
        description: {
          en: 'Add onion, garlic, and tomato to the chorizo. Cook 3-4 minutes until softened.',
          es: 'Agrega cebolla, ajo y jitomate al chorizo. Cocina 3-4 minutos hasta que se ablanden.',
        },
      },
      {
        id: 's6',
        equipmentIds: [],
        title: { en: 'Combine everything', es: 'Combinar todo' },
        description: {
          en: 'Add bacon, chorizo mixture, and chopped jalapeños to the beans. Simmer 20 more minutes.',
          es: 'Agrega el tocino, la mezcla de chorizo y los jalapeños picados a los frijoles. Hierve 20 minutos más.',
        },
        timerMinutes: 20,
      },
      {
        id: 's7',
        equipmentIds: [],
        title: { en: 'Finish and serve', es: 'Terminar y servir' },
        description: {
          en: 'Stir in fresh cilantro just before serving. Adjust salt. Serve hot in a deep bowl.',
          es: 'Agrega el cilantro fresco justo antes de servir. Ajusta la sal. Sirve caliente en plato hondo.',
        },
      },
    ],
    tips: {
      en: 'Do not add salt while beans are cooking — it toughens the skin. Always salt at the end.',
      es: 'No agregues sal mientras los frijoles se cocinan — endurece la piel. Siempre sala al final.',
    },
  },

  r10: {
    // Salsa Roja
    photoUrl: null,
    equipment: [
      { id: 'e1', en: 'Comal or skillet', es: 'Comal o sartén' },
      { id: 'e2', en: 'Blender', es: 'Licuadora' },
      { id: 'e3', en: 'Bowl', es: 'Tazón' },
      { id: 'e4', en: 'Sharp knife', es: 'Cuchillo filoso' },
      { id: 'e5', en: 'Strainer (optional)', es: 'Colador (opcional)' },
    ],
    steps: [
      {
        id: 's1',
        equipmentIds: [],
        title: { en: 'Toast the chiles', es: 'Tostar los chiles' },
        description: {
          en: 'Toast chile de árbol and guajillo on a hot comal until fragrant. Do not burn or salsa will be bitter.',
          es: 'Tuesta el chile de árbol y guajillo en comal caliente hasta que suelten aroma. No los quemes o la salsa va a estar amarga.',
        },
      },
      {
        id: 's2',
        equipmentIds: [],
        title: { en: 'Hydrate the chiles', es: 'Hidratar los chiles' },
        description: {
          en: 'Soak chiles in hot water for 10-15 minutes until soft.',
          es: 'Remoja los chiles en agua caliente 10-15 minutos hasta que se ablanden.',
        },
        timerMinutes: 12,
      },
      {
        id: 's3',
        equipmentIds: [],
        title: { en: 'Roast the tomatoes', es: 'Asar los jitomates' },
        description: {
          en: 'Roast tomatoes, onion, and garlic on the comal until charred on all sides.',
          es: 'Asa los jitomates, cebolla y ajo en el comal hasta que estén quemaditos por todos lados.',
        },
      },
      {
        id: 's4',
        equipmentIds: [],
        title: { en: 'Blend everything', es: 'Licuar todo' },
        description: {
          en: 'Blend hydrated chiles, roasted tomato, onion, garlic, and salt with a little soaking water until smooth.',
          es: 'Licúa los chiles hidratados, jitomate asado, cebolla, ajo y sal con un poco del agua de remojo hasta que quede liso.',
        },
      },
      {
        id: 's5',
        equipmentIds: [],
        title: { en: 'Adjust consistency', es: 'Ajustar la consistencia' },
        description: {
          en: 'Strain if you want it smoother. Add water if too thick. Taste and adjust salt.',
          es: 'Cuela si la quieres más lisa. Agrega agua si está muy espesa. Prueba y ajusta sal.',
        },
      },
      {
        id: 's6',
        equipmentIds: [],
        title: { en: 'Pack and store', es: 'Empacar y guardar' },
        description: {
          en: 'Pour into clean containers. Refrigerate. Best within 5 days.',
          es: 'Vierte en recipientes limpios. Refrigera. Mejor consumir en 5 días.',
        },
      },
    ],
    tips: {
      en: 'Roasting the tomatoes is essential — it deepens the flavor. Raw tomato salsa tastes flat in comparison.',
      es: 'Asar los jitomates es esencial — concentra el sabor. La salsa con jitomate crudo sabe sin cuerpo en comparación.',
    },
  },

  r11: {
    // Salsa Verde
    photoUrl: null,
    equipment: [
      { id: 'e1', en: 'Comal or skillet', es: 'Comal o sartén' },
      { id: 'e2', en: 'Blender', es: 'Licuadora' },
      { id: 'e3', en: 'Bowl', es: 'Tazón' },
      { id: 'e4', en: 'Sharp knife', es: 'Cuchillo filoso' },
    ],
    steps: [
      {
        id: 's1',
        equipmentIds: [],
        title: { en: 'Remove tomatillo husks', es: 'Pelar los tomatillos' },
        description: {
          en: 'Remove papery husks from tomatillos. Rinse off the sticky residue under cold water.',
          es: 'Quita las cáscaras de papel de los tomatillos. Enjuaga el residuo pegajoso con agua fría.',
        },
      },
      {
        id: 's2',
        equipmentIds: [],
        title: { en: 'Boil tomatillos', es: 'Hervir los tomatillos' },
        description: {
          en: 'Place tomatillos and serrano chiles in a pot with water. Boil 8-10 minutes until they change color.',
          es: 'Pon los tomatillos y chiles serranos en una olla con agua. Hierve 8-10 minutos hasta que cambien de color.',
        },
        timerMinutes: 9,
      },
      {
        id: 's3',
        equipmentIds: [],
        title: { en: 'Drain', es: 'Escurrir' },
        description: {
          en: 'Drain tomatillos and chiles, reserving some of the cooking water for blending.',
          es: 'Escurre los tomatillos y chiles, reservando algo del agua de cocción para licuar.',
        },
      },
      {
        id: 's4',
        equipmentIds: [],
        title: { en: 'Blend', es: 'Licuar' },
        description: {
          en: 'Blend tomatillos, chiles, garlic, onion, fresh cilantro, and salt with a little reserved water until smooth.',
          es: 'Licúa los tomatillos, chiles, ajo, cebolla, cilantro fresco y sal con un poco del agua reservada hasta que quede liso.',
        },
      },
      {
        id: 's5',
        equipmentIds: [],
        title: { en: 'Taste and adjust', es: 'Probar y ajustar' },
        description: {
          en: 'Taste and adjust salt. If too acidic, add a small piece of avocado or pinch of sugar.',
          es: 'Prueba y ajusta sal. Si está muy ácida, agrega un pedacito de aguacate o una pizca de azúcar.',
        },
      },
      {
        id: 's6',
        equipmentIds: [],
        title: { en: 'Pack and store', es: 'Empacar y guardar' },
        description: {
          en: 'Pour into clean containers. Refrigerate. Best within 4 days.',
          es: 'Vierte en recipientes limpios. Refrigera. Mejor consumir en 4 días.',
        },
      },
    ],
    tips: {
      en: 'Adding cilantro raw at the end keeps the bright green color. If you boil it, the salsa turns dull.',
      es: 'Agrega el cilantro crudo al final para mantener el verde brillante. Si lo hierves, la salsa pierde color.',
    },
  },

  r12: {
    // Chiles Rellenos
    photoUrl: null,
    equipment: [
      { id: 'e1', en: 'Comal', es: 'Comal' },
      { id: 'e2', en: 'Plastic bag (for steaming)', es: 'Bolsa de plástico (para sudar)' },
      { id: 'e3', en: 'Mixing bowls', es: 'Tazones' },
      { id: 'e4', en: 'Whisk', es: 'Batidor' },
      { id: 'e5', en: 'Frying skillet', es: 'Sartén para freír' },
      { id: 'e6', en: 'Tongs', es: 'Pinzas' },
      { id: 'e7', en: 'Paper towels', es: 'Toallas de papel' },
    ],
    steps: [
      {
        id: 's1',
        equipmentIds: [],
        title: { en: 'Roast the chiles', es: 'Asar los chiles' },
        description: {
          en: 'Roast poblano chiles directly over flame or on the comal until skin is blackened on all sides.',
          es: 'Asa los chiles poblanos directamente al fuego o en comal hasta que la piel esté quemada por todos lados.',
        },
      },
      {
        id: 's2',
        equipmentIds: [],
        title: { en: 'Sweat in a bag', es: 'Sudar en bolsa' },
        description: {
          en: 'Place hot chiles in a plastic bag. Close and let steam for 10 minutes. This loosens the skin.',
          es: 'Pon los chiles calientes en una bolsa de plástico. Cierra y deja sudar 10 minutos. Esto afloja la piel.',
        },
        timerMinutes: 10,
      },
      {
        id: 's3',
        equipmentIds: [],
        title: { en: 'Peel and slit', es: 'Pelar y abrir' },
        description: {
          en: 'Peel off the blackened skin. Make a vertical slit on one side and carefully remove seeds, leaving stem intact.',
          es: 'Pela la piel quemada. Haz una raja vertical en un lado y saca las semillas con cuidado, dejando el rabo intacto.',
        },
      },
      {
        id: 's4',
        equipmentIds: [],
        title: { en: 'Stuff with cheese', es: 'Rellenar con queso' },
        description: {
          en: 'Stuff each chile with Oaxaca cheese. Do not overstuff — they should close. Secure with toothpick if needed.',
          es: 'Rellena cada chile con queso Oaxaca. No los sobrecargues — deben cerrarse. Asegura con palillo si es necesario.',
        },
      },
      {
        id: 's5',
        equipmentIds: [],
        title: { en: 'Make the batter', es: 'Hacer el capeado' },
        description: {
          en: 'Separate egg whites and yolks. Beat whites to stiff peaks. Gently fold in yolks and a pinch of salt.',
          es: 'Separa las claras y yemas. Bate las claras a punto de turrón. Incorpora suavemente las yemas y una pizca de sal.',
        },
      },
      {
        id: 's6',
        equipmentIds: [],
        title: { en: 'Coat the chiles', es: 'Capear los chiles' },
        description: {
          en: 'Dust each stuffed chile lightly with flour. Then dip into the egg batter to coat completely.',
          es: 'Espolvorea cada chile relleno con harina ligeramente. Luego sumerge en el capeado de huevo para cubrir completo.',
        },
      },
      {
        id: 's7',
        equipmentIds: [],
        title: { en: 'Fry the chiles', es: 'Freír los chiles' },
        description: {
          en: 'Heat oil to 350°F. Fry chiles 2-3 minutes per side until golden brown. Drain on paper towels.',
          es: 'Calienta el aceite a 350°F. Fríe los chiles 2-3 minutos por lado hasta dorar. Escurre en toallas de papel.',
        },
        timerMinutes: 5,
      },
      {
        id: 's8',
        equipmentIds: [],
        title: { en: 'Serve hot', es: 'Servir caliente' },
        description: {
          en: 'Serve immediately with red sauce on top or on the side. Best while the batter is still crispy.',
          es: 'Sirve de inmediato con salsa roja encima o al lado. Mejor cuando el capeado todavía está crujiente.',
        },
      },
    ],
    tips: {
      en: 'Beat the egg whites until really stiff — this gives the batter its puffy, crispy texture. Cold eggs whip better.',
      es: 'Bate las claras hasta que estén muy firmes — eso le da al capeado su textura inflada y crujiente. Los huevos fríos se baten mejor.',
    },
  },
};

// ============================================================
// LAYER 2 — SEED DATA
// ============================================================

// SERVICE TEMPLATES — reusable bundles of service items applied to products
const SEED_SERVICE_TEMPLATES = [
  {
    id: 'st1',
    name: 'Carnes Service',
    description: 'Standard service for meat-by-the-pound orders',
    items: [
      { id: 'sti1', name: 'Salsa roja (cup 2oz)', cost: 0.15 },
      { id: 'sti2', name: 'Cebolla picada', cost: 0.10 },
      { id: 'sti3', name: 'Cilantro picado', cost: 0.08 },
      { id: 'sti4', name: 'Bolsa de papel', cost: 0.05 },
    ],
  },
  {
    id: 'st2',
    name: 'Pollo Service',
    description: 'Service items for roasted chicken orders',
    items: [
      { id: 'sti5', name: 'Bolsa caliente', cost: 0.12 },
      { id: 'sti6', name: 'Salsa cup', cost: 0.05 },
      { id: 'sti7', name: 'Tenedores plástico', cost: 0.08 },
      { id: 'sti8', name: 'Servilletas (4)', cost: 0.04 },
    ],
  },
  {
    id: 'st3',
    name: 'Combo Service',
    description: 'Family combo packaging with utensils',
    items: [
      { id: 'sti9', name: 'Caja familiar', cost: 0.45 },
      { id: 'sti10', name: 'Servilletas (10)', cost: 0.10 },
      { id: 'sti11', name: 'Utensilios desechables (4 sets)', cost: 0.32 },
      { id: 'sti12', name: 'Bolsa grande', cost: 0.10 },
    ],
  },
  {
    id: 'st4',
    name: 'Tacos Service',
    description: 'Toppings, condiments, and packaging for tacos',
    items: [
      { id: 'sti13', name: 'Salsa (porción)', cost: 0.08 },
      { id: 'sti14', name: 'Cebolla picada', cost: 0.05 },
      { id: 'sti15', name: 'Cilantro', cost: 0.04 },
      { id: 'sti16', name: 'Limón (1/4)', cost: 0.06 },
      { id: 'sti17', name: 'Plato aluminio', cost: 0.18 },
      { id: 'sti18', name: 'Servilletas (2)', cost: 0.02 },
    ],
  },
];

// PURCHASED ITEMS — products bought from suppliers, resold (no recipe needed)
const SEED_PURCHASED_ITEMS = [
  { id: 'pi1', name: 'Coca-Cola 2L', category: 'Bebidas', cost: 2.50, sellingPrice: 4.99 },
  { id: 'pi2', name: 'Coca-Cola Lata', category: 'Bebidas', cost: 0.45, sellingPrice: 1.50 },
  { id: 'pi3', name: 'Agua Botella', category: 'Bebidas', cost: 0.30, sellingPrice: 1.25 },
  { id: 'pi4', name: 'Jarritos', category: 'Bebidas', cost: 0.85, sellingPrice: 2.25 },
  { id: 'pi5', name: 'Tortilla de Harina (unidad)', category: 'Tortillería', cost: 0.10, sellingPrice: 0.30 },
  { id: 'pi6', name: 'Tortillas de Harina (docena)', category: 'Tortillería', cost: 1.20, sellingPrice: 3.49 },
];

// PRODUCTS — what you actually sell
// Type 'simple': single recipe, optional sizes
// Type 'combo':  multiple components (recipes + purchased items)
const SEED_PRODUCTS = [
  // ─── SIMPLE PRODUCTS ────────────────────────────────────────
  {
    id: 'p1',
    type: 'simple',
    name: 'Carnitas',
    category: 'Carnes',
    recipeId: 'r4',
    serviceTemplateId: 'st1',
    sizes: [
      { id: 'sz1', label: '1/2 lb', qty: 0.5, unit: 'Libras', sellingPrice: 7.99 },
      { id: 'sz2', label: '1 lb',   qty: 1,   unit: 'Libras', sellingPrice: 12.99 },
    ],
    notes: 'Incluye salsa roja, cebolla, cilantro',
  },
  {
    id: 'p2',
    type: 'simple',
    name: 'Barbacoa',
    category: 'Carnes',
    recipeId: 'r1',
    serviceTemplateId: 'st1',
    sizes: [
      { id: 'sz3', label: '1/2 lb', qty: 0.5, unit: 'Libras', sellingPrice: 7.99 },
      { id: 'sz4', label: '1 lb',   qty: 1,   unit: 'Libras', sellingPrice: 12.99 },
    ],
    notes: 'Incluye salsa roja, cebolla, cilantro',
  },
  {
    id: 'p3',
    type: 'simple',
    name: 'Birria',
    category: 'Carnes',
    recipeId: 'r3',
    serviceTemplateId: 'st1',
    sizes: [
      { id: 'sz5', label: '1/2 lb', qty: 0.07,  unit: 'Unidades', sellingPrice: 7.99 },  // ~half portion
      { id: 'sz6', label: '1 lb',   qty: 0.143, unit: 'Unidades', sellingPrice: 14.99 }, // ~one full portion
    ],
    notes: 'Birria de res. Incluye salsa, cebolla, cilantro.',
  },
  {
    id: 'p4',
    type: 'simple',
    name: 'Chicharrón Carnudo',
    category: 'Carnes',
    recipeId: 'r5',
    serviceTemplateId: 'st1',
    sizes: [
      { id: 'sz7', label: '1 lb', qty: 1, unit: 'Libras', sellingPrice: 17.99 },
    ],
    notes: 'Vendido por libra solamente.',
  },
  {
    id: 'p5',
    type: 'simple',
    name: 'Tortillas de Maíz',
    category: 'Tortillería',
    recipeId: 'r6',
    serviceTemplateId: null,
    sizes: [
      { id: 'sz8', label: '1 lb', qty: 1, unit: 'Libras', sellingPrice: 1.89 },
    ],
    notes: 'Recién hechas.',
  },
  {
    id: 'p6',
    type: 'simple',
    name: 'Medio Pollo',
    category: 'Pollos',
    recipeId: 'r2',
    serviceTemplateId: 'st2',
    sizes: [
      { id: 'sz9', label: 'Solo medio pollo', qty: 0.5, unit: 'Unidades', sellingPrice: 8.99 },
    ],
    notes: 'Vendido sin combo. Incluye 1/2 lb tortillas y 1 side.',
  },
  // ─── COMBO PRODUCTS ─────────────────────────────────────────
  {
    id: 'p7',
    type: 'combo',
    name: 'Pollo Entero',
    category: 'Combos',
    serviceTemplateId: 'st2',
    sellingPrice: 14.99,
    components: [
      { id: 'c1', type: 'recipe', recipeId: 'r2',  qty: 1,    unit: 'Unidades', label: '1 Pollo Rostizado' },
      { id: 'c2', type: 'recipe', recipeId: 'r6',  qty: 1,    unit: 'Libras',   label: '1 lb Tortillas de Maíz' },
      { id: 'c3', type: 'recipe', recipeId: 'r8',  qty: 0.75, unit: 'Libras',   label: 'Arroz Rojo (porción)' },
      { id: 'c4', type: 'recipe', recipeId: 'r9',  qty: 0.75, unit: 'Libras',   label: 'Frijoles Charros (porción)' },
      { id: 'c5', type: 'recipe', recipeId: 'r10', qty: 0.25, unit: 'Libras',   label: 'Salsa Roja 4oz' },
    ],
    notes: '1 pollo entero + 1 lb tortillas + 2 sides + salsa.',
  },
  {
    id: 'p8',
    type: 'combo',
    name: 'Combo Familiar',
    category: 'Combos',
    serviceTemplateId: 'st3',
    sellingPrice: 30.99,
    components: [
      { id: 'c6',  type: 'recipe',    recipeId: 'r2',  qty: 2,   unit: 'Unidades', label: '2 Pollos Rostizados' },
      { id: 'c7',  type: 'recipe',    recipeId: 'r6',  qty: 1.5, unit: 'Libras',   label: '1.5 lb Tortillas de Maíz' },
      { id: 'c8',  type: 'recipe',    recipeId: 'r8',  qty: 1.5, unit: 'Libras',   label: 'Arroz Rojo (2 porciones)' },
      { id: 'c9',  type: 'recipe',    recipeId: 'r9',  qty: 1.5, unit: 'Libras',   label: 'Frijoles Charros (2 porciones)' },
      { id: 'c10', type: 'recipe',    recipeId: 'r10', qty: 0.5, unit: 'Libras',   label: 'Salsa Roja 8oz' },
      { id: 'c11', type: 'purchased', purchasedItemId: 'pi1', qty: 1, label: 'Coca-Cola 2L' },
    ],
    notes: '2 pollos + 1.5 lb tortillas + 4 sides + salsa 8oz + Coca 2L.',
  },
  // ─── TACOS (Layer 2 hero use case) ───────────────────────────
  {
    id: 'p9',
    type: 'taco',
    name: 'Taco de Barbacoa',
    category: 'Tacos',
    carneRecipeId: 'r1',          // Barbacoa
    carneGramsPerTaco: 85,        // standard taco portion
    tortillasPerTaco: 2,          // double tortilla
    serviceTemplateId: 'st4',     // Tacos Service
    sizes: [
      { id: 'tsz1', label: 'Maíz',   tortillaType: 'maiz',   sellingPrice: 2.99 },
      { id: 'tsz2', label: 'Harina', tortillaType: 'harina', tortillasOverride: 1, sellingPrice: 3.59 },
    ],
    notes: 'Doble tortilla. Servido con salsa, cebolla, cilantro, limón en plato aluminio.',
  },
  {
    id: 'p10',
    type: 'taco',
    name: 'Taco de Carnitas',
    category: 'Tacos',
    carneRecipeId: 'r4',          // Carnitas
    carneGramsPerTaco: 85,
    tortillasPerTaco: 2,
    serviceTemplateId: 'st4',
    sizes: [
      { id: 'tsz3', label: 'Maíz',   tortillaType: 'maiz',   sellingPrice: 2.99 },
      { id: 'tsz4', label: 'Harina', tortillaType: 'harina', tortillasOverride: 1, sellingPrice: 3.59 },
    ],
    notes: 'Doble tortilla. Servido con salsa, cebolla, cilantro, limón en plato aluminio.',
  },
  {
    id: 'p11',
    type: 'taco',
    name: 'Taco de Birria',
    category: 'Tacos',
    carneRecipeId: 'r3',          // Birria
    carneGramsPerTaco: 85,
    tortillasPerTaco: 2,
    serviceTemplateId: 'st4',
    sizes: [
      { id: 'tsz5', label: 'Maíz',   tortillaType: 'maiz',   sellingPrice: 2.99 },
      { id: 'tsz6', label: 'Harina', tortillaType: 'harina', tortillasOverride: 1, sellingPrice: 3.59 },
    ],
    notes: 'Doble tortilla. Servido con salsa, cebolla, cilantro, limón en plato aluminio.',
  },
  {
    id: 'p12',
    type: 'taco',
    name: 'Taco de Chicharrón',
    category: 'Tacos',
    carneRecipeId: 'r5',          // Chicharrón
    carneGramsPerTaco: 85,
    tortillasPerTaco: 2,
    serviceTemplateId: 'st4',
    sizes: [
      { id: 'tsz7', label: 'Maíz',   tortillaType: 'maiz',   sellingPrice: 2.99 },
      { id: 'tsz8', label: 'Harina', tortillaType: 'harina', tortillasOverride: 1, sellingPrice: 3.59 },
    ],
    notes: 'Doble tortilla. Servido con salsa, cebolla, cilantro, limón en plato aluminio.',
  },
  {
    id: 'p13',
    type: 'taco',
    name: 'Taco de Fajita de Pollo',
    category: 'Tacos',
    carneRecipeId: 'r7',          // Fajita de Pollo
    carneGramsPerTaco: 85,
    tortillasPerTaco: 2,
    serviceTemplateId: 'st4',
    sizes: [
      { id: 'tsz9',  label: 'Maíz',   tortillaType: 'maiz',   sellingPrice: 2.99 },
      { id: 'tsz10', label: 'Harina', tortillaType: 'harina', tortillasOverride: 1, sellingPrice: 3.59 },
    ],
    notes: 'Pollo a la fajita con cebolla y pimiento. Doble tortilla. Servido con salsa, cebolla, cilantro, limón en plato aluminio.',
  },
];

// ============================================================
// CALCULATION ENGINE
// ============================================================
// Module-level reference to the Master List of ingredients.
// Updated by the top-level RecipeCostCalculator component each render so that
// calculateRecipe() (called in many places) can resolve migrated ingredients
// without threading the master list through every call site.
let _MASTER_INGREDIENTS_REF = [];
const setMasterIngredientsRef = (list) => { _MASTER_INGREDIENTS_REF = list || []; };

// When a recipe ingredient has been migrated, its `totalCost` is derived live
// from the Master List entry (price + merma). This lets us change a price in
// one place and have all recipes recalculate. Legacy (skipped) ingredients keep
// their hardcoded `totalCost`.
const resolveIngredientCost = (ing, masterIngredients = _MASTER_INGREDIENTS_REF) => {
  if (!ing.ingredientId) return ing; // legacy / skipped — use hardcoded fields
  const master = masterIngredients.find((m) => m.id === ing.ingredientId);
  if (!master) return ing; // master entry missing — fallback to hardcoded
  const converted = convertUnits(Number(ing.qty) || 0, ing.unit, master.recipeUnit);
  if (converted == null) {
    return { ...ing, _masterMismatch: true };
  }
  const { perUnitWithMerma } = calcIngredientCost(master);
  const totalCost = converted * perUnitWithMerma;
  return {
    ...ing,
    unitCost: perUnitWithMerma,
    totalCost,
    _resolvedFromMaster: true,
    _masterName: master.name,
    _masterUnit: master.recipeUnit,
    _convertedQty: converted,
  };
};

const calculateRecipe = (r, masterIngredients = _MASTER_INGREDIENTS_REF) => {
  // Resolve any migrated ingredients live from the Master List
  const resolved = {
    ...r,
    ingredients: r.ingredients.map((i) => resolveIngredientCost(i, masterIngredients)),
  };
  const ingredientsEnriched = resolved.ingredients.map((i) => {
    const conv = toBase(i.qty, i.unit);
    const costPerBase = conv && conv.qtyInBase > 0
      ? (Number(i.totalCost) || 0) / conv.qtyInBase
      : null;
    return { ...i, qtyInBase: conv?.qtyInBase, baseUnit: conv?.baseUnit, costPerBase };
  });

  const ingredientsTotal = resolved.ingredients.reduce((s, i) => s + (Number(i.totalCost) || 0), 0);
  const yieldValue = Number(r.yield.value) || 1;
  const ingredientCostPerUnit = ingredientsTotal / yieldValue;

  // Yield in base units — used by Layer 2 to calculate "120g of barbacoa per taco"
  const yieldConv = toBase(yieldValue, r.yield.unit);
  const yieldInBase = yieldConv?.qtyInBase;
  const yieldBaseUnit = yieldConv?.baseUnit;
  const realCostPerBase = yieldInBase && yieldInBase > 0
    ? null  // computed after realCostPerUnit below
    : null;

  const includeCook = r.includeCookInLabor !== false;
  const laborHours = (Number(r.prepHours) || 0) + (includeCook ? (Number(r.cookHours) || 0) : 0);
  const workers = Math.max(1, Number(r.workers) || 1);
  const laborTotal = laborHours * workers * (Number(r.laborRate) || 0);
  const laborPerUnit = laborTotal / yieldValue;

  // Gas & Electricity — calculated as a percentage of ingredient cost (industry standard 10–15%, default 12%).
  // This covers gas, electricity, water, and miscellaneous kitchen overhead.
  const overheadPct = r.overheadPct !== undefined ? Number(r.overheadPct) : 12;
  const utilitiesPerUnit = ingredientCostPerUnit * (overheadPct / 100);
  const utilitiesTotal = ingredientsTotal * (overheadPct / 100);
  // Keep gas/electricity split for backward compat & display, but folded into utilities total
  const gasTotal = 0;
  const electricityTotal = 0;

  // Packaging — sum all components (container + bag + wrap + other)
  const pkg = r.packaging || {};
  // Backward compat: if packaging is a plain number, use it as 'other'
  const packagingPerUnit = typeof pkg === 'number'
    ? pkg
    : (Number(pkg.container) || 0) + (Number(pkg.bag) || 0) + (Number(pkg.wrap) || 0) + (Number(pkg.other) || 0);

  const operationalPerUnit = laborPerUnit + utilitiesPerUnit + packagingPerUnit;
  const realCostPerUnit = ingredientCostPerUnit + operationalPerUnit;
  // Cost per gram (or ml, or unit) — critical for Layer 2 (Products)
  // E.g. "Taco uses 120g of barbacoa" → 120 × realCostPerBaseG = ingredient cost contribution
  const realCostPerBaseUnit = yieldInBase && yieldInBase > 0
    ? (realCostPerUnit * yieldValue) / yieldInBase
    : null;

  const priceAt = (m) => realCostPerUnit / (1 - m);

  const currentPrice = Number(r.currentPrice) || 0;
  const competitorLow = Number(r.competitorPriceLow) || 0;
  const competitorHigh = Number(r.competitorPriceHigh) || 0;

  const actualMargin = currentPrice > 0 ? ((currentPrice - realCostPerUnit) / currentPrice) * 100 : 0;
  const profitPerUnit = currentPrice - realCostPerUnit;
  const vsRealCost = realCostPerUnit > 0 ? ((currentPrice - realCostPerUnit) / realCostPerUnit) * 100 : 0;

  let healthStatus = 'none';
  if (currentPrice > 0 && realCostPerUnit > 0) {
    if (actualMargin < 25) healthStatus = 'losing';
    else if (actualMargin < 35) healthStatus = 'tight';
    else healthStatus = 'healthy';
  }

  let marketPosition = 'unknown';
  if (currentPrice > 0 && competitorLow > 0 && competitorHigh > 0) {
    if (currentPrice < competitorLow) marketPosition = 'below';
    else if (currentPrice > competitorHigh) marketPosition = 'above';
    else marketPosition = 'within';
  }

  return {
    ingredientsTotal, ingredientCostPerUnit,
    ingredientsEnriched,           // each ingredient with qtyInBase, baseUnit, costPerBase
    yieldInBase, yieldBaseUnit,    // yield converted to grams/ml/units (null if unknown unit)
    realCostPerBaseUnit,           // cost per gram/ml/unit — for Layer 2 product portioning
    laborHours, laborTotal, laborPerUnit, workers,
    gasTotal, electricityTotal, utilitiesTotal, utilitiesPerUnit,
    overheadPct,
    packagingPerUnit, operationalPerUnit, realCostPerUnit,
    prices: { 30: priceAt(.30), 35: priceAt(.35), 40: priceAt(.40), 45: priceAt(.45), 50: priceAt(.50) },
    currentPrice, competitorLow, competitorHigh,
    actualMargin, profitPerUnit, vsRealCost,
    healthStatus, marketPosition,
  };
};

// ============================================================
// LAYER 2 — PRODUCT CALCULATION ENGINE
// ============================================================
//
// Uses Layer 1 recipe outputs (realCostPerBaseUnit, realCostPerUnit) plus:
//   - Service Templates (sum of their items)
//   - Purchased Items (direct cost)
//   - Flat costs (sides at $X, etc.)
//
// For combo components that use a recipe at a specific qty/unit:
//   - Try base-unit conversion (most accurate, e.g. 120g of barbacoa)
//   - Fall back to qty-in-yield-unit math (e.g. 0.5 lb of carnitas)

const calculateServiceTemplate = (template) => {
  if (!template) return { total: 0, items: [] };
  const total = template.items.reduce((s, i) => s + (Number(i.cost) || 0), 0);
  return { total, items: template.items };
};

// Calculate cost of a single product component
const calculateComponent = (component, recipes, purchasedItems) => {
  if (!component) return { cost: 0, label: 'Unknown' };
  switch (component.type) {
    case 'recipe': {
      const recipe = recipes.find((r) => r.id === component.recipeId);
      if (!recipe) return { cost: 0, label: component.label || 'Missing recipe' };
      const recipeCalc = calculateRecipe(recipe);

      // Try base-unit conversion first (most accurate for cross-unit math)
      const compConv = toBase(component.qty, component.unit);
      if (compConv && recipeCalc.realCostPerBaseUnit !== null && compConv.baseUnit === recipeCalc.yieldBaseUnit) {
        const cost = compConv.qtyInBase * recipeCalc.realCostPerBaseUnit;
        return { cost, label: component.label, recipeCalc };
      }
      // Fall back: same-unit math (qty × cost-per-yield-unit)
      // Works when component.unit === recipe.yield.unit
      const cost = (Number(component.qty) || 0) * recipeCalc.realCostPerUnit;
      return { cost, label: component.label, recipeCalc };
    }
    case 'purchased': {
      const item = purchasedItems.find((p) => p.id === component.purchasedItemId);
      if (!item) return { cost: 0, label: component.label || 'Missing item' };
      return { cost: (Number(component.qty) || 1) * (Number(item.cost) || 0), label: component.label, item };
    }
    case 'flat':
      return { cost: Number(component.cost) || 0, label: component.label };
    default:
      return { cost: 0, label: 'Unknown component type' };
  }
};

// Calculate full product cost — works for both 'simple' (uses sizes) and 'combo'
const calculateProduct = (product, recipes, serviceTemplates, purchasedItems, sizeId) => {
  if (!product) return null;

  // Resolve service template
  const template = serviceTemplates.find((t) => t.id === product.serviceTemplateId);
  const serviceCalc = calculateServiceTemplate(template);

  if (product.type === 'simple') {
    // Use selected size (or first size by default)
    const size = sizeId
      ? product.sizes.find((s) => s.id === sizeId) || product.sizes[0]
      : product.sizes[0];
    if (!size) return null;

    const recipe = recipes.find((r) => r.id === product.recipeId);
    if (!recipe) {
      return { error: 'Recipe missing', size, recipe: null };
    }
    const recipeCalc = calculateRecipe(recipe);

    // Calculate the recipe portion cost (using base units if compatible)
    const sizeConv = toBase(size.qty, size.unit);
    let recipeCost = 0;
    if (sizeConv && recipeCalc.realCostPerBaseUnit !== null && sizeConv.baseUnit === recipeCalc.yieldBaseUnit) {
      recipeCost = sizeConv.qtyInBase * recipeCalc.realCostPerBaseUnit;
    } else {
      recipeCost = (Number(size.qty) || 0) * recipeCalc.realCostPerUnit;
    }

    const totalCost = recipeCost + serviceCalc.total;
    const sellingPrice = Number(size.sellingPrice) || 0;
    const profit = sellingPrice - totalCost;
    const margin = sellingPrice > 0 ? (profit / sellingPrice) * 100 : 0;

    return {
      type: 'simple',
      size,
      recipe,
      recipeCalc,
      recipeCost,
      serviceCalc,
      serviceCost: serviceCalc.total,
      totalCost,
      sellingPrice,
      profit,
      margin,
      healthStatus: margin < 25 ? 'losing' : margin < 35 ? 'tight' : 'healthy',
    };
  }

  // TACO — carne + tortillas (Maíz vs Harina) + service
  if (product.type === 'taco') {
    const size = sizeId
      ? product.sizes.find((s) => s.id === sizeId) || product.sizes[0]
      : product.sizes[0];
    if (!size) return null;

    // Carne cost (always grams)
    const carneRecipe = recipes.find((r) => r.id === product.carneRecipeId);
    let carneCost = 0;
    let carneCalc = null;
    if (carneRecipe) {
      carneCalc = calculateRecipe(carneRecipe);
      const carneGrams = Number(product.carneGramsPerTaco) || 0;
      if (carneCalc.realCostPerBaseUnit && carneCalc.yieldBaseUnit === 'g') {
        carneCost = carneGrams * carneCalc.realCostPerBaseUnit;
      } else {
        // Fallback if recipe yield isn't in grams (shouldn't happen for meat recipes)
        carneCost = (carneGrams / 453.592) * carneCalc.realCostPerUnit; // approx via lb
      }
    }

    // Tortilla cost — depends on size. Each size can override the default tortilla count
    // (Maíz uses 2 by default — double tortilla; Harina uses 1 — single tortilla)
    const numTortillas = size.tortillasOverride !== undefined
      ? Number(size.tortillasOverride) || 0
      : Number(product.tortillasPerTaco) || 0;
    let tortillaCost = 0;
    let tortillaDetail = '';
    if (size.tortillaType === 'maiz') {
      // Tortillas de Maíz from recipe r6 (yields lb; ~30 tortillas per lb → ~15.1g each)
      const tortRecipe = recipes.find((r) => r.id === 'r6');
      if (tortRecipe) {
        const tortCalc = calculateRecipe(tortRecipe);
        const gramsPerTortilla = 15.1; // standard 6" corn tortilla
        const totalTortGrams = numTortillas * gramsPerTortilla;
        tortillaCost = (tortCalc.realCostPerBaseUnit || 0) * totalTortGrams;
        tortillaDetail = `${numTortillas} tortillas de maíz (${totalTortGrams.toFixed(1)}g)`;
      }
    } else {
      // Tortillas de Harina — purchased item (Liz buys them, doesn't make them)
      const harTortilla = purchasedItems.find((p) => p.id === 'pi5');
      const perUnit = harTortilla ? Number(harTortilla.cost) || 0 : 0.10;
      tortillaCost = numTortillas * perUnit;
      tortillaDetail = `${numTortillas} tortillas de harina (purchased ${money(perUnit)} each)`;
    }

    const totalCost = carneCost + tortillaCost + serviceCalc.total;
    const sellingPrice = Number(size.sellingPrice) || 0;
    const profit = sellingPrice - totalCost;
    const margin = sellingPrice > 0 ? (profit / sellingPrice) * 100 : 0;

    return {
      type: 'taco',
      size,
      carneRecipe,
      carneCalc,
      carneCost,
      carneGrams: Number(product.carneGramsPerTaco) || 0,
      numTortillas,
      tortillaCost,
      tortillaDetail,
      serviceCalc,
      serviceCost: serviceCalc.total,
      totalCost,
      sellingPrice,
      profit,
      margin,
      healthStatus: margin < 25 ? 'losing' : margin < 35 ? 'tight' : 'healthy',
    };
  }

  // COMBO
  const componentResults = (product.components || []).map((comp) =>
    ({ comp, ...calculateComponent(comp, recipes, purchasedItems) })
  );
  const componentsTotal = componentResults.reduce((s, c) => s + c.cost, 0);
  const totalCost = componentsTotal + serviceCalc.total;
  const sellingPrice = Number(product.sellingPrice) || 0;
  const profit = sellingPrice - totalCost;
  const margin = sellingPrice > 0 ? (profit / sellingPrice) * 100 : 0;

  return {
    type: 'combo',
    componentResults,
    componentsTotal,
    serviceCalc,
    serviceCost: serviceCalc.total,
    totalCost,
    sellingPrice,
    profit,
    margin,
    healthStatus: margin < 25 ? 'losing' : margin < 35 ? 'tight' : 'healthy',
  };
};

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const pct = (n) => `${(Number(n) || 0).toFixed(1)}%`;

// ============================================================
// UNIT SYSTEM — base units + conversions
// ============================================================
// Three internal base units (used for all calculations):
//   - g    (weight)
//   - ml   (volume)
//   - unit (count)
//
// Each user-facing unit has: { dimension, base, factor, label, imprecise? }
// Convert any user qty to base units: qty × factor
// Convert base units back to display: qtyInBase ÷ factor

const UNITS = {
  // Weight (base: g)
  Libras:       { dimension: 'weight', base: 'g',    factor: 453.592, label: 'Libras' },
  Onzas:        { dimension: 'weight', base: 'g',    factor: 28.3495, label: 'Onzas' },
  Kilos:        { dimension: 'weight', base: 'g',    factor: 1000,    label: 'Kilos' },
  Gramos:       { dimension: 'weight', base: 'g',    factor: 1,       label: 'Gramos' },
  // Volume (base: ml)
  Galones:      { dimension: 'volume', base: 'ml',   factor: 3785.41, label: 'Galones' },
  Cuartos:      { dimension: 'volume', base: 'ml',   factor: 946.353, label: 'Cuartos' },
  Litros:       { dimension: 'volume', base: 'ml',   factor: 1000,    label: 'Litros' },
  Taza:         { dimension: 'volume', base: 'ml',   factor: 240,     label: 'Taza' },
  Cucharada:    { dimension: 'volume', base: 'ml',   factor: 15,      label: 'Cucharada', imprecise: true },
  Cucharadita:  { dimension: 'volume', base: 'ml',   factor: 5,       label: 'Cucharadita', imprecise: true },
  // Count (base: unit)
  Unidades:     { dimension: 'count',  base: 'unit', factor: 1,       label: 'Unidades' },
  Cabezas:      { dimension: 'count',  base: 'unit', factor: 1,       label: 'Cabezas' },
  Lata:         { dimension: 'count',  base: 'unit', factor: 1,       label: 'Lata' },
  Manojo:       { dimension: 'count',  base: 'unit', factor: 1,       label: 'Manojo' },
  Tanda:        { dimension: 'count',  base: 'unit', factor: 1,       label: 'Tanda' },
};

const UNIT_OPTIONS = Object.keys(UNITS);

// Aliases — map old/duplicate units to canonical ones (for backward compat with seed data)
const UNIT_ALIASES = {
  'Libra': 'Libras',
  'lb':    'Libras',
  'lbs':   'Libras',
  'Oz':    'Onzas',
  'oz':    'Onzas',
  'Galón': 'Galones',
  'gal':   'Galones',
  'Kilo':  'Kilos',
  'kg':    'Kilos',
  'Gramo': 'Gramos',
  'g':     'Gramos',
  'Litro': 'Litros',
  'L':     'Litros',
  'l':     'Litros',
  'Unidad': 'Unidades',
  'unit':   'Unidades',
  'unidad': 'Unidades',
  'Cabeza': 'Cabezas',
};

// Resolve any unit string to its canonical entry
function resolveUnit(rawUnit) {
  if (!rawUnit) return null;
  const canonical = UNIT_ALIASES[rawUnit] || rawUnit;
  return UNITS[canonical] ? { name: canonical, ...UNITS[canonical] } : null;
}

// Convert a (qty, unit) pair to base units. Returns { qtyInBase, baseUnit } or null if unknown unit.
function toBase(qty, unit) {
  const u = resolveUnit(unit);
  if (!u) return null;
  return { qtyInBase: (Number(qty) || 0) * u.factor, baseUnit: u.base };
}

// Check if two units share the same dimension (can be cross-converted)
function sameDimension(unitA, unitB) {
  const a = resolveUnit(unitA);
  const b = resolveUnit(unitB);
  return a && b && a.dimension === b.dimension;
}

const tokens = {
  fontBody: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  fontMono: "'JetBrains Mono', 'SF Mono', Menlo, monospace",
};

const MARGIN_TONES = {
  30: { bg: 'bg-rose-50',    ring: 'ring-rose-200',    text: 'text-rose-700',    activeBg: 'bg-rose-100',    activeRing: 'ring-rose-500',    dot: 'bg-rose-500'    },
  35: { bg: 'bg-amber-50',   ring: 'ring-amber-200',   text: 'text-amber-800',   activeBg: 'bg-amber-100',   activeRing: 'ring-amber-500',   dot: 'bg-amber-500'   },
  40: { bg: 'bg-sky-50',     ring: 'ring-sky-200',     text: 'text-sky-700',     activeBg: 'bg-sky-100',     activeRing: 'ring-sky-500',     dot: 'bg-sky-500'     },
  45: { bg: 'bg-emerald-50', ring: 'ring-emerald-200', text: 'text-emerald-700', activeBg: 'bg-emerald-100', activeRing: 'ring-emerald-500', dot: 'bg-emerald-500' },
  50: { bg: 'bg-violet-50',  ring: 'ring-violet-200',  text: 'text-violet-700',  activeBg: 'bg-violet-100',  activeRing: 'ring-violet-500',  dot: 'bg-violet-500'  },
};

const HEALTH_TONE = {
  losing:  { bg: 'bg-rose-50',    ring: 'ring-rose-200',    text: 'text-rose-700',    dot: 'bg-rose-500',    labelKey: 'health.losing' },
  tight:   { bg: 'bg-amber-50',   ring: 'ring-amber-200',   text: 'text-amber-700',   dot: 'bg-amber-500',   labelKey: 'health.tight'  },
  healthy: { bg: 'bg-emerald-50', ring: 'ring-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500', labelKey: 'health.healthy' },
  none:    { bg: 'bg-slate-50',   ring: 'ring-slate-200',   text: 'text-slate-400',   dot: 'bg-slate-300',   labelKey: 'health.none'   },
};

// ============================================================
// MAIN
// ============================================================
// The outer export wraps the app in LanguageProvider so every component
// down the tree can call useT() / useContext(LanguageContext) for i18n.
// Production swap: wrap with i18next's <I18nextProvider> instead.
// ============================================================
// CONFIRM DIALOG — promise-based, slick replacement for window.confirm
// ============================================================
// Usage:  const confirm = useConfirm();
//         if (!(await confirm({ title, message, danger: true }))) return;
const ConfirmContext = createContext(() => Promise.resolve(false));
const useConfirm = () => useContext(ConfirmContext);

function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null); // { options, resolve }

  const confirm = React.useCallback(
    (options = {}) => new Promise((resolve) => setDialog({ options, resolve })),
    []
  );

  const settle = (result) => {
    setDialog((cur) => {
      if (cur) cur.resolve(result);
      return null;
    });
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <ConfirmDialog
          options={dialog.options}
          onConfirm={() => settle(true)}
          onCancel={() => settle(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

function ConfirmDialog({ options, onConfirm, onCancel }) {
  const { lang } = useT();
  const {
    title = 'Are you sure?',
    message = '',
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    danger = false,
  } = options || {};
  const body = message || (danger ? (lang === 'es' ? 'Esta acción no se puede deshacer.' : 'This action cannot be undone.') : '');

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
      else if (e.key === 'Enter') onConfirm();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Matches the app's "Confirm Migration" modal styling. Portaled to
  // document.body (like TrainingMode) so it floats above BeeNexa's sticky
  // header/nav; the wrapper re-declares the scoped id so Tailwind still applies.
  return createPortal(
    <div id="ec-recipe-cost-root">
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm"
        style={{ fontFamily: tokens.fontBody, animation: 'ec-confirm-backdrop 150ms ease-out both' }}
        onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      >
        <style>{`
          @keyframes ec-confirm-backdrop { from { opacity: 0 } to { opacity: 1 } }
          @keyframes ec-confirm-pop { from { opacity: 0; transform: translateY(10px) scale(0.96) } to { opacity: 1; transform: translateY(0) scale(1) } }
        `}</style>
        <div
          role="alertdialog"
          aria-modal="true"
          className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
          style={{ animation: 'ec-confirm-pop 200ms cubic-bezier(0.16, 1, 0.3, 1) both' }}
        >
          <div className="p-5 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md bg-gradient-to-br ${danger ? 'from-rose-500 to-rose-600' : 'from-amber-500 to-amber-600'}`}>
                <AlertTriangle className="w-5 h-5" strokeWidth={2.2} />
              </div>
              <h3 className="text-[17px] font-bold text-slate-900">{title}</h3>
            </div>
          </div>
          {body ? (
            <div className="p-5">
              <p className="text-[13px] text-slate-700 leading-relaxed break-words">{body}</p>
            </div>
          ) : null}
          <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-white rounded-lg transition"
            >
              {cancelLabel}
            </button>
            <button
              onClick={onConfirm}
              autoFocus
              className={`px-5 py-2.5 text-[13px] font-semibold text-white rounded-xl shadow-sm flex items-center gap-1.5 transition ${danger ? 'bg-rose-600 hover:bg-rose-700 shadow-rose-600/30' : 'bg-teal-600 hover:bg-teal-700 shadow-teal-600/30'}`}
            >
              {danger ? <Trash2 className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function RecipeCostApp({ locationId }) {
  return (
    <div id="ec-recipe-cost-root">
      <LanguageProvider>
        <ConfirmProvider>
          <RecipeCostCalculatorInner locationId={locationId} />
        </ConfirmProvider>
      </LanguageProvider>
    </div>
  );
}

function RecipeCostCalculatorInner({ locationId }) {
  const { t } = useT();
  const confirm = useConfirm();
  const [recipes, setRecipes] = useState(SEED_RECIPES);
  const [products, setProducts] = useState(SEED_PRODUCTS);
  const [serviceTemplates, setServiceTemplates] = useState(SEED_SERVICE_TEMPLATES);
  const [purchasedItems, setPurchasedItems] = useState(SEED_PURCHASED_ITEMS);
  const [ingredients, setIngredients] = useState(SEED_INGREDIENTS);
  const [showMigration, setShowMigration] = useState(false);
  // Backup of original recipes — used by Revert Migration in Step 2.
  // In Step 1 we only capture this when user opens the migration screen.
  const [recipesBackup, setRecipesBackup] = useState(null);
  // Keep the module-level master list ref in sync so calculateRecipe() can
  // resolve migrated ingredients to live Master List costs. Set on every render
  // — cheap and ensures resolution always uses the latest data.
  setMasterIngredientsRef(ingredients);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [activeId, setActiveId] = useState(null);
  const [activeProductId, setActiveProductId] = useState(null);
  const [activeStepsId, setActiveStepsId] = useState(null);
  const [stepsLanguage, setStepsLanguage] = useState('en'); // 'en' or 'es' for Steps playbook
  const [playbooks, setPlaybooks] = useState(PLAYBOOKS);
  const updatePlaybook = (recipeId, updated) => setPlaybooks({ ...playbooks, [recipeId]: updated });

  // -- BeeNexa backend sync (additive) --------------------------------
  // On mount, hydrate the Master List + recipes for this tenant. Anything
  // that fails (offline, no locationId, auth) leaves the in-memory seed data
  // in place so the module still works as a demo. Recipe edits persist via a
  // debounced save once the initial load has completed.
  const loadedRef = useRef(false);
  useEffect(() => {
    if (!locationId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await RecipeCostApi.listIngredients(locationId);
        const list = (res && res.ingredients) || [];
        if (!cancelled) {
          if (list.length) {
            setIngredients(list.map(serverToClientIngredient));
          } else {
            const seeded = await RecipeCostApi.seedIngredients(locationId);
            const sl = (seeded && seeded.ingredients) || [];
            if (!cancelled && sl.length) setIngredients(sl.map(serverToClientIngredient));
          }
        }
      } catch (e) { /* offline -> keep seed ingredients */ }
      try {
        const rc = await RecipeCostApi.listRecipes(locationId);
        if (!cancelled && rc && Array.isArray(rc.recipes) && rc.recipes.length) {
          setRecipes(rc.recipes);
          if (rc.hasBackup) setRecipesBackup(rc.recipesBackup || []);
        }
      } catch (e) { /* offline -> keep seed recipes */ }
      if (!cancelled) loadedRef.current = true;
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  // Debounced recipe persistence (only after the initial hydrate).
  useEffect(() => {
    if (!locationId || !loadedRef.current) return undefined;
    const h = setTimeout(() => { RecipeCostApi.saveRecipes(locationId, recipes).catch(() => {}); }, 800);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipes, locationId]);

  const activeRecipe = recipes.find((r) => r.id === activeId);
  const activeProduct = products.find((p) => p.id === activeProductId);
  const updateRecipe = (updated) => setRecipes(recipes.map((r) => (r.id === updated.id ? updated : r)));
  const updateProduct = (updated) => setProducts(products.map((p) => (p.id === updated.id ? updated : p)));
  const updateServiceTemplate = (updated) => setServiceTemplates(serviceTemplates.map((s) => (s.id === updated.id ? updated : s)));
  const updatePurchasedItem = (updated) => setPurchasedItems(purchasedItems.map((p) => (p.id === updated.id ? updated : p)));

  const duplicate = (id) => {
    const src = recipes.find((r) => r.id === id);
    if (!src) return;
    const copy = {
      ...src, id: `r${Date.now()}`, name: `${src.name} (copy)`,
      ingredients: src.ingredients.map((i, idx) => ({ ...i, id: `i${Date.now()}-${idx}` })),
    };
    setRecipes([...recipes, copy]);
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: t('recipe.deleteRecipeConfirm'),
      confirmLabel: t('action.delete'),
      cancelLabel: t('action.cancel'),
      danger: true,
    });
    if (!ok) return;
    setRecipes(recipes.filter((r) => r.id !== id));
    if (activeId === id) setActiveId(null);
  };

  const createNew = () => {
    const blank = {
      id: `r${Date.now()}`,
      name: 'New Recipe', category: 'Uncategorized',
      yield: { value: 1, unit: 'Unidades', rawQty: 0, rawUnit: 'Libras' }, status: 'draft', ingredients: [],
      prepHours: 0, cookHours: 0, laborRate: 12,
      workers: 1,
      overheadPct: 12,
      packaging: { container: 0, bag: 0, wrap: 0, other: 0 },
      includeCookInLabor: true,
      currentPrice: 0, competitorPriceLow: 0, competitorPriceHigh: 0, notes: '',
    };
    setRecipes([...recipes, blank]);
    setActiveTab('recipes');
    setActiveId(blank.id);
  };

  return (
    <div className="min-h-screen bg-slate-100" style={{ fontFamily: tokens.fontBody }}>
      {/* ═══════════════════════════════════════════════════════
          TOP BAR — System identity (Level 1)
          Brand "EL CAMPESINO" in green (tortilleria brand)
          Product name secondary below
          Sticky, premium feel
          ═══════════════════════════════════════════════════════ */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-none mx-auto px-4 sm:px-6 lg:px-8">
          {/* Row 1 · brand + actions */}
          <div className="py-3.5 sm:py-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center shadow-md shadow-emerald-700/25 flex-shrink-0">
                <ChefHat className="w-5.5 h-5.5 sm:w-6 sm:h-6 text-white" strokeWidth={2.2} />
              </div>
              <div className="leading-tight min-w-0">
                <div className="text-[12px] sm:text-[13px] font-bold tracking-[0.18em] text-emerald-700 uppercase truncate">
                  {t('app.brand').replace('EL CAMPESINO', 'El Campesino')}
                </div>
                <div className="text-[15px] sm:text-[17px] font-semibold text-slate-800 -mt-0.5 truncate">
                  {t('app.title')}
                </div>
              </div>
            </div>
            <div className="flex-shrink-0">
              <AppLanguageToggle />
            </div>
          </div>
        </div>

        {/* Row 2 · tab nav (pill style — like Houses Project) */}
        <div className="border-t border-slate-100 bg-slate-50/50">
          <div className="max-w-none mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-3.5">
            <div className="flex items-center gap-2 sm:gap-2.5 overflow-x-auto scrollbar-none -mx-1 px-1">
              <TabButton
                active={activeTab === 'dashboard'}
                onClick={() => { setActiveTab('dashboard'); setActiveId(null); setActiveProductId(null); }}
                icon={<DollarSign className="w-4 h-4" />}
                label={t('tab.recipeCosts')}
                module="dashboard"
              />
              <TabButton
                active={activeTab === 'recipes'}
                onClick={() => { setActiveTab('recipes'); setActiveProductId(null); }}
                icon={<BookOpen className="w-4 h-4" />}
                label={t('tab.recipes')}
                badge={recipes.length}
                module="recipes"
              />
              <TabButton
                active={activeTab === 'ingredients'}
                onClick={() => { setActiveTab('ingredients'); setActiveId(null); setActiveProductId(null); setActiveStepsId(null); }}
                icon={<Leaf className="w-4 h-4" />}
                label={t('tab.ingredients')}
                badge={ingredients.length}
                module="ingredients"
              />
              <TabButton
                active={activeTab === 'products'}
                onClick={() => { setActiveTab('products'); setActiveId(null); setActiveStepsId(null); }}
                icon={<ShoppingBag className="w-4 h-4" />}
                label={t('tab.menu')}
                badge={products.length}
                module="products"
              />
              <TabButton
                active={activeTab === 'steps'}
                onClick={() => { setActiveTab('steps'); setActiveId(null); setActiveProductId(null); }}
                icon={<ChefHat className="w-4 h-4" />}
                label={t('tab.steps')}
                badge={recipes.length}
                module="steps"
              />
              <TabButton
                active={activeTab === 'analytics'}
                onClick={() => { setActiveTab('analytics'); setActiveId(null); setActiveProductId(null); setActiveStepsId(null); }}
                icon={<BarChart3 className="w-4 h-4" />}
                label={t('tab.analytics')}
                module="analytics"
              />
            </div>
          </div>
        </div>
      </header>

      {/* === CONTENT AREA === */}
      <div className="max-w-none mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {activeTab === 'dashboard' && (
          <DashboardTab
            recipes={recipes}
            onOpen={(id) => { setActiveTab('recipes'); setActiveId(id); }}
          />
        )}

        {activeTab === 'recipes' && !activeRecipe && (
          <RecipesTab
            recipes={recipes}
            onOpen={(id) => setActiveId(id)}
            onDuplicate={duplicate}
            onDelete={remove}
            onNew={createNew}
          />
        )}

        {activeTab === 'recipes' && activeRecipe && (
          <RecipeDetail
            recipe={activeRecipe}
            onBack={() => setActiveId(null)}
            onUpdate={updateRecipe}
            onDuplicate={() => duplicate(activeRecipe.id)}
            onDelete={() => remove(activeRecipe.id)}
          />
        )}

        {activeTab === 'products' && !activeProduct && (
          <ProductsTab
            products={products}
            recipes={recipes}
            serviceTemplates={serviceTemplates}
            purchasedItems={purchasedItems}
            onOpen={(id) => setActiveProductId(id)}
          />
        )}

        {activeTab === 'products' && activeProduct && (
          <ProductDetail
            product={activeProduct}
            recipes={recipes}
            serviceTemplates={serviceTemplates}
            purchasedItems={purchasedItems}
            onBack={() => setActiveProductId(null)}
            onUpdate={updateProduct}
            onDelete={async () => {
              const ok = await confirm({
                title: t('recipe.deleteMenuItemConfirm'),
                confirmLabel: t('action.delete'),
                cancelLabel: t('action.cancel'),
                danger: true,
              });
              if (!ok) return;
              setProducts(products.filter((p) => p.id !== activeProduct.id));
              setActiveProductId(null);
            }}
          />
        )}

        {activeTab === 'steps' && !activeStepsId && (
          <StepsTab
            recipes={recipes}
            playbooks={playbooks}
            onOpen={(id) => setActiveStepsId(id)}
          />
        )}

        {activeTab === 'steps' && activeStepsId && (
          <StepsDetail
            recipe={recipes.find((r) => r.id === activeStepsId)}
            playbook={playbooks[activeStepsId]}
            onUpdatePlaybook={(updated) => updatePlaybook(activeStepsId, updated)}
            language={stepsLanguage}
            onChangeLanguage={setStepsLanguage}
            onBack={() => setActiveStepsId(null)}
          />
        )}

        {activeTab === 'ingredients' && (
          <IngredientsTab
            ingredients={ingredients}
            recipes={recipes}
            onCreate={(ing) => { setIngredients([...ingredients, ing]); if (locationId) RecipeCostApi.saveIngredient(locationId, clientToServerIngredient(ing)).catch(() => {}); }}
            onUpdate={(ing) => { setIngredients(ingredients.map((x) => x.id === ing.id ? ing : x)); if (locationId) RecipeCostApi.saveIngredient(locationId, clientToServerIngredient(ing)).catch(() => {}); }}
            onDelete={(id) => { setIngredients(ingredients.filter((x) => x.id !== id)); if (locationId) RecipeCostApi.deleteIngredient(locationId, id).catch(() => {}); }}
            onDuplicate={(id) => {
              const src = ingredients.find((x) => x.id === id);
              if (!src) return;
              const copy = { ...src, id: `ing_${Date.now()}`, name: `${src.name} (copy)` };
              setIngredients([...ingredients, copy]);
              if (locationId) RecipeCostApi.saveIngredient(locationId, clientToServerIngredient(copy)).catch(() => {});
            }}
            onOpenMigration={() => {
              // Capture backup of recipes the first time migration screen is opened
              if (!recipesBackup) setRecipesBackup(JSON.parse(JSON.stringify(recipes)));
              if (locationId) RecipeCostApi.backupRecipes(locationId, recipes).catch(() => {});
              setShowMigration(true);
            }}
            hasBackup={!!recipesBackup}
            onRevert={() => {
              if (recipesBackup) {
                setRecipes(JSON.parse(JSON.stringify(recipesBackup)));
                // Keep the backup in case user wants to migrate again later
              }
              if (locationId) RecipeCostApi.revertMigration(locationId).catch(() => {});
            }}
          />
        )}

        {activeTab === 'analytics' && <AnalyticsTab recipes={recipes} />}
      </div>

      {/* Migration Review overlay (Step 2: review + apply with confirmation) */}
      {showMigration && (
        <MigrationReview
          recipes={recipes}
          ingredients={ingredients}
          onClose={() => setShowMigration(false)}
          onAddIngredient={(ing) => setIngredients((prev) => [...prev, ing])}
          onApply={(decisions) => {
            // Apply the user-approved decisions to recipes.
            // For each recipe ingredient with action 'accept': add ingredientId
            // For 'skip': leave as-is (keeps hardcoded fields)
            const updated = recipes.map((r) => ({
              ...r,
              ingredients: r.ingredients.map((ing, idx) => {
                const key = `${r.id}::${idx}`;
                const d = decisions[key];
                if (!d || d.action !== 'accept' || !d.ingredientId) return ing;
                // Migrated: attach ingredientId, keep qty/unit as user entered them.
                // Drop hardcoded unitCost/totalCost since they'll be derived live
                // from Master List via calculateRecipe → resolveIngredientCost.
                // Apply nameOverride (Master English by default) for consistency.
                const { unitCost, totalCost, ...rest } = ing;
                return {
                  ...rest,
                  ingredientId: d.ingredientId,
                  name: d.nameOverride || rest.name,
                };
              }),
            }));
            setRecipes(updated);
            if (locationId) RecipeCostApi.applyMigration(locationId, updated).catch(() => {});
            setShowMigration(false);
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// TAB NAV
// ============================================================
// Per-module color identity for tabs (active state — solid fill pill style)
const MODULE_TAB_COLORS = {
  dashboard:  { activeBg: 'bg-emerald-600', activeShadow: 'shadow-emerald-600/30', activeBadge: 'bg-emerald-700/40 text-white' },
  recipes:    { activeBg: 'bg-blue-600',    activeShadow: 'shadow-blue-600/30',    activeBadge: 'bg-blue-700/40 text-white' },
  ingredients:{ activeBg: 'bg-teal-600',    activeShadow: 'shadow-teal-600/30',    activeBadge: 'bg-teal-700/40 text-white' },
  products:   { activeBg: 'bg-violet-600',  activeShadow: 'shadow-violet-600/30',  activeBadge: 'bg-violet-700/40 text-white' },
  steps:      { activeBg: 'bg-orange-600',  activeShadow: 'shadow-orange-600/30',  activeBadge: 'bg-orange-700/40 text-white' },
  analytics:  { activeBg: 'bg-slate-800',   activeShadow: 'shadow-slate-800/30',   activeBadge: 'bg-slate-700/60 text-white' },
};

function TabButton({ active, onClick, icon, label, badge, module = 'recipes' }) {
  const c = MODULE_TAB_COLORS[module] || MODULE_TAB_COLORS.recipes;
  return (
    <button
      onClick={onClick}
      className={`flex-shrink-0 flex items-center gap-2 px-4 sm:px-5 py-2.5 sm:py-3 rounded-full text-[14px] sm:text-[14px] font-semibold transition-all whitespace-nowrap ${
        active
          ? `${c.activeBg} text-white shadow-md ${c.activeShadow}`
          : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 hover:border-slate-300'
      }`}
    >
      {icon}
      <span>{label}</span>
      {typeof badge === 'number' && (
        <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-bold transition-colors ${
          active ? c.activeBadge : 'bg-slate-100 text-slate-600'
        }`}>
          {badge}
        </span>
      )}
    </button>
  );
}

// ============================================================
// MODULE HERO — Level 2 visual hierarchy: which module you're in
// ============================================================
// Per-module identity: each module has its own icon color and personality
// while keeping consistent structure across the app.

const MODULE_IDENTITY = {
  dashboard: {
    icon: DollarSign,
    iconBg: 'from-emerald-500 to-emerald-600',
    iconShadow: 'shadow-emerald-500/30',
    accentBorder: 'border-emerald-200',
    accentBg: 'bg-emerald-50/30',
    accent: 'emerald',
    titleKey: 'tab.recipeCosts',
    subtitleKey: 'module.recipeCosts.subtitle',
  },
  recipes: {
    icon: BookOpen,
    iconBg: 'from-blue-500 to-blue-600',
    iconShadow: 'shadow-blue-500/30',
    accentBorder: 'border-blue-200',
    accentBg: 'bg-blue-50/30',
    accent: 'blue',
    titleKey: 'tab.recipes',
    subtitleKey: 'module.recipes.subtitle',
  },
  ingredients: {
    icon: Leaf,
    iconBg: 'from-teal-500 to-teal-600',
    iconShadow: 'shadow-teal-500/30',
    accentBorder: 'border-teal-200',
    accentBg: 'bg-teal-50/30',
    accent: 'teal',
    titleKey: 'tab.ingredients',
    subtitleKey: 'module.ingredients.subtitle',
  },
  steps: {
    icon: ChefHat,
    iconBg: 'from-orange-500 to-orange-600',
    iconShadow: 'shadow-orange-500/30',
    accentBorder: 'border-orange-200',
    accentBg: 'bg-orange-50/30',
    accent: 'orange',
    titleKey: 'tab.steps',
    subtitleKey: 'module.steps.subtitle',
  },
  products: {
    icon: ShoppingBag,
    iconBg: 'from-violet-500 to-violet-600',
    iconShadow: 'shadow-violet-500/30',
    accentBorder: 'border-violet-200',
    accentBg: 'bg-violet-50/30',
    accent: 'violet',
    titleKey: 'tab.menu',
    subtitleKey: 'module.menu.subtitle',
  },
  analytics: {
    icon: BarChart3,
    iconBg: 'from-slate-700 to-slate-800',
    iconShadow: 'shadow-slate-700/30',
    accentBorder: 'border-slate-300',
    accentBg: 'bg-slate-50/50',
    accent: 'slate',
    titleKey: 'tab.analytics',
    subtitleKey: 'module.analytics.subtitle',
  },
};

function ModuleHero({ module, rightSlot }) {
  const { t } = useT();
  const m = MODULE_IDENTITY[module];
  if (!m) return null;
  const Icon = m.icon;
  return (
    <div className={`mb-6 sm:mb-7 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-4 sm:py-5 ${m.accentBg} border-b ${m.accentBorder}`}>
      <div className="max-w-none mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-3.5 min-w-0 flex-1">
          <div className={`flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-br ${m.iconBg} flex items-center justify-center text-white shadow-md ${m.iconShadow}`}>
            <Icon className="w-5 h-5 sm:w-5.5 sm:h-5.5" strokeWidth={2.2} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-[20px] sm:text-[24px] font-bold text-slate-900 tracking-tight leading-tight">
              {t(m.titleKey)}
            </h2>
            <p className="text-[12px] sm:text-[13px] text-slate-500 leading-snug mt-0.5">
              {t(m.subtitleKey)}
            </p>
          </div>
        </div>
        {rightSlot && <div className="flex-shrink-0 w-full sm:w-auto">{rightSlot}</div>}
      </div>
    </div>
  );
}

// ============================================================
// PDF PREVIEW + EXPORT SYSTEM
// ============================================================
//
// Usage:
//   const [showPdf, setShowPdf] = useState(false);
//   <button onClick={() => setShowPdf(true)}>Print</button>
//   {showPdf && <PDFPreviewModal docSpec={...} onClose={() => setShowPdf(false)} />}
//
// docSpec shape:
// {
//   title: 'Barbacoa',
//   subtitle: 'Recipe Cost Breakdown',
//   filename: 'barbacoa-cost.pdf',
//   sections: [{ title: 'Ingredients', rows: [['Name', 'Qty', 'Total'], ...] }],
//   footer: 'Generated on Jun 24, 2026'
// }
// ============================================================

// Dynamically load jsPDF + html2canvas from CDN.
// Returns a promise that resolves when both globals are available.
// Cached after first load.
let _pdfLibsPromise = null;
const loadPdfLibs = () => {
  if (_pdfLibsPromise) return _pdfLibsPromise;
  _pdfLibsPromise = new Promise((resolve, reject) => {
    if (window.jspdf && window.html2canvas) {
      resolve({ jsPDF: window.jspdf.jsPDF, html2canvas: window.html2canvas });
      return;
    }
    const loadScript = (src) => new Promise((res, rej) => {
      // Skip if already in DOM
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        // If still loading, wait for it
        if (existing.dataset.loaded === 'true') return res();
        existing.addEventListener('load', () => res());
        existing.addEventListener('error', () => rej(new Error(`Failed to load ${src}`)));
        return;
      }
      const s = document.createElement('script');
      s.src = src;
      s.onload = () => { s.dataset.loaded = 'true'; res(); };
      s.onerror = () => rej(new Error(`Failed to load ${src}`));
      document.head.appendChild(s);
    });
    Promise.all([
      loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js'),
      loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js'),
    ])
      .then(() => {
        if (!window.jspdf || !window.html2canvas) {
          reject(new Error('PDF libraries failed to load'));
          return;
        }
        resolve({ jsPDF: window.jspdf.jsPDF, html2canvas: window.html2canvas });
      })
      .catch(reject);
  });
  return _pdfLibsPromise;
};

// Helper: format today's date for footer
const todayLabel = () => {
  const d = new Date();
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

// PDF preview document — rendered as a paper-shaped div the user can see.
// The same DOM is rasterized and embedded into a real PDF on download/print.
// Wraps a fixed-width document so it scales responsively. Measures the inner
// content height (which can grow with more ingredients/sections) and applies
// that × scale to the outer container, so the flex parent gives it correct
// space and overflow scrolling works.
function ScaledDocumentWrapper({ scale, children }) {
  const innerRef = useRef(null);
  const [innerHeight, setInnerHeight] = useState(1056);

  useEffect(() => {
    if (!innerRef.current) return;
    // ResizeObserver for content changes; fallback to a one-time measure
    const measure = () => {
      if (innerRef.current) {
        setInnerHeight(innerRef.current.offsetHeight);
      }
    };
    measure();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(innerRef.current);
    }
    const t = setTimeout(measure, 200); // re-measure after fonts/images settle
    return () => { if (ro) ro.disconnect(); clearTimeout(t); };
  }, [children]);

  return (
    <div
      style={{
        width: 816 * scale,
        height: innerHeight * scale,
        flexShrink: 0,
      }}
    >
      <div
        ref={innerRef}
        className="shadow-2xl rounded-sm overflow-hidden bg-white"
        style={{
          width: 816,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function PDFDocument({ docSpec }) {
  return (
    <div
      id="pdf-document-content"
      className="bg-white text-slate-900"
      style={{
        // US Letter aspect (8.5 × 11) — using fixed width for crisp render
        width: '816px',
        minHeight: '1056px',
        padding: '48px 56px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: '12px',
        lineHeight: '1.5',
        color: '#0f172a',
      }}
    >
      {/* Header band — El Campesino branding */}
      <div style={{ borderBottom: '3px solid #047857', paddingBottom: '20px', marginBottom: '28px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{
            width: '52px', height: '52px', borderRadius: '12px',
            background: 'linear-gradient(135deg, #059669, #047857)',
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '24px', fontWeight: 'bold', flexShrink: 0,
          }}>EC</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '11px', fontWeight: 'bold', letterSpacing: '0.12em', color: '#047857', textTransform: 'uppercase' }}>
              El Campesino · Tortillería
            </div>
            <div style={{ fontSize: '15px', fontWeight: 600, color: '#1e293b', marginTop: '2px' }}>
              {docSpec.subtitle || 'Recipe Cost Calculator'}
            </div>
          </div>
          <div style={{ textAlign: 'right', fontSize: '10px', color: '#64748b' }}>
            <div style={{ fontWeight: 'bold', color: '#475569' }}>{docSpec.docType || 'COST REPORT'}</div>
            <div style={{ marginTop: '2px' }}>{todayLabel()}</div>
          </div>
        </div>
      </div>

      {/* Document title */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#0f172a', letterSpacing: '-0.01em' }}>
          {docSpec.title}
        </div>
        {docSpec.titleMeta && (
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>{docSpec.titleMeta}</div>
        )}
      </div>

      {/* KPI strip (optional) */}
      {docSpec.kpis && docSpec.kpis.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${docSpec.kpis.length}, 1fr)`, gap: '10px', marginBottom: '24px' }}>
          {docSpec.kpis.map((k, idx) => (
            <div key={idx} style={{
              border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 14px',
              background: k.tone === 'good' ? '#ecfdf5' : k.tone === 'warn' ? '#fef3c7' : k.tone === 'bad' ? '#fef2f2' : '#f8fafc',
            }}>
              <div style={{ fontSize: '9px', fontWeight: 'bold', letterSpacing: '0.1em', color: '#64748b', textTransform: 'uppercase' }}>
                {k.label}
              </div>
              <div style={{
                fontSize: '20px', fontWeight: 'bold', marginTop: '4px',
                color: k.tone === 'good' ? '#047857' : k.tone === 'warn' ? '#b45309' : k.tone === 'bad' ? '#b91c1c' : '#0f172a',
                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
              }}>
                {k.value}
              </div>
              {k.sublabel && <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>{k.sublabel}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Sections */}
      {docSpec.sections && docSpec.sections.map((section, idx) => (
        <div key={idx} style={{ marginBottom: '24px', breakInside: 'avoid' }}>
          <div style={{
            fontSize: '11px', fontWeight: 'bold', color: '#475569',
            letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px',
            borderBottom: '1px solid #e2e8f0', paddingBottom: '6px',
          }}>
            {section.title}
          </div>
          {section.note && (
            <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '10px', fontStyle: 'italic' }}>
              {section.note}
            </div>
          )}
          {/* Table */}
          {section.rows && section.rows.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  {section.rows[0].map((h, i) => (
                    <th key={i} style={{
                      padding: '8px 10px', textAlign: i === 0 ? 'left' : 'right',
                      fontWeight: 'bold', color: '#475569', fontSize: '10px',
                      letterSpacing: '0.05em', textTransform: 'uppercase',
                      borderBottom: '1px solid #cbd5e1',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {section.rows.slice(1).map((row, ri) => (
                  <tr key={ri} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    {row.map((cell, ci) => (
                      <td key={ci} style={{
                        padding: '7px 10px', textAlign: ci === 0 ? 'left' : 'right',
                        color: ci === 0 ? '#0f172a' : '#1e293b',
                        fontWeight: ci === 0 ? 500 : 400,
                        fontFamily: ci === 0 ? 'inherit' : 'ui-monospace, "SF Mono", Menlo, monospace',
                      }}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {section.totalRow && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid #475569' }}>
                    {section.totalRow.map((cell, ci) => (
                      <td key={ci} style={{
                        padding: '10px', textAlign: ci === 0 ? 'left' : 'right',
                        fontWeight: 'bold', fontSize: '12px', color: '#0f172a',
                        fontFamily: ci === 0 ? 'inherit' : 'ui-monospace, "SF Mono", Menlo, monospace',
                      }}>{cell}</td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          )}
          {/* Plain key-value rows */}
          {section.kv && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px' }}>
              {section.kv.map(([k, v], i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ color: '#64748b', fontSize: '11px' }}>{k}</span>
                  <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '11px', fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace' }}>{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Footer */}
      <div style={{
        marginTop: '40px', paddingTop: '14px', borderTop: '1px solid #e2e8f0',
        display: 'flex', justifyContent: 'space-between',
        fontSize: '9px', color: '#94a3b8',
      }}>
        <span>El Campesino · Recipe Cost Calculator</span>
        <span>Generated {todayLabel()} · Internal use only</span>
      </div>
    </div>
  );
}

function PDFPreviewModal({ docSpec, onClose }) {
  const { t } = useT();
  const [status, setStatus] = useState('loading'); // loading, ready, generating, error
  const [errorMsg, setErrorMsg] = useState('');
  const [previewScale, setPreviewScale] = useState(1);
  const libsRef = useRef(null);
  const previewContainerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadPdfLibs()
      .then((libs) => {
        if (!cancelled) {
          libsRef.current = libs;
          setStatus('ready');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setErrorMsg(err.message || 'Failed to load PDF tools');
          setStatus('error');
        }
      });
    return () => { cancelled = true; };
  }, []);

  // Compute a responsive scale so the 816px-wide document fits the visible width.
  // Recomputes on resize.
  useEffect(() => {
    const DOC_WIDTH = 816;
    const compute = () => {
      const container = previewContainerRef.current;
      if (!container) return;
      const available = container.clientWidth - 32; // account for padding on both sides
      const scale = Math.min(1, Math.max(0.3, available / DOC_WIDTH));
      setPreviewScale(scale);
    };
    compute();
    window.addEventListener('resize', compute);
    // Recompute once after libs load (in case layout settles late)
    const timer = setTimeout(compute, 100);
    return () => { window.removeEventListener('resize', compute); clearTimeout(timer); };
  }, [status]);

  const generatePdf = async () => {
    if (!libsRef.current) return null;
    const { jsPDF, html2canvas } = libsRef.current;
    const node = document.getElementById('pdf-document-content');
    if (!node) return null;

    // The on-screen preview scales the 816px page down to fit small screens via a
    // CSS `transform: scale(...)` on the wrapper (ScaledDocumentWrapper). html2canvas
    // cannot correctly capture an element that sits under a scaled ancestor — it
    // produces a garbled / overlapping image (fine on desktop where scale == 1, broken
    // on mobile). So neutralize that transform for the duration of the capture and
    // restore it afterwards, guaranteeing a natural 1:1 render regardless of device.
    const scaledWrapper = node.parentElement;
    const prevTransform = scaledWrapper ? scaledWrapper.style.transform : '';
    const prevTransformOrigin = scaledWrapper ? scaledWrapper.style.transformOrigin : '';
    if (scaledWrapper) {
      scaledWrapper.style.transform = 'none';
      scaledWrapper.style.transformOrigin = 'top left';
    }

    try {
    // US Letter: 8.5x11 in = 612x792 pt
    const pdf = new jsPDF({ unit: 'pt', format: 'letter', orientation: 'portrait' });
    const pageWidth = pdf.internal.pageSize.getWidth();   // 612
    const pageHeight = pdf.internal.pageSize.getHeight();  // 792
    const margin = 42;                                     // clean page gutter
    const contentWidth = pageWidth - margin * 2;
    const bottomLimit = pageHeight - margin;
    const usableHeight = pageHeight - margin * 2;
    const blockGap = 12;

    // Paginate block-by-block (header, title, KPIs, each section, footer) so a
    // section — or a table's header row — is never sliced across a page break,
    // and every page keeps a clean top/bottom margin. Only a single block that
    // is itself taller than a page gets sliced (large tables), with the gutters
    // masked so nothing bleeds into the margins.
    const blocks = Array.from(node.children).filter((el) => el.offsetHeight > 1);
    let cursorY = margin;

    for (const block of blocks) {
      // eslint-disable-next-line no-await-in-loop
      const canvas = await html2canvas(block, { scale: 2, backgroundColor: '#ffffff', logging: false });
      const imgData = canvas.toDataURL('image/png');
      const imgH = (canvas.height * contentWidth) / canvas.width;

      if (imgH <= usableHeight) {
        // Whole block fits on one page — move to the next page if it won't fit here.
        if (cursorY + imgH > bottomLimit && cursorY > margin + 0.5) {
          pdf.addPage();
          cursorY = margin;
        }
        pdf.addImage(imgData, 'PNG', margin, cursorY, contentWidth, imgH);
        cursorY += imgH + blockGap;
      } else {
        // Block taller than a page — start fresh, then slice it, masking the
        // gutters so slices never bleed into the page margins.
        if (cursorY > margin + 0.5) { pdf.addPage(); cursorY = margin; }
        let offset = 0;
        while (offset < imgH - 0.5) {
          pdf.addImage(imgData, 'PNG', margin, margin - offset, contentWidth, imgH);
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pageWidth, margin, 'F');                    // top gutter
          pdf.rect(0, bottomLimit, pageWidth, margin + 2, 'F');      // bottom gutter
          pdf.rect(0, 0, margin, pageHeight, 'F');                   // left gutter
          pdf.rect(pageWidth - margin, 0, margin, pageHeight, 'F');  // right gutter
          offset += usableHeight;
          if (offset < imgH - 0.5) { pdf.addPage(); }
        }
        cursorY = bottomLimit + 1; // force the next block onto a fresh page
      }
    }

    return pdf;
    } finally {
      // Always restore the preview's scale transform, even if capture threw.
      if (scaledWrapper) {
        scaledWrapper.style.transform = prevTransform;
        scaledWrapper.style.transformOrigin = prevTransformOrigin;
      }
    }
  };

  const handleDownload = async () => {
    setStatus('generating');
    try {
      const pdf = await generatePdf();
      if (pdf) {
        pdf.save(docSpec.filename || 'document.pdf');
      }
      setStatus('ready');
    } catch (err) {
      setErrorMsg(err.message || 'Failed to generate PDF');
      setStatus('error');
    }
  };

  // Portal to document.body so this full-screen preview floats above BeeNexa's
  // sticky header/nav (z-index up to 1000); the wrapper re-declares the scoped
  // id so the Tailwind utilities still apply to the portaled DOM.
  return createPortal(
    <div id="ec-recipe-cost-root">
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-[9998] flex flex-col" style={{ fontFamily: tokens.fontBody }}>
      {/* Toolbar */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-5 py-3 flex items-center justify-between gap-2 flex-shrink-0 shadow-sm">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center text-white flex-shrink-0">
            <FileText className="w-4.5 h-4.5" strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <div className="text-[13px] sm:text-[14px] font-bold text-slate-900 truncate">{t('pdf.preview')}</div>
            <div className="text-[11px] text-slate-500 truncate">{docSpec.filename || 'document.pdf'}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {status === 'error' && (
            <span className="hidden sm:inline text-[11px] text-rose-700 mr-2">{errorMsg}</span>
          )}
          <button
            onClick={handleDownload}
            disabled={status !== 'ready'}
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-[12px] sm:text-[13px] font-semibold transition shadow-sm"
            title={t('pdf.downloadTitle')}
          >
            {status === 'generating'
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Download className="w-3.5 h-3.5" strokeWidth={2.4} />
            }
            <span className="hidden sm:inline">{t('action.download')}</span>
          </button>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition ml-1"
            title={t('pdf.closeTitle')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Preview area — scrollable */}
      <div ref={previewContainerRef} className="flex-1 overflow-auto p-4 sm:p-8 flex items-start justify-center">
        {status === 'loading' && (
          <div className="text-center py-20">
            <Loader2 className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-3" />
            <div className="text-[13px] text-slate-500">{t('pdf.loading')}</div>
          </div>
        )}
        {status === 'error' && (
          <div className="bg-white rounded-2xl border border-rose-200 p-6 max-w-md text-center mt-10">
            <AlertTriangle className="w-8 h-8 text-rose-500 mx-auto mb-3" />
            <div className="text-[14px] font-bold text-slate-900 mb-1">{t('pdf.errorTitle')}</div>
            <div className="text-[12px] text-slate-600">{errorMsg}</div>
            <button
              onClick={onClose}
              className="mt-4 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-[13px] font-semibold text-slate-700 transition"
            >
              {t('action.close')}
            </button>
          </div>
        )}
        {(status === 'ready' || status === 'generating') && (
          // Wrapper has the SCALED dimensions so the parent flex layout sizes correctly.
          // We use a measured-height ref to handle documents that grow beyond 1 page.
          <ScaledDocumentWrapper scale={previewScale}>
            <PDFDocument docSpec={docSpec} />
          </ScaledDocumentWrapper>
        )}
      </div>

      {/* Mobile help text */}
      <div className="sm:hidden bg-slate-100 border-t border-slate-200 px-4 py-2 text-center text-[11px] text-slate-500 flex-shrink-0">
        {t('pdf.helpMobile')}
      </div>
    </div>
    </div>,
    document.body
  );
}

// Builders — turn app data into docSpec objects ready for PDFPreviewModal
// Each builder accepts `t` (translation function) so PDFs can be generated in EN or ES.
function buildRecipeDocSpec(recipe, calc, t = (k) => k) {
  const ingRows = [[t('pdf.col.ingredient'), t('pdf.col.qty'), t('pdf.col.unit'), t('pdf.col.totalCost')]];
  recipe.ingredients.forEach((ing) => {
    const resolved = ing.ingredientId ? resolveIngredientCost(ing) : ing;
    const total = Number(resolved.totalCost) || 0;
    ingRows.push([
      ing.ingredientId ? `${resolved._masterName || ing.name} (${t('recipe.masterList').split(' ')[0]})` : ing.name,
      String(ing.qty),
      ing.unit,
      `$${total.toFixed(2)}`,
    ]);
  });
  const ingTotal = recipe.ingredients.reduce((s, i) => {
    const r = i.ingredientId ? resolveIngredientCost(i) : i;
    return s + (Number(r.totalCost) || 0);
  }, 0);

  const sections = [
    {
      title: t('pdf.section.ingredients'),
      rows: ingRows,
      totalRow: [t('pdf.section.ingredientsTotal'), '', '', `$${ingTotal.toFixed(2)}`],
    },
    {
      title: t('pdf.section.costBreakdownPerUnit'),
      kv: [
        [t('pdf.field.ingredientCost'), `$${calc.ingredientCostPerUnit.toFixed(2)}`],
        [t('pdf.field.labor'), `$${calc.laborPerUnit.toFixed(2)}`],
        [t('pdf.field.utilities'), `$${calc.utilitiesPerUnit.toFixed(2)}`],
        [t('pdf.field.packaging'), `$${(calc.packagingPerUnit || 0).toFixed(2)}`],
        [t('pdf.field.operational'), `$${(calc.operationalPerUnit || 0).toFixed(2)}`],
        [t('pdf.field.realCostPerUnit'), `$${calc.realCostPerUnit.toFixed(2)}`],
      ],
    },
  ];

  // Pricing tiers
  if (calc.prices) {
    const tierRows = [[t('pdf.col.targetMargin'), t('pdf.col.sellingPrice'), t('pdf.col.profitPerUnit')]];
    [30, 35, 40, 45, 50].forEach((m) => {
      const price = calc.prices[m];
      const profit = price - calc.realCostPerUnit;
      tierRows.push([`${m}%`, `$${price.toFixed(2)}`, `$${profit.toFixed(2)}`]);
    });
    sections.push({ title: t('pdf.section.pricingTiers'), rows: tierRows });
  }

  // Current pricing performance (if priced)
  if (calc.currentPrice > 0) {
    sections.push({
      title: t('pdf.section.currentPerformance'),
      kv: [
        [t('pdf.field.currentSellingPrice'), `$${calc.currentPrice.toFixed(2)}`],
        [t('pdf.field.realCostPerUnit'), `$${calc.realCostPerUnit.toFixed(2)}`],
        [t('recipe.profitPerUnit'), `$${calc.profitPerUnit.toFixed(2)}`],
        [t('dashboard.actualMargin'), `${calc.actualMargin.toFixed(1)}%`],
        [t('recipe.healthStatus'), calc.healthStatus.toUpperCase()],
      ],
    });
  }

  const kpis = [
    { label: t('recipe.realCostPerUnit'), value: `$${calc.realCostPerUnit.toFixed(2)}`, sublabel: recipe.yield?.unit },
    { label: t('recipe.yield'), value: `${recipe.yield?.value || 0}`, sublabel: recipe.yield?.unit },
  ];
  if (calc.currentPrice > 0) {
    kpis.push({
      label: t('recipe.margin'),
      value: `${calc.actualMargin.toFixed(1)}%`,
      tone: calc.actualMargin >= 35 ? 'good' : calc.actualMargin >= 25 ? 'warn' : 'bad',
    });
    kpis.push({
      label: t('recipe.profitPerUnit'),
      value: `$${calc.profitPerUnit.toFixed(2)}`,
      tone: calc.profitPerUnit > 0 ? 'good' : 'bad',
    });
  }

  return {
    title: recipe.name,
    titleMeta: `${recipe.category || t('analytics.recipe')} · ${recipe.yield?.value || 0} ${recipe.yield?.unit || ''}`,
    subtitle: t('pdf.docHeader.recipeBreakdown'),
    docType: t('pdf.docType.costReport'),
    filename: `${recipe.name.toLowerCase().replace(/\s+/g, '-')}-cost.pdf`,
    kpis,
    sections,
  };
}

function buildAnalyticsDocSpec(recipes, t = (k) => k) {
  const calcs = recipes.map((r) => ({ r, c: calculateRecipe(r) })).filter((x) => x.c.currentPrice > 0);
  if (calcs.length === 0) {
    return {
      title: t('pdf.analyticsReport'),
      subtitle: t('pdf.docHeader.businessOverview'),
      filename: 'analytics-report.pdf',
      sections: [{ title: t('analytics.noData'), kv: [['Status', t('pdf.noPriced')]] }],
    };
  }
  const avgMargin = calcs.reduce((s, x) => s + x.c.actualMargin, 0) / calcs.length;
  const losing = calcs.filter((x) => x.c.healthStatus === 'losing');
  const tight = calcs.filter((x) => x.c.healthStatus === 'tight');
  const healthy = calcs.filter((x) => x.c.healthStatus === 'healthy');
  const sorted = [...calcs].sort((a, b) => b.c.actualMargin - a.c.actualMargin);

  const rankRows = [['#', t('pdf.col.recipe'), t('pdf.col.margin'), t('pdf.col.profitPerUnit').replace(' / unit', '/Unit'), t('pdf.col.status')]];
  sorted.forEach(({ r, c }, idx) => {
    rankRows.push([
      String(idx + 1),
      r.name,
      `${c.actualMargin.toFixed(1)}%`,
      `$${c.profitPerUnit.toFixed(2)}`,
      c.healthStatus.toUpperCase(),
    ]);
  });

  const sections = [
    { title: t('pdf.section.marginRanking'), rows: rankRows },
  ];

  if (losing.length > 0) {
    const losingRows = [[t('pdf.col.recipe'), t('pdf.col.margin'), t('pdf.col.lossPerUnit')]];
    losing.forEach(({ r, c }) => {
      losingRows.push([r.name, `${c.actualMargin.toFixed(1)}%`, `$${c.profitPerUnit.toFixed(2)}`]);
    });
    sections.push({ title: t('pdf.section.losing'), rows: losingRows, note: t('pdf.section.losingNote') });
  }

  return {
    title: t('pdf.analyticsReport'),
    titleMeta: t('pdf.totalRecipes', { n: recipes.length, m: calcs.length }),
    subtitle: t('pdf.docHeader.businessOverview'),
    docType: t('pdf.docType.analytics'),
    filename: 'analytics-report.pdf',
    kpis: [
      { label: t('analytics.avgMargin'), value: `${avgMargin.toFixed(1)}%`,
        tone: avgMargin >= 35 ? 'good' : avgMargin >= 25 ? 'warn' : 'bad' },
      { label: t('health.healthy'), value: String(healthy.length), tone: 'good' },
      { label: t('health.tight'), value: String(tight.length), tone: tight.length > 0 ? 'warn' : 'good' },
      { label: t('analytics.losingMoney'), value: String(losing.length), tone: losing.length > 0 ? 'bad' : 'good' },
    ],
    sections,
  };
}

function buildProductDocSpec(product, calc, t = (k) => k) {
  // calc shape comes from calculateProduct — defensive accessors
  const fallback = (v, d = 0) => (v == null ? d : v);
  const realCost = fallback(calc?.realCost, fallback(calc?.totalCost, 0));
  const sellingPrice = fallback(product.price, fallback(calc?.sellingPrice, 0));
  const margin = sellingPrice > 0 ? ((sellingPrice - realCost) / sellingPrice) * 100 : 0;
  const profit = sellingPrice - realCost;

  const compRows = [[t('pdf.col.component'), t('pdf.col.cost')]];
  (calc?.components || []).forEach((c) => {
    compRows.push([c.label || c.name || 'Item', `$${(c.cost || 0).toFixed(2)}`]);
  });

  const sections = [];
  if (compRows.length > 1) {
    sections.push({ title: t('pdf.section.components'), rows: compRows, totalRow: [t('pdf.field.totalCost'), `$${realCost.toFixed(2)}`] });
  }
  sections.push({
    title: t('pdf.section.pricingPerformance'),
    kv: [
      [t('recipe.sellingPrice'), `$${sellingPrice.toFixed(2)}`],
      [t('recipe.realCost'), `$${realCost.toFixed(2)}`],
      [t('recipe.profit'), `$${profit.toFixed(2)}`],
      [t('recipe.margin'), `${margin.toFixed(1)}%`],
    ],
  });

  return {
    title: product.name,
    titleMeta: product.category || t('pdf.docType.menuItem'),
    subtitle: t('pdf.docHeader.menuItem'),
    docType: t('pdf.docType.menuItem'),
    filename: `${(product.name || 'menu-item').toLowerCase().replace(/\s+/g, '-')}-cost.pdf`,
    kpis: [
      { label: t('recipe.realCost'), value: `$${realCost.toFixed(2)}` },
      { label: t('recipe.sellingPrice'), value: `$${sellingPrice.toFixed(2)}` },
      { label: t('recipe.margin'), value: `${margin.toFixed(1)}%`,
        tone: margin >= 35 ? 'good' : margin >= 25 ? 'warn' : 'bad' },
      { label: t('recipe.profit'), value: `$${profit.toFixed(2)}`, tone: profit > 0 ? 'good' : 'bad' },
    ],
    sections,
  };
}

// ============================================================
// TAB 1 · DASHBOARD (read-only, KPI-focused)
// ============================================================
function DashboardTab({ recipes, onOpen }) {
  const { t } = useT();
  const calcs = recipes.map((r) => ({ r, c: calculateRecipe(r) }));
  const pricedCalcs = calcs.filter((x) => x.c.currentPrice > 0);
  const alerts = pricedCalcs.filter((x) => x.c.healthStatus === 'losing' || x.c.healthStatus === 'tight');

  return (
    <div>
      <ModuleHero module="dashboard" />

      {/* Recipe performance cards — individual, clickable */}
      <div className="mb-6">
        <SectionTitle icon={<BarChart3 className="w-4 h-4" />} title={t('dashboard.recipePerformance')} count={recipes.length} />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {calcs.map(({ r, c }) => (
            <DashboardRecipeCard key={r.id} recipe={r} calc={c} onOpen={() => onOpen(r.id)} />
          ))}
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="mb-6">
          <SectionTitle icon={<AlertTriangle className="w-4 h-4" />} title={t('analytics.needsAttention')} count={alerts.length} />
          <div className="space-y-2">
            {alerts.map(({ r, c }) => (
              <AlertRow key={r.id} recipe={r} calc={c} onOpen={() => onOpen(r.id)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DashboardRecipeCard({ recipe, calc, onOpen }) {
  const { t } = useT();
  const h = HEALTH_TONE[calc.healthStatus];
  const hasPrice = calc.currentPrice > 0;
  return (
    <button
      onClick={onOpen}
      className="group text-left bg-white rounded-2xl border border-slate-200 hover:border-slate-300 hover:shadow-lg hover:shadow-slate-900/5 transition-all overflow-hidden"
    >
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold tracking-wide text-blue-700 bg-blue-50 uppercase">
            {recipe.category}
          </span>
          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide ${h.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${h.dot}`}></span>
            {t(h.labelKey)}
          </span>
        </div>

        <h3 className="text-xl font-semibold text-slate-900 tracking-tight">{recipe.name}</h3>
        <div className="text-[12px] text-slate-500 mt-0.5 mb-4">
          {t('recipe.yield')} · {recipe.yield.value} {recipe.yield.unit}
        </div>

        {/* Real Cost + Selling Price */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100">
            <div className="text-[9px] font-semibold tracking-wider text-slate-500 uppercase">{t('recipe.realCost')}</div>
            <div className="text-[15px] font-bold text-slate-900 mt-0.5" style={{ fontFamily: tokens.fontMono }}>
              {money(calc.realCostPerUnit)}
            </div>
          </div>
          <div className={`p-2.5 rounded-lg border ${
            !hasPrice ? 'bg-slate-50 border-slate-100' :
            calc.healthStatus === 'losing' ? 'bg-rose-50 border-rose-200' :
            calc.healthStatus === 'tight' ? 'bg-amber-50 border-amber-200' :
            'bg-emerald-50 border-emerald-200'
          }`}>
            <div className={`text-[9px] font-semibold tracking-wider uppercase ${
              !hasPrice ? 'text-slate-500' :
              calc.healthStatus === 'losing' ? 'text-rose-600' :
              calc.healthStatus === 'tight' ? 'text-amber-700' :
              'text-emerald-700'
            }`}>{t('dashboard.sellingAt')}</div>
            <div className={`text-[15px] font-bold mt-0.5 ${
              !hasPrice ? 'text-slate-300' :
              calc.healthStatus === 'losing' ? 'text-rose-700' :
              calc.healthStatus === 'tight' ? 'text-amber-800' :
              'text-emerald-700'
            }`} style={{ fontFamily: tokens.fontMono }}>
              {hasPrice ? money(calc.currentPrice) : '—'}
            </div>
          </div>
        </div>

        {/* Margin + profit */}
        {hasPrice ? (
          <div className="flex items-center justify-between px-1">
            <span className="text-[11px] text-slate-500">{t('dashboard.actualMargin')}</span>
            <div className="flex items-center gap-1.5">
              <span className={`text-[13px] font-bold ${h.text}`} style={{ fontFamily: tokens.fontMono }}>
                {calc.actualMargin < 0 ? '−' : ''}{Math.abs(calc.actualMargin).toFixed(1)}%
              </span>
              {calc.profitPerUnit >= 0 ? (
                <span className="text-[10px] text-slate-400">{t('dashboard.profit')} {money(calc.profitPerUnit)}</span>
              ) : (
                <span className="text-[10px] text-rose-500 font-medium">{t('dashboard.loss')} {money(Math.abs(calc.profitPerUnit))}</span>
              )}
            </div>
          </div>
        ) : (
          <div className="text-[11px] text-slate-400 text-center py-1">{t('dashboard.setCurrentPrice')}</div>
        )}

        {/* Suggested Prices by Margin — colored pills */}
        <div className="pt-4 mt-3 border-t border-slate-100">
          <div className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase mb-2">
            {t('dashboard.suggestedPrices')}
          </div>
          <div className="grid grid-cols-5 gap-1.5">
            {[30, 35, 40, 45, 50].map((m) => {
              const tone = MARGIN_TONES[m];
              return (
                <div key={m} className={`rounded-lg px-1 py-2 ring-1 min-w-0 ${tone.bg} ${tone.ring} text-center`}>
                  <div className={`text-[10px] font-bold ${tone.text}`}>{m}%</div>
                  <div className={`text-[11px] mt-0.5 font-bold tracking-tight break-words ${tone.text}`} style={{ fontFamily: tokens.fontMono }}>
                    {money(calc.prices[m])}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </button>
  );
}

function SectionTitle({ icon, title, count }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="text-slate-400">{icon}</span>
      <h3 className="text-[15px] font-bold text-slate-900 tracking-tight">{title}</h3>
      {typeof count === 'number' && (
        <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-md text-[10px] font-bold">{count}</span>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, sublabel, tone = 'slate' }) {
  const toneMap = {
    slate:   { iconBg: 'bg-slate-100',   iconText: 'text-slate-600',   valueText: 'text-slate-900' },
    blue:    { iconBg: 'bg-blue-100',    iconText: 'text-blue-600',    valueText: 'text-slate-900' },
    emerald: { iconBg: 'bg-emerald-100', iconText: 'text-emerald-600', valueText: 'text-emerald-700' },
    amber:   { iconBg: 'bg-amber-100',   iconText: 'text-amber-600',   valueText: 'text-amber-700' },
    rose:    { iconBg: 'bg-rose-100',    iconText: 'text-rose-600',    valueText: 'text-rose-700' },
  };
  const t = toneMap[tone];
  return (
    <div className="p-4 sm:p-5 bg-white rounded-2xl border border-slate-200 shadow-sm shadow-slate-900/[0.02]">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-8 h-8 rounded-lg ${t.iconBg} ${t.iconText} flex items-center justify-center`}>
          {icon}
        </div>
      </div>
      <div className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase">{label}</div>
      <div className={`mt-1 text-2xl sm:text-3xl font-bold tracking-tight ${t.valueText}`} style={{ fontFamily: tokens.fontMono }}>
        {value}
      </div>
      <div className="mt-1 text-[11px] text-slate-500">{sublabel}</div>
    </div>
  );
}

function AlertRow({ recipe, calc, onOpen }) {
  const { t } = useT();
  const h = HEALTH_TONE[calc.healthStatus];
  return (
    <button
      onClick={onOpen}
      className={`w-full flex items-center justify-between gap-3 p-3.5 rounded-xl ring-1 ${h.bg} ${h.ring} hover:shadow-sm transition text-left`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
          calc.healthStatus === 'losing' ? 'bg-rose-500' : 'bg-amber-500'
        } text-white`}>
          <AlertTriangle className="w-4 h-4" strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <div className={`text-[14px] font-semibold truncate ${h.text}`}>{recipe.name}</div>
          <div className={`text-[12px] ${h.text} opacity-80`}>
            {calc.healthStatus === 'losing'
              ? t('dashboard.alertRaise', { m: pct(calc.actualMargin), p: money(calc.prices[35]) })
              : t('dashboard.alertConsider', { m: pct(calc.actualMargin), p: money(calc.prices[40]) })}
          </div>
        </div>
      </div>
      <ChevronRight className={`w-4 h-4 flex-shrink-0 ${h.text}`} />
    </button>
  );
}

function PerformanceRow({ recipe, calc, onOpen }) {
  const h = HEALTH_TONE[calc.healthStatus];
  return (
    <button
      onClick={onOpen}
      className="w-full grid grid-cols-12 gap-4 px-6 py-3.5 hover:bg-slate-50/60 transition border-b border-slate-50 last:border-b-0 text-left items-center"
    >
      <div className="col-span-4 min-w-0">
        <div className="text-[14px] font-semibold text-slate-900 truncate">{recipe.name}</div>
        <div className="text-[11px] text-slate-500">{recipe.category} · {recipe.yield.value} {recipe.yield.unit}</div>
      </div>
      <div className="col-span-2 text-right text-[14px] font-semibold text-slate-900" style={{ fontFamily: tokens.fontMono }}>
        {money(calc.realCostPerUnit)}
      </div>
      <div className="col-span-2 text-right text-[14px] text-slate-700" style={{ fontFamily: tokens.fontMono }}>
        {calc.currentPrice > 0 ? money(calc.currentPrice) : <span className="text-slate-300">—</span>}
      </div>
      <div className="col-span-2 text-right">
        {calc.currentPrice > 0 ? (
          <span className={`text-[14px] font-bold ${h.text}`} style={{ fontFamily: tokens.fontMono }}>
            {pct(calc.actualMargin)}
          </span>
        ) : <span className="text-slate-300">—</span>}
      </div>
      <div className="col-span-1 text-right text-[13px]" style={{ fontFamily: tokens.fontMono }}>
        {calc.currentPrice > 0 ? (
          <span className={calc.profitPerUnit >= 0 ? 'text-slate-700' : 'text-rose-600 font-semibold'}>
            {calc.profitPerUnit < 0 && '−'}{money(Math.abs(calc.profitPerUnit))}
          </span>
        ) : <span className="text-slate-300">—</span>}
      </div>
      <div className="col-span-1 flex justify-end">
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold ${h.bg} ${h.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${h.dot}`}></span>
        </span>
      </div>
    </button>
  );
}

function MobilePerformanceCard({ recipe, calc, onOpen }) {
  const { t } = useT();
  const h = HEALTH_TONE[calc.healthStatus];
  return (
    <button onClick={onOpen} className="w-full p-4 text-left hover:bg-slate-50/60 transition">
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-[14px] font-semibold text-slate-900">{recipe.name}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{recipe.category} · {recipe.yield.value} {recipe.yield.unit}</div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${h.bg} ${h.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${h.dot}`}></span>
          {t(h.labelKey)}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3">
        <div>
          <div className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">{t('recipe.realCost')}</div>
          <div className="text-[14px] font-bold text-slate-900 mt-0.5" style={{ fontFamily: tokens.fontMono }}>
            {money(calc.realCostPerUnit)}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">{t('recipe.currentPrice')}</div>
          <div className="text-[14px] text-slate-700 mt-0.5" style={{ fontFamily: tokens.fontMono }}>
            {calc.currentPrice > 0 ? money(calc.currentPrice) : '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">{t('recipe.margin')}</div>
          <div className={`text-[14px] font-bold mt-0.5 ${h.text}`} style={{ fontFamily: tokens.fontMono }}>
            {calc.currentPrice > 0 ? pct(calc.actualMargin) : '—'}
          </div>
        </div>
      </div>
    </button>
  );
}

// ============================================================
// TAB 2 · RECIPES (operations — list + detail)
// ============================================================
function RecipesTab({ recipes, onOpen, onDuplicate, onDelete, onNew }) {
  const { t } = useT();
  const [search, setSearch] = useState('');
  const filtered = useMemo(
    () => recipes.filter((r) => r.name.toLowerCase().includes(search.toLowerCase())),
    [recipes, search]
  );

  return (
    <div>
      <ModuleHero
        module="recipes"
        rightSlot={
          <button
            onClick={onNew}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-[14px] font-medium rounded-xl hover:bg-blue-700 shadow-sm shadow-blue-500/20 transition"
          >
            <Plus className="w-4 h-4" strokeWidth={2.5} />
            <span className="hidden sm:inline">{t('recipe.new')}</span>
            <span className="sm:hidden">{t('action.new')}</span>
          </button>
        }
      />

      <div className="mb-5 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder={t('recipe.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-[14px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition"
        />
      </div>

      {/* Total counter */}
      <div className="mb-3 flex items-center gap-2 text-[12px] text-slate-500">
        <span className="font-semibold text-slate-700">{filtered.length}</span>
        <span>{filtered.length === 1 ? t('analytics.recipe') : t('analytics.recipeP')}</span>
        {search && (
          <>
            <span className="text-slate-300">·</span>
            <span>{t('recipe.filteredFrom', { n: recipes.length })}</span>
          </>
        )}
      </div>

      {/* Simplified recipe cards — operations focus */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((r) => (
          <SimpleRecipeCard
            key={r.id}
            recipe={r}
            onOpen={() => onOpen(r.id)}
            onDuplicate={() => onDuplicate(r.id)}
            onDelete={() => onDelete(r.id)}
          />
        ))}

        <button
          onClick={onNew}
          className="border-2 border-dashed border-slate-200 rounded-2xl p-6 min-h-[180px] flex flex-col items-center justify-center gap-3 text-slate-400 hover:border-blue-400 hover:bg-blue-50/30 hover:text-blue-600 transition-all"
        >
          <div className="w-11 h-11 rounded-full border-2 border-current flex items-center justify-center">
            <Plus className="w-5 h-5" strokeWidth={2} />
          </div>
          <span className="text-[13px] font-medium">{t('recipe.addNew')}</span>
        </button>
      </div>
    </div>
  );
}

function SimpleRecipeCard({ recipe, onOpen, onDuplicate, onDelete }) {
  const { t } = useT();
  const ingredientCount = recipe.ingredients.length;
  const calc = useMemo(() => calculateRecipe(recipe), [recipe]);
  const hasPrice = calc.currentPrice > 0;
  const h = hasPrice ? HEALTH_TONE[calc.healthStatus] : null;

  return (
    <div className="group bg-white rounded-2xl border border-slate-200 hover:border-slate-300 hover:shadow-lg hover:shadow-slate-900/5 transition-all overflow-hidden">
      <button onClick={onOpen} className="w-full p-4 text-left block">
        <div className="flex items-center justify-between mb-3">
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold tracking-wide text-blue-700 bg-blue-50 uppercase">
            {recipe.category}
          </span>
          {hasPrice ? (
            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide ${h.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${h.dot}`}></span>
              {t(h.labelKey)}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-300"></span>
              {t('health.none')}
            </span>
          )}
        </div>

        <h3 className="text-xl font-semibold text-slate-900 tracking-tight">{recipe.name}</h3>

        <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-500">
          <div className="flex items-center gap-1">
            <span className="text-slate-400">{t('recipe.yield')}</span>
            <span className="font-medium text-slate-700" style={{ fontFamily: tokens.fontMono }}>
              {recipe.yield.value} {recipe.yield.unit}
            </span>
          </div>
          <div className="w-1 h-1 rounded-full bg-slate-300"></div>
          <div className="flex items-center gap-1">
            <span className="font-medium text-slate-700">{ingredientCount}</span>
            <span className="text-slate-400">{t('recipe.ingredients').toLowerCase()}</span>
          </div>
        </div>

        {/* Cost / Margin cards — same style as Menu tab */}
        <div className="grid grid-cols-2 gap-2 mt-4">
          <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100">
            <div className="text-[9px] font-semibold tracking-wider text-slate-500 uppercase">{t('recipe.realCost')}</div>
            <div className="text-[15px] font-bold text-slate-900 mt-0.5" style={{ fontFamily: tokens.fontMono }}>
              {money(calc.realCostPerUnit)}
            </div>
          </div>
          {hasPrice ? (
            <div className={`p-2.5 rounded-lg border ${
              calc.healthStatus === 'losing' ? 'bg-rose-50 border-rose-200' :
              calc.healthStatus === 'tight' ? 'bg-amber-50 border-amber-200' :
              'bg-emerald-50 border-emerald-200'
            }`}>
              <div className={`text-[9px] font-semibold tracking-wider uppercase ${h.text}`}>{t('recipe.margin')}</div>
              <div className={`text-[15px] font-bold mt-0.5 ${h.text}`} style={{ fontFamily: tokens.fontMono }}>
                {pct(calc.actualMargin)}
              </div>
            </div>
          ) : (
            <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 border-dashed">
              <div className="text-[9px] font-semibold tracking-wider text-slate-500 uppercase">{t('recipe.margin')}</div>
              <div className="text-[15px] font-bold text-slate-400 mt-0.5" style={{ fontFamily: tokens.fontMono }}>
                —
              </div>
            </div>
          )}
        </div>
      </button>

      <div className="px-4 py-3 bg-slate-50/60 border-t border-slate-100 flex items-center justify-between">
        <button onClick={onOpen} className="text-[12px] font-medium text-blue-600 hover:text-blue-700 flex items-center gap-0.5">
          Edit <ChevronRight className="w-3.5 h-3.5" />
        </button>
        <div className="flex items-center gap-0.5">
          <IconButton onClick={onDuplicate} title="Duplicate"><Copy className="w-3.5 h-3.5" /></IconButton>
          <IconButton onClick={onDelete} title="Delete" danger><Trash2 className="w-3.5 h-3.5" /></IconButton>
        </div>
      </div>
    </div>
  );
}

function IconButton({ children, onClick, title, danger }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded-md text-slate-400 hover:bg-white transition ${danger ? 'hover:text-rose-600' : 'hover:text-slate-700'}`}
    >
      {children}
    </button>
  );
}

// ============================================================
// RECIPE DETAIL (inside Recipes tab)
// ============================================================
function RecipeDetail({ recipe, onBack, onUpdate, onDuplicate, onDelete }) {
  const { t } = useT();
  const calc = useMemo(() => calculateRecipe(recipe), [recipe]);
  const [selectedMargin, setSelectedMargin] = useState(45);
  const [showPdf, setShowPdf] = useState(false);

  const patch = (changes) => onUpdate({ ...recipe, ...changes });
  const patchIngredient = (id, changes) => {
    onUpdate({ ...recipe, ingredients: recipe.ingredients.map((i) => (i.id === id ? { ...i, ...changes } : i)) });
  };
  const addIngredient = () => {
    onUpdate({
      ...recipe,
      ingredients: [...recipe.ingredients, { id: `i${Date.now()}`, name: '', qty: 0, unit: 'Libras', totalCost: 0, unitCost: 0 }],
    });
  };
  const removeIngredient = (id) => onUpdate({ ...recipe, ingredients: recipe.ingredients.filter((i) => i.id !== id) });

  return (
    <div>
      {/* Breadcrumb + actions */}
      <div className="mb-5 flex items-center justify-between gap-2">
        <button onClick={onBack} className="flex items-center gap-1.5 text-[13px] font-medium text-slate-600 hover:text-slate-900 px-2 py-1 -ml-2 rounded-md hover:bg-slate-100 transition">
          <ArrowLeft className="w-4 h-4" />
          {t('recipe.allRecipes')}
        </button>
        <div className="flex items-center gap-2">
          <AutoSaveBadge />
          <button
            onClick={() => setShowPdf(true)}
            className="flex px-3 py-1.5 text-[12px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 items-center gap-1.5 transition"
            title={t('pdf.printGenerate')}
          >
            <Download className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t('action.download')}</span>
          </button>
          <button onClick={onDuplicate} className="hidden sm:flex px-3 py-1.5 text-[12px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 items-center gap-1.5 transition">
            <Copy className="w-3.5 h-3.5" /> {t('action.duplicate')}
          </button>
          <button onClick={onDelete} className="px-3 py-1.5 text-[12px] font-medium text-rose-600 bg-white border border-slate-200 rounded-lg hover:bg-rose-50 hover:border-rose-200 flex items-center gap-1.5 transition">
            <Trash2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t('action.delete')}</span>
          </button>
        </div>
      </div>

      {showPdf && (
        <PDFPreviewModal
          docSpec={buildRecipeDocSpec(recipe, calc, t)}
          onClose={() => setShowPdf(false)}
        />
      )}

      {/* Summary header */}
      <Card className="mb-5 overflow-hidden">
        <div className="p-4 sm:p-5 bg-gradient-to-br from-slate-50 to-white">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
            <div className="lg:col-span-3">
              <div className="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-semibold tracking-wider text-blue-700 bg-blue-50 uppercase mb-3">
                {recipe.category}
              </div>
              <FieldLabel>{t('recipe.name')}</FieldLabel>
              <Input value={recipe.name} onChange={(v) => patch({ name: v })} size="xl" className="font-bold text-slate-900 tracking-tight" />
              <FieldLabel className="mt-3">{t('ingredient.notes').replace(' (optional)', '').replace(' (opcional)', '')}</FieldLabel>
              <Textarea value={recipe.notes} onChange={(v) => patch({ notes: v })} placeholder={t('ingredient.notesPlaceholder')} />
            </div>
            <div className="lg:col-span-2">
              <ProductionYieldEditor
                rawQty={recipe.yield.rawQty}
                rawUnit={recipe.yield.rawUnit}
                sellableQty={recipe.yield.value}
                sellableUnit={recipe.yield.unit}
                onRawQtyChange={(v) => patch({ yield: { ...recipe.yield, rawQty: v } })}
                onRawUnitChange={(u) => patch({ yield: { ...recipe.yield, rawUnit: u } })}
                onSellableQtyChange={(v) => patch({ yield: { ...recipe.yield, value: v } })}
                onSellableUnitChange={(u) => patch({ yield: { ...recipe.yield, unit: u } })}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* 01 · INGREDIENTS */}
      <Card className="mb-5">
        <SectionHeader
          number="01" title={t('recipe.ingredients')} subtitle={t('recipe.ingredients') === 'Ingredientes' ? 'Solo ingredientes crudos. Todos los campos editables.' : 'Raw ingredients only. All fields editable.'}
          action={
            <button onClick={addIngredient} className="px-3 py-1.5 text-[12px] font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg flex items-center gap-1.5 transition">
              <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> {t('recipe.addIngredient')}
            </button>
          }
        />

        <div className="px-4 sm:px-5 pb-4 sm:pb-5">
          <div className="hidden md:block overflow-x-auto">
            <div className="min-w-full">
              <div className="grid grid-cols-12 gap-2 px-3 pb-2 text-[10px] font-semibold tracking-wider text-slate-400 uppercase">
                <div className="col-span-4">{t('pdf.col.ingredient')}</div>
                <div className="col-span-2 text-right">{t('recipe.qty')}</div>
                <div className="col-span-2">{t('recipe.unit')}</div>
                <div className="col-span-2 text-right">{t('recipe.totalCost')}</div>
                <div className="col-span-2 text-right">{t('recipe.unitCost')}</div>
              </div>
              {recipe.ingredients.map((ing) => {
                const isMigrated = !!ing.ingredientId;
                const resolved = isMigrated ? resolveIngredientCost(ing) : ing;
                const displayTotal = Number(resolved.totalCost) || 0;
                const computedUnitCost = (Number(ing.qty) || 0) > 0
                  ? displayTotal / Number(ing.qty)
                  : 0;
                return (
                  <div key={ing.id} className="group/row grid grid-cols-12 gap-2 items-center py-1.5">
                    <div className="col-span-4">
                      {isMigrated ? (
                        <div className="flex items-center gap-1.5 px-2 py-1.5 bg-teal-50 border border-teal-200 rounded-lg">
                          <Leaf className="w-3.5 h-3.5 text-teal-600 flex-shrink-0" />
                          <span className="text-[12px] font-semibold text-slate-800 truncate flex-1">{resolved._masterName || ing.name}</span>
                          <span className="text-[9px] font-bold text-teal-700 bg-teal-100 px-1 py-0.5 rounded uppercase tracking-wide flex-shrink-0">{t('recipe.masterList').split(' ')[0]}</span>
                        </div>
                      ) : (
                        <Input value={ing.name} onChange={(v) => patchIngredient(ing.id, { name: v })} placeholder={t('recipe.ingredientNamePlaceholder')} size="sm" />
                      )}
                    </div>
                    <div className="col-span-2">
                      <NumberInput value={ing.qty} onChange={(v) => patchIngredient(ing.id, { qty: v })} size="sm" align="right" />
                    </div>
                    <div className="col-span-2">
                      <UnitSelect value={ing.unit} onChange={(v) => patchIngredient(ing.id, { unit: v })} />
                    </div>
                    <div className="col-span-2">
                      {isMigrated ? (
                        <div className="px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-right" title="Cost is derived from the Master List entry">
                          <span className="text-[12px] font-mono font-bold text-slate-700">${displayTotal.toFixed(2)}</span>
                        </div>
                      ) : (
                        <CurrencyInput value={ing.totalCost} onChange={(v) => patchIngredient(ing.id, { totalCost: v })} size="sm" emphasis />
                      )}
                    </div>
                    <div className="col-span-2 flex items-center gap-1.5">
                      <ComputedUnitCost value={computedUnitCost} unit={ing.unit} />
                      <button onClick={() => removeIngredient(ing.id)} title={t('action.delete')} className="flex-shrink-0 p-1 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="md:hidden space-y-3">
            {recipe.ingredients.map((ing) => {
              const isMigrated = !!ing.ingredientId;
              const resolved = isMigrated ? resolveIngredientCost(ing) : ing;
              const displayTotal = Number(resolved.totalCost) || 0;
              const computedUnitCost = (Number(ing.qty) || 0) > 0
                ? displayTotal / Number(ing.qty)
                : 0;
              return (
                <div key={ing.id} className={`p-3 rounded-xl border space-y-2 ${isMigrated ? 'bg-teal-50/40 border-teal-200' : 'bg-slate-50/60 border-slate-100'}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <FieldLabel className="mb-0">{t('recipe.name')}</FieldLabel>
                        {isMigrated && (
                          <span className="inline-flex items-center gap-1 text-[9px] font-bold text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded uppercase tracking-wide">
                            <Leaf className="w-2.5 h-2.5" /> {t('recipe.masterList')}
                          </span>
                        )}
                      </div>
                      {isMigrated ? (
                        <div className="px-3 py-2 bg-white border border-teal-200 rounded-xl text-[13px] font-semibold text-slate-800">
                          {resolved._masterName || ing.name}
                        </div>
                      ) : (
                        <Input value={ing.name} onChange={(v) => patchIngredient(ing.id, { name: v })} placeholder={t('recipe.ingredientNamePlaceholder')} />
                      )}
                    </div>
                    <button onClick={() => removeIngredient(ing.id)} className="mt-6 p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><FieldLabel>{t('recipe.quantity')}</FieldLabel><NumberInput value={ing.qty} onChange={(v) => patchIngredient(ing.id, { qty: v })} /></div>
                    <div><FieldLabel>{t('recipe.unit')}</FieldLabel><UnitSelect value={ing.unit} onChange={(v) => patchIngredient(ing.id, { unit: v })} /></div>
                  </div>
                  <div>
                    <FieldLabel>{t('recipe.totalCost')} {isMigrated && <span className="text-[10px] text-teal-700 font-normal normal-case tracking-normal">· {t('recipe.fromMasterList')}</span>}</FieldLabel>
                    {isMigrated ? (
                      <div className="px-3 py-2 bg-white border border-teal-200 rounded-xl text-[14px] font-mono font-bold text-slate-800">
                        ${displayTotal.toFixed(2)}
                      </div>
                    ) : (
                      <CurrencyInput value={ing.totalCost} onChange={(v) => patchIngredient(ing.id, { totalCost: v })} emphasis />
                    )}
                  </div>
                  <div className="pt-2 mt-1 border-t border-slate-200/70 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase">{t('recipe.unitCost')}</span>
                      <span className="text-[10px] text-slate-400" title={t('ingredient.autoCalculated')}>ⓘ</span>
                    </div>
                    <div className="text-[13px] font-semibold text-slate-500" style={{ fontFamily: tokens.fontMono }}>
                      {money(computedUnitCost)} {ing.unit ? <span className="text-[11px] text-slate-400 font-normal">/ {ing.unit}</span> : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {recipe.ingredients.length === 0 && (
            <div className="py-10 text-center text-[13px] text-slate-400">
              No ingredients yet. Click "Add Ingredient" to begin.
            </div>
          )}

          <div className="mt-4 p-4 rounded-xl bg-gradient-to-br from-amber-50 to-amber-50/40 border border-amber-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-semibold tracking-wider text-amber-900 uppercase">
                Batch Total · {recipe.ingredients.length} items
              </span>
              <span className="text-[15px] font-semibold text-slate-900" style={{ fontFamily: tokens.fontMono }}>
                {money(calc.ingredientsTotal)}
              </span>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-amber-200/60">
              <span className="text-[11px] text-amber-800">
                Per {String(recipe.yield.unit).split(' ')[0]} ({recipe.yield.value} {recipe.yield.unit})
              </span>
              <span className="text-xl font-bold text-amber-900 tracking-tight" style={{ fontFamily: tokens.fontMono }}>
                {money(calc.ingredientCostPerUnit)}
              </span>
            </div>
            {Number(recipe.yield.value) === 1 && (recipe.yield.unit === 'Unidades' || recipe.yield.unit === 'unit') && calc.ingredientsTotal > 0 && (
              <div className="mt-3 pt-3 border-t border-amber-200/60 flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                <span className="text-[11px] text-amber-800 leading-relaxed">
                  Set <span className="font-semibold">Production Yield</span> at the top to get accurate per-unit cost. Right now it's dividing by 1 default unit.
                </span>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* 02 · OPERATIONAL */}
      <Card className="mb-5">
        <SectionHeader number="02" title={t('recipe.operationalCost')} subtitle={t('recipe.operationalCostSub')} />

        <div className="px-4 sm:px-5 pb-4 sm:pb-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl border border-slate-200 bg-white">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                <Users className="w-4 h-4" strokeWidth={2} />
              </div>
              <div>
                <div className="text-[14px] font-semibold text-slate-900">Labor</div>
                <div className="text-[11px] text-slate-500">Prep + cooking time × rate</div>
              </div>
            </div>
            <div className="space-y-2.5">
              <div className="grid grid-cols-2 gap-2.5">
                <div><FieldLabel>Prep Hours</FieldLabel><NumberInput value={recipe.prepHours} onChange={(v) => patch({ prepHours: v })} suffix="hrs" /></div>
                <div><FieldLabel>Cook Hours</FieldLabel><NumberInput value={recipe.cookHours} onChange={(v) => patch({ cookHours: v })} suffix="hrs" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <div className="flex items-center gap-1 mb-1.5">
                    <span className="text-[11px] font-semibold tracking-wide text-slate-500">Hourly Rate</span>
                    <span
                      className="text-[10px] text-slate-400 cursor-help"
                      title="Use loaded rate including taxes/benefits (~15-25% above wage). For contractors, use hourly fee."
                    >ⓘ</span>
                  </div>
                  <CurrencyInput value={recipe.laborRate} onChange={(v) => patch({ laborRate: v })} />
                </div>
                <div>
                  <div className="flex items-center gap-1 mb-1.5">
                    <span className="text-[11px] font-semibold tracking-wide text-slate-500">Workers</span>
                    <span
                      className="text-[10px] text-slate-400 cursor-help"
                      title="Number of employees working simultaneously on this recipe. E.g. tamales often need 2 people."
                    >ⓘ</span>
                  </div>
                  <NumberInput
                    value={recipe.workers ?? 1}
                    onChange={(v) => patch({ workers: Math.max(1, v) })}
                    suffix={(recipe.workers ?? 1) === 1 ? 'person' : 'people'}
                  />
                </div>
              </div>

              <div className="pt-1">
                <FieldLabel>Cooking Mode</FieldLabel>
                <CookingModeSelector
                  mode={recipe.includeCookInLabor !== false ? 'active' : 'passive'}
                  onChange={(m) => patch({ includeCookInLabor: m === 'active' })}
                />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-1.5">
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-500">
                  {(() => {
                    const w = recipe.workers ?? 1;
                    const baseFormula = recipe.includeCookInLabor !== false
                      ? `(Prep ${recipe.prepHours || 0} + Cook ${recipe.cookHours || 0})`
                      : `Prep ${recipe.prepHours || 0} hrs`;
                    return w > 1
                      ? `${baseFormula} × ${w} workers × ${money(recipe.laborRate)}`
                      : `${baseFormula} × ${money(recipe.laborRate)}`;
                  })()}
                </span>
                <span className="font-semibold text-slate-900" style={{ fontFamily: tokens.fontMono }}>{money(calc.laborTotal)}</span>
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-500">÷ {recipe.yield.value} {recipe.yield.unit} ready to sell</span>
                <span className="text-slate-400" style={{ fontFamily: tokens.fontMono }}>=</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold tracking-wider text-blue-700 uppercase">Labor Per Unit</span>
                <span className="text-lg font-bold text-blue-700" style={{ fontFamily: tokens.fontMono }}>{money(calc.laborPerUnit)}</span>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-xl border border-slate-200 bg-white">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center">
                <Flame className="w-4 h-4" strokeWidth={2} />
              </div>
              <div>
                <div className="text-[14px] font-semibold text-slate-900">Gas & Electricity</div>
                <div className="text-[11px] text-slate-500">Industry-standard overhead percentage</div>
              </div>
            </div>

            <OverheadBlock
              overheadPct={recipe.overheadPct}
              ingredientCostPerUnit={calc.ingredientCostPerUnit}
              utilitiesPerUnit={calc.utilitiesPerUnit}
              utilitiesTotal={calc.utilitiesTotal}
              ingredientsTotal={calc.ingredientsTotal}
              cookHours={Number(recipe.cookHours) || 0}
              onChange={(v) => patch({ overheadPct: v })}
            />
          </div>
        </div>

        {/* Packaging subsection — inside Operational Cost card */}
        <div className="mx-4 sm:mx-5 mb-4">
          <div className="p-4 rounded-xl border border-slate-200 bg-white">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center">
                <Package className="w-4 h-4" strokeWidth={2} />
              </div>
              <div>
                <div className="text-[14px] font-semibold text-slate-900">Packaging Cost</div>
                <div className="text-[11px] text-slate-500">Per-unit cost of all packaging components</div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
              <div>
                <FieldLabel>Container</FieldLabel>
                <CurrencyInput
                  value={(recipe.packaging || {}).container}
                  onChange={(v) => patch({ packaging: { ...(recipe.packaging || {}), container: v } })}
                />
              </div>
              <div>
                <FieldLabel>Bag</FieldLabel>
                <CurrencyInput
                  value={(recipe.packaging || {}).bag}
                  onChange={(v) => patch({ packaging: { ...(recipe.packaging || {}), bag: v } })}
                />
              </div>
              <div>
                <FieldLabel>Wrap / Foil</FieldLabel>
                <CurrencyInput
                  value={(recipe.packaging || {}).wrap}
                  onChange={(v) => patch({ packaging: { ...(recipe.packaging || {}), wrap: v } })}
                />
              </div>
              <div>
                <FieldLabel>Other</FieldLabel>
                <CurrencyInput
                  value={(recipe.packaging || {}).other}
                  onChange={(v) => patch({ packaging: { ...(recipe.packaging || {}), other: v } })}
                />
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
              <span className="text-[11px] font-semibold tracking-wider text-violet-700 uppercase">Total Packaging / Unit</span>
              <span className="text-lg font-bold text-violet-700" style={{ fontFamily: tokens.fontMono }}>{money(calc.packagingPerUnit)}</span>
            </div>
          </div>
        </div>

        <div className="mx-4 sm:mx-5 mb-5 sm:mb-6 p-4 rounded-xl bg-gradient-to-br from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/20 flex items-center justify-between">
          <span className="text-[11px] font-semibold tracking-wider uppercase text-blue-100">Total Operational / Unit</span>
          <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: tokens.fontMono }}>{money(calc.operationalPerUnit)}</span>
        </div>
      </Card>

      {/* 03 · REAL COST FINAL */}
      <Card className="mb-5 overflow-hidden">
        <SectionHeader number="03" title={t('recipe.realCostFinal')} subtitle={t('recipe.realCostFinalSub')} />
        <div className="px-4 sm:px-5 pb-4 sm:pb-5">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 items-center">
            <div className="lg:col-span-3 space-y-1.5">
              <BreakdownRow label="Ingredients" value={money(calc.ingredientCostPerUnit)} />
              <BreakdownRow label="Labor" value={money(calc.laborPerUnit)} />
              <BreakdownRow label="Gas + Electricity" value={money(calc.utilitiesPerUnit)} />
              <BreakdownRow label="Packaging" value={money(calc.packagingPerUnit)} />
            </div>
            <div className="lg:col-span-2 rounded-xl bg-gradient-to-br from-slate-900 to-slate-800 p-5 text-white shadow-lg shadow-slate-900/20">
              <div className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase">
                True cost per {String(recipe.yield.unit).split(' ')[0]}
              </div>
              <div className="mt-1 text-4xl sm:text-5xl font-bold tracking-tight" style={{ fontFamily: tokens.fontMono }}>
                {money(calc.realCostPerUnit)}
              </div>
              <div className="mt-2 text-[11px] text-slate-400">
                Based on {recipe.yield.value} {recipe.yield.unit} yield
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* 04 · MARGIN PRICING */}
      <Card className="mb-5">
        <SectionHeader number="04" title={t('recipe.sellingByMargin')} subtitle={t('recipe.sellingByMarginSub')} />
        <div className="px-4 sm:px-5 pb-4 sm:pb-5">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
            {[30, 35, 40, 45, 50].map((m) => {
              const t = MARGIN_TONES[m];
              const isActive = selectedMargin === m;
              return (
                <button
                  key={m} onClick={() => setSelectedMargin(m)}
                  className={`relative p-3 rounded-xl ring-1 transition-all text-left ${
                    isActive ? `${t.activeBg} ${t.activeRing} ring-2 shadow-md` : `${t.bg} ${t.ring} hover:shadow-sm hover:-translate-y-0.5`
                  }`}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`}></span>
                    <span className={`text-[11px] font-bold ${t.text}`}>{m}% margin</span>
                  </div>
                  <div className={`text-xl font-bold tracking-tight ${t.text}`} style={{ fontFamily: tokens.fontMono }}>
                    {money(calc.prices[m])}
                  </div>
                  {isActive && <div className="absolute top-2 right-2"><CheckCircle2 className={`w-4 h-4 ${t.text}`} strokeWidth={2.5} /></div>}
                </button>
              );
            })}
          </div>
          <div className="mt-5 p-3.5 rounded-xl bg-slate-50 border border-slate-100 flex items-start gap-2.5">
            <Sparkles className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" strokeWidth={2} />
            <div className="text-[12px] text-slate-600 leading-relaxed">
              Prices auto-recalculate as you edit. <span className="text-slate-900 font-medium">No save button needed.</span>
            </div>
          </div>
        </div>
      </Card>

      {/* 05 · MARKET PRICING */}
      <Card className="mb-5">
        <SectionHeader number="05" title={t('recipe.marketPricing')} subtitle={t('recipe.marketPricingSub')} />
        <div className="px-4 sm:px-5 pb-4 sm:pb-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><FieldLabel>Your Current Price</FieldLabel><CurrencyInput value={recipe.currentPrice} onChange={(v) => patch({ currentPrice: v })} emphasis /></div>
            <div><FieldLabel>Competitor Low</FieldLabel><CurrencyInput value={recipe.competitorPriceLow} onChange={(v) => patch({ competitorPriceLow: v })} /></div>
            <div><FieldLabel>Competitor High</FieldLabel><CurrencyInput value={recipe.competitorPriceHigh} onChange={(v) => patch({ competitorPriceHigh: v })} /></div>
          </div>

          {calc.currentPrice > 0 ? (
            <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
              <AnalyticsCard
                label="Actual Margin"
                value={`${calc.actualMargin < 0 ? '−' : ''}${Math.abs(calc.actualMargin).toFixed(1)}%`}
                tone={calc.healthStatus}
              />
              <AnalyticsCard
                label={calc.profitPerUnit >= 0 ? 'Profit / unit' : 'Loss / unit'}
                value={`${calc.profitPerUnit < 0 ? '−' : ''}${money(Math.abs(calc.profitPerUnit))}`}
                valueClass={calc.profitPerUnit >= 0 ? 'text-slate-900' : 'text-rose-600'}
              />
              <AnalyticsCard
                label="Markup vs Cost"
                value={`${calc.vsRealCost >= 0 ? '+' : ''}${calc.vsRealCost.toFixed(1)}%`}
              />
              <AnalyticsCard
                label="Market Position"
                textValue={
                  calc.marketPosition === 'below' ? '↓ Below market' :
                  calc.marketPosition === 'above' ? '↑ Premium pricing' :
                  calc.marketPosition === 'within' ? '✓ In market range' :
                  'Add competitor prices'
                }
                valueClass={
                  calc.marketPosition === 'below' ? 'text-rose-600' :
                  calc.marketPosition === 'above' ? 'text-violet-600' :
                  calc.marketPosition === 'within' ? 'text-emerald-600' :
                  'text-slate-400'
                }
              />
            </div>
          ) : (
            <div className="mt-5 p-4 rounded-xl bg-slate-50 border border-dashed border-slate-200 text-center">
              <div className="text-[13px] text-slate-500">
                Enter your current selling price above to see profitability analytics
              </div>
            </div>
          )}

          {calc.currentPrice > 0 && <HealthAlertStrip calc={calc} />}
        </div>
      </Card>
    </div>
  );
}

function AnalyticsCard({ label, value, textValue, tone, valueClass = 'text-slate-900' }) {
  const toneMap = {
    losing:  { bg: 'bg-rose-50',    border: 'border-rose-200',    labelColor: 'text-rose-700',    valueColor: 'text-rose-700' },
    tight:   { bg: 'bg-amber-50',   border: 'border-amber-200',   labelColor: 'text-amber-800',   valueColor: 'text-amber-800' },
    healthy: { bg: 'bg-emerald-50', border: 'border-emerald-200', labelColor: 'text-emerald-700', valueColor: 'text-emerald-700' },
  };
  const t = tone && toneMap[tone];
  return (
    <div className={`p-4 rounded-xl border min-w-0 ${t ? `${t.bg} ${t.border}` : 'border-slate-200 bg-white'}`}>
      <div className={`text-[10px] font-semibold tracking-wider uppercase ${t ? t.labelColor : 'text-slate-500'}`}>
        {label}
      </div>
      {value ? (
        <div className={`text-xl sm:text-2xl font-bold tracking-tight mt-1 break-words ${t ? t.valueColor : valueClass}`} style={{ fontFamily: tokens.fontMono }}>
          {value}
        </div>
      ) : (
        <div className={`text-sm font-semibold mt-2 ${valueClass}`}>{textValue}</div>
      )}
    </div>
  );
}

function HealthAlertStrip({ calc }) {
  const h = HEALTH_TONE[calc.healthStatus];
  return (
    <div className={`mt-4 p-3.5 rounded-xl border flex items-start gap-2.5 ${h.bg} ${h.ring.replace('ring-', 'border-')}`}>
      {calc.healthStatus === 'losing' && (
        <>
          <div className="w-5 h-5 rounded-full bg-rose-500 text-white flex items-center justify-center flex-shrink-0 text-[11px] font-bold">!</div>
          <div>
            <div className="text-[13px] font-semibold text-rose-800">
              {calc.profitPerUnit < 0 ? 'Losing money on every unit sold' : 'Margin too low — below 25%'}
            </div>
            <div className="text-[12px] text-rose-700 mt-0.5">
              Raise price to at least {money(calc.prices[35])} (35%) to stay healthy.
            </div>
          </div>
        </>
      )}
      {calc.healthStatus === 'tight' && (
        <>
          <div className="w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0 text-[11px] font-bold">!</div>
          <div>
            <div className="text-[13px] font-semibold text-amber-900">Tight margin — at the limit</div>
            <div className="text-[12px] text-amber-800 mt-0.5">
              Safe zone starts at 35%. Consider raising to {money(calc.prices[40])} (40%).
            </div>
          </div>
        </>
      )}
      {calc.healthStatus === 'healthy' && (
        <>
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" strokeWidth={2.5} />
          <div>
            <div className="text-[13px] font-semibold text-emerald-800">Healthy pricing</div>
            <div className="text-[12px] text-emerald-700 mt-0.5">
              You're earning {money(calc.profitPerUnit)} profit per unit at a {pct(calc.actualMargin)} margin.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================
// TAB · PRODUCTS (Layer 2)
// ============================================================
function ProductsTab({ products, recipes, serviceTemplates, purchasedItems, onOpen }) {
  const { t } = useT();
  const [search, setSearch] = useState('');
  const filtered = useMemo(
    () => products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase())),
    [products, search]
  );

  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach((p) => {
      const cat = p.category || 'Otros';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
    });
    return groups;
  }, [filtered]);

  return (
    <div>
      <ModuleHero module="products" />

      <div className="mb-5 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder={t('recipe.searchPlaceholder').replace('recipes', 'menu items').replace('recetas', 'platillos')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-[14px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition"
        />
      </div>

      {/* Total counter */}
      <div className="mb-3 flex items-center gap-2 text-[12px] text-slate-500">
        <span className="font-semibold text-slate-700">{filtered.length}</span>
        <span>{t('tab.menu').toLowerCase()}</span>
        {search && (
          <>
            <span className="text-slate-300">·</span>
            <span>{t('recipe.filteredFrom', { n: products.length })}</span>
          </>
        )}
      </div>

      {Object.entries(grouped).map(([category, prods]) => (
        <div key={category} className="mb-7">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-[13px] font-bold tracking-wide text-slate-500 uppercase">{category}</h3>
            <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-md text-[10px] font-bold">
              {prods.length} {t('misc.of')} {filtered.length}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {prods.map((p) => (
              <ProductCard
                key={p.id}
                product={p}
                recipes={recipes}
                serviceTemplates={serviceTemplates}
                purchasedItems={purchasedItems}
                onOpen={() => onOpen(p.id)}
              />
            ))}
          </div>
        </div>
      ))}

      {filtered.length === 0 && (
        <div className="py-16 text-center text-slate-400">
          <ShoppingBag className="w-10 h-10 mx-auto mb-3 text-slate-300" strokeWidth={1.5} />
          <div className="text-[14px]">{t('ingredient.noneFound')}</div>
        </div>
      )}
    </div>
  );
}

function ProductCard({ product, recipes, serviceTemplates, purchasedItems, onOpen }) {
  const { t } = useT();
  // For simple products, show first size by default
  const calc = useMemo(
    () => calculateProduct(product, recipes, serviceTemplates, purchasedItems, null),
    [product, recipes, serviceTemplates, purchasedItems]
  );
  if (!calc) return null;

  const h = HEALTH_TONE[calc.healthStatus];
  const isCombo = product.type === 'combo';
  const isTaco = product.type === 'taco';
  const sizesCount = product.sizes?.length || 0;

  const badge = isTaco
    ? { text: 'text-amber-700', bg: 'bg-amber-50', label: '🌮 Taco', icon: null }
    : isCombo
    ? { text: 'text-violet-700', bg: 'bg-violet-50', label: 'Combo', icon: <Layers className="w-3 h-3 mr-1" /> }
    : { text: 'text-blue-700', bg: 'bg-blue-50', label: 'By the Pound', icon: null };

  return (
    <button
      onClick={onOpen}
      className="group text-left bg-white rounded-2xl border border-slate-200 hover:border-slate-300 hover:shadow-lg hover:shadow-slate-900/5 transition-all overflow-hidden"
    >
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold tracking-wide uppercase ${badge.text} ${badge.bg}`}>
            {badge.icon}{badge.label}
          </span>
          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide ${h.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${h.dot}`}></span>
            {t(h.labelKey)}
          </span>
        </div>

        <h3 className="text-xl font-semibold text-slate-900 tracking-tight">{product.name}</h3>
        {isTaco && (
          <div className="text-[11px] text-slate-500 mt-0.5 mb-3">
            {product.carneGramsPerTaco}g carne · Maíz {product.sizes[0].tortillasOverride ?? product.tortillasPerTaco} tortillas {money(product.sizes[0].sellingPrice)} · Harina {product.sizes[1].tortillasOverride ?? product.tortillasPerTaco} tortilla {money(product.sizes[1].sellingPrice)}
          </div>
        )}
        {!isTaco && sizesCount > 1 && (
          <div className="text-[11px] text-slate-500 mt-0.5 mb-3">
            {sizesCount} sizes · starts at {money(Math.min(...product.sizes.map((s) => s.sellingPrice)))}
          </div>
        )}
        {!isTaco && sizesCount <= 1 && !isCombo && (
          <div className="text-[11px] text-slate-500 mt-0.5 mb-3">{product.sizes?.[0]?.label || ''}</div>
        )}
        {isCombo && (
          <div className="text-[11px] text-slate-500 mt-0.5 mb-3">{product.components.length} components</div>
        )}

        <div className="grid grid-cols-2 gap-2 mt-4">
          <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100">
            <div className="text-[9px] font-semibold tracking-wider text-slate-500 uppercase">Cost</div>
            <div className="text-[15px] font-bold text-slate-900 mt-0.5" style={{ fontFamily: tokens.fontMono }}>
              {money(calc.totalCost)}
            </div>
          </div>
          <div className={`p-2.5 rounded-lg border ${
            calc.healthStatus === 'losing' ? 'bg-rose-50 border-rose-200' :
            calc.healthStatus === 'tight' ? 'bg-amber-50 border-amber-200' :
            'bg-emerald-50 border-emerald-200'
          }`}>
            <div className={`text-[9px] font-semibold tracking-wider uppercase ${h.text}`}>Margin</div>
            <div className={`text-[15px] font-bold mt-0.5 ${h.text}`} style={{ fontFamily: tokens.fontMono }}>
              {pct(calc.margin)}
            </div>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between text-[11px]">
          <span className="text-slate-500">Selling at</span>
          <span className="font-semibold text-slate-900" style={{ fontFamily: tokens.fontMono }}>
            {money(calc.sellingPrice)}
          </span>
        </div>
      </div>
    </button>
  );
}

// ============================================================
// PRODUCT DETAIL
// ============================================================
function ProductDetail({ product, recipes, serviceTemplates, purchasedItems, onBack, onUpdate, onDelete }) {
  const { t } = useT();
  const [selectedSizeId, setSelectedSizeId] = useState(product.sizes?.[0]?.id || null);
  const [showPdf, setShowPdf] = useState(false);

  const calc = useMemo(
    () => calculateProduct(product, recipes, serviceTemplates, purchasedItems, selectedSizeId),
    [product, recipes, serviceTemplates, purchasedItems, selectedSizeId]
  );

  const patch = (changes) => onUpdate({ ...product, ...changes });
  const isCombo = product.type === 'combo';
  const isTaco = product.type === 'taco';

  if (!calc) {
    return (
      <div>
        <button onClick={onBack} className="text-blue-600">← Back</button>
        <div className="mt-4 p-6 bg-white rounded-xl border">Product cannot be calculated. Check recipe references.</div>
      </div>
    );
  }

  const template = serviceTemplates.find((t) => t.id === product.serviceTemplateId);

  return (
    <div>
      {/* Breadcrumb */}
      <div className="mb-5 flex items-center justify-between gap-2">
        <button onClick={onBack} className="flex items-center gap-1.5 text-[13px] font-medium text-slate-600 hover:text-slate-900 px-2 py-1 -ml-2 rounded-md hover:bg-slate-100 transition">
          <ArrowLeft className="w-4 h-4" />
          {t('tab.menu')}
        </button>
        <div className="flex items-center gap-2">
          <AutoSaveBadge />
          <button
            onClick={() => setShowPdf(true)}
            className="flex px-3 py-1.5 text-[12px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 items-center gap-1.5 transition"
            title={t('pdf.printGenerate')}
          >
            <Download className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t('action.download')}</span>
          </button>
          <button onClick={onDelete} className="px-3 py-1.5 text-[12px] font-medium text-rose-600 bg-white border border-slate-200 rounded-lg hover:bg-rose-50 hover:border-rose-200 flex items-center gap-1.5 transition">
            <Trash2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t('action.delete')}</span>
          </button>
        </div>
      </div>

      {showPdf && (
        <PDFPreviewModal
          docSpec={buildProductDocSpec(product, calc, t)}
          onClose={() => setShowPdf(false)}
        />
      )}

      {/* Header card */}
      <Card className="mb-5">
        <div className="p-4 sm:p-5 bg-gradient-to-br from-slate-50 to-white">
          <div className="flex items-center gap-2 mb-3">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-semibold tracking-wider uppercase ${
              isTaco ? 'text-amber-700 bg-amber-50' :
              isCombo ? 'text-violet-700 bg-violet-50' :
              'text-blue-700 bg-blue-50'
            }`}>
              {isTaco ? '🌮 Taco' : isCombo ? <><Layers className="w-3 h-3 mr-1" /> Combo</> : 'By the Pound'}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold tracking-wide text-slate-600 bg-slate-100 uppercase">
              {product.category}
            </span>
          </div>
          <FieldLabel>Product Name</FieldLabel>
          <Input value={product.name} onChange={(v) => patch({ name: v })} size="xl" className="font-bold text-slate-900 tracking-tight" />
          <FieldLabel className="mt-3">Notes</FieldLabel>
          <Textarea value={product.notes} onChange={(v) => patch({ notes: v })} placeholder="Add product notes..." />
        </div>
      </Card>
      {/* Tortilla type selector for tacos */}
      {isTaco && (
        <Card className="mb-5">
          <SectionHeader number="01" title="Tortilla Type" subtitle="Choose how this taco is prepared" />
          <div className="px-4 sm:px-5 pb-4 sm:pb-5">
            <div className="grid grid-cols-2 gap-2.5">
              {product.sizes.map((sz) => {
                const isActive = sz.id === selectedSizeId;
                return (
                  <button
                    key={sz.id}
                    onClick={() => setSelectedSizeId(sz.id)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      isActive
                        ? 'border-amber-500 bg-amber-50 shadow-sm'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        isActive ? 'border-amber-500' : 'border-slate-300'
                      }`}>
                        {isActive && <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>}
                      </div>
                      <span className={`text-[14px] font-bold ${isActive ? 'text-amber-700' : 'text-slate-900'}`}>
                        Tortilla de {sz.label}
                      </span>
                    </div>
                    <div className={`text-[20px] font-bold mt-1 ${isActive ? 'text-amber-700' : 'text-slate-900'}`} style={{ fontFamily: tokens.fontMono }}>
                      {money(sz.sellingPrice)}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {sz.tortillaType === 'maiz' ? 'Made fresh from your tortilla recipe' : 'Flat cost (no recipe yet)'}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {/* Size selector for simple products with multiple sizes */}
      {!isCombo && !isTaco && product.sizes && product.sizes.length > 1 && (
        <Card className="mb-5">
          <SectionHeader number="01" title="Size" subtitle="Each size has its own portion and price" />
          <div className="px-4 sm:px-5 pb-4 sm:pb-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {product.sizes.map((sz) => {
                const isActive = sz.id === selectedSizeId;
                return (
                  <button
                    key={sz.id}
                    onClick={() => setSelectedSizeId(sz.id)}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      isActive
                        ? 'border-blue-600 bg-blue-50 shadow-sm'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className={`text-[13px] font-bold ${isActive ? 'text-blue-700' : 'text-slate-900'}`}>
                      {sz.label}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {sz.qty} {sz.unit}
                    </div>
                    <div className="text-[14px] font-bold text-slate-900 mt-1" style={{ fontFamily: tokens.fontMono }}>
                      {money(sz.sellingPrice)}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {/* TACO components — carne + tortillas */}
      {isTaco && (
        <Card className="mb-5">
          <SectionHeader number="02" title="Taco Composition" subtitle="Carne portion and tortilla count" />
          <div className="px-4 sm:px-5 pb-4 sm:pb-5 space-y-3">
            {/* Carne row */}
            <div className="p-4 rounded-xl border border-rose-100 bg-rose-50/40">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-7 h-7 rounded-lg bg-rose-500 text-white flex items-center justify-center">
                  <BookOpen className="w-3.5 h-3.5" strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-slate-900">
                    {calc.carneRecipe?.name || 'Recipe missing'}
                  </div>
                  <div className="text-[11px] text-slate-500">Meat portion per taco</div>
                </div>
                <div className="text-[15px] font-bold text-rose-700" style={{ fontFamily: tokens.fontMono }}>
                  {money(calc.carneCost)}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <FieldLabel>Carne (grams)</FieldLabel>
                  <NumberInput
                    value={product.carneGramsPerTaco}
                    onChange={(v) => patch({ carneGramsPerTaco: v })}
                    suffix="g"
                  />
                </div>
                <div>
                  <FieldLabel>Recipe</FieldLabel>
                  <select
                    value={product.carneRecipeId || ''}
                    onChange={(e) => patch({ carneRecipeId: e.target.value })}
                    className={`${inputBase} cursor-pointer text-[13px]`}
                  >
                    {recipes.filter((r) => r.category === 'Carnes' || r.category === 'Aves').map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              {calc.carneCalc?.realCostPerBaseUnit && (
                <div className="mt-2 text-[10px] text-slate-500">
                  Cost: ${calc.carneCalc.realCostPerBaseUnit.toFixed(4)}/g × {calc.carneGrams}g
                </div>
              )}
            </div>

            {/* Tortilla row */}
            <div className="p-4 rounded-xl border border-amber-100 bg-amber-50/40">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-7 h-7 rounded-lg bg-amber-500 text-white flex items-center justify-center text-[14px]">
                  🌮
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-slate-900">
                    Tortillas de {calc.size?.label}
                  </div>
                  <div className="text-[11px] text-slate-500">{calc.tortillaDetail}</div>
                </div>
                <div className="text-[15px] font-bold text-amber-700" style={{ fontFamily: tokens.fontMono }}>
                  {money(calc.tortillaCost)}
                </div>
              </div>
              <div>
                <FieldLabel>Tortillas per taco — {calc.size?.label}</FieldLabel>
                <NumberInput
                  value={calc.numTortillas}
                  onChange={(v) => {
                    const updatedSizes = product.sizes.map((s) =>
                      s.id === calc.size.id ? { ...s, tortillasOverride: v } : s
                    );
                    patch({ sizes: updatedSizes });
                  }}
                  suffix={(calc.numTortillas || 0) === 1 ? 'tortilla' : 'tortillas'}
                />
                <div className="text-[10px] text-slate-500 mt-1">
                  Each size can have a different tortilla count. Switch tabs above to edit the other size.
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Components for SIMPLE: recipe portion */}
      {!isCombo && !isTaco && (
        <Card className="mb-5">
          <SectionHeader
            number={product.sizes?.length > 1 ? '02' : '01'}
            title="Recipe Portion"
            subtitle="Linked recipe and how much goes into this product"
          />
          <div className="px-4 sm:px-5 pb-4 sm:pb-5">
            <SimpleRecipePortionEditor
              product={product}
              size={calc.size}
              recipes={recipes}
              calc={calc}
              onPatchSize={(changes) => {
                const updatedSizes = product.sizes.map((s) =>
                  s.id === calc.size.id ? { ...s, ...changes } : s
                );
                patch({ sizes: updatedSizes });
              }}
              onPatchRecipe={(recipeId) => patch({ recipeId })}
            />
          </div>
        </Card>
      )}

      {/* Components for COMBO */}
      {isCombo && (
        <Card className="mb-5">
          <SectionHeader
            number="01"
            title="Combo Components"
            subtitle="Recipes, purchased items, and flat costs that make up this combo"
          />
          <div className="px-4 sm:px-5 pb-4 sm:pb-5 space-y-2.5">
            {calc.componentResults.map(({ comp, cost, label }) => (
              <ComponentRow
                key={comp.id}
                component={comp}
                cost={cost}
                label={label}
                recipes={recipes}
                purchasedItems={purchasedItems}
              />
            ))}

            <div className="mt-4 p-3.5 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-between">
              <span className="text-[11px] font-semibold tracking-wider text-slate-500 uppercase">
                Components Subtotal
              </span>
              <span className="text-[16px] font-bold text-slate-900" style={{ fontFamily: tokens.fontMono }}>
                {money(calc.componentsTotal)}
              </span>
            </div>
          </div>
        </Card>
      )}

      {/* Service Template */}
      <Card className="mb-5">
        <SectionHeader
          number={isTaco ? '03' : isCombo ? '02' : (product.sizes?.length > 1 ? '03' : '02')}
          title="Service Items"
          subtitle="Packaging, utensils, and condiments included with this product"
        />
        <div className="px-4 sm:px-5 pb-4 sm:pb-5">
          {template ? (
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[14px] font-semibold text-slate-900">{template.name}</div>
                  <div className="text-[11px] text-slate-500">{template.description}</div>
                </div>
                <span className="text-[13px] font-bold text-slate-900" style={{ fontFamily: tokens.fontMono }}>
                  {money(calc.serviceCost)}
                </span>
              </div>
              <div className="space-y-1.5 pt-3 border-t border-slate-100">
                {template.items.map((item) => (
                  <div key={item.id} className="flex items-center justify-between text-[12px]">
                    <span className="text-slate-600">· {item.name}</span>
                    <span className="text-slate-700 font-medium" style={{ fontFamily: tokens.fontMono }}>
                      {money(item.cost)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-center">
              <div className="text-[12px] text-slate-500">No service template assigned</div>
            </div>
          )}
        </div>
      </Card>

      {/* Cost Summary */}
      <Card className="mb-5 overflow-hidden">
        <SectionHeader
          number={isTaco ? '04' : isCombo ? '03' : (product.sizes?.length > 1 ? '04' : '03')}
          title="Total Cost & Margin"
          subtitle="Final product economics"
        />
        <div className="px-4 sm:px-5 pb-4 sm:pb-5">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-center">
            <div className="lg:col-span-3 space-y-1.5">
              {isTaco && <BreakdownRow label="Carne" value={money(calc.carneCost)} />}
              {isTaco && <BreakdownRow label="Tortillas" value={money(calc.tortillaCost)} />}
              {!isCombo && !isTaco && <BreakdownRow label="Recipe portion" value={money(calc.recipeCost)} />}
              {isCombo && <BreakdownRow label="Components" value={money(calc.componentsTotal)} />}
              <BreakdownRow label="Service" value={money(calc.serviceCost)} />
            </div>
            <div className="lg:col-span-2 rounded-xl bg-gradient-to-br from-slate-900 to-slate-800 p-4 text-white shadow-lg shadow-slate-900/20">
              <div className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase">Total Cost</div>
              <div className="mt-1 text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: tokens.fontMono }}>
                {money(calc.totalCost)}
              </div>
              <div className="mt-2 pt-2 border-t border-slate-700 flex items-center justify-between">
                <span className="text-[11px] text-slate-400">Selling at</span>
                <span className="text-[14px] font-bold" style={{ fontFamily: tokens.fontMono }}>
                  {money(calc.sellingPrice)}
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-[11px] text-slate-400">Profit</span>
                <span className={`text-[14px] font-bold ${calc.profit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} style={{ fontFamily: tokens.fontMono }}>
                  {calc.profit >= 0 ? '+' : ''}{money(calc.profit)}
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-[11px] text-slate-400">Margin</span>
                <span className={`text-[16px] font-bold ${
                  calc.healthStatus === 'losing' ? 'text-rose-400' :
                  calc.healthStatus === 'tight' ? 'text-amber-400' :
                  'text-emerald-400'
                }`} style={{ fontFamily: tokens.fontMono }}>
                  {pct(calc.margin)}
                </span>
              </div>
            </div>
          </div>

          {/* Selling Price editor */}
          <div className="mt-4 pt-4 border-t border-slate-100">
            {isCombo ? (
              <div>
                <FieldLabel>Selling Price</FieldLabel>
                <CurrencyInput
                  value={product.sellingPrice}
                  onChange={(v) => patch({ sellingPrice: v })}
                  emphasis
                />
              </div>
            ) : (
              <div>
                <FieldLabel>Selling Price ({calc.size?.label})</FieldLabel>
                <CurrencyInput
                  value={calc.size?.sellingPrice}
                  onChange={(v) => {
                    const updatedSizes = product.sizes.map((s) =>
                      s.id === calc.size.id ? { ...s, sellingPrice: v } : s
                    );
                    patch({ sizes: updatedSizes });
                  }}
                  emphasis
                />
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

function SimpleRecipePortionEditor({ product, size, recipes, calc, onPatchSize, onPatchRecipe }) {
  const recipe = recipes.find((r) => r.id === product.recipeId);
  return (
    <div className="space-y-3">
      {/* Recipe selector */}
      <div>
        <FieldLabel>Recipe</FieldLabel>
        <select
          value={product.recipeId || ''}
          onChange={(e) => onPatchRecipe(e.target.value)}
          className={`${inputBase} cursor-pointer`}
        >
          <option value="">— Select recipe —</option>
          {recipes.map((r) => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
      </div>

      {recipe && (
        <>
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <FieldLabel>Portion Size</FieldLabel>
              <NumberInput
                value={size.qty}
                onChange={(v) => onPatchSize({ qty: v })}
              />
            </div>
            <div>
              <FieldLabel>Unit</FieldLabel>
              <UnitSelect value={size.unit} onChange={(v) => onPatchSize({ unit: v })} />
            </div>
          </div>

          <div className="p-3.5 rounded-xl bg-blue-50/60 border border-blue-100">
            <div className="flex items-center justify-between text-[11px] mb-1">
              <span className="text-blue-800">Recipe cost per yield unit</span>
              <span className="font-semibold text-blue-900" style={{ fontFamily: tokens.fontMono }}>
                {money(calc.recipeCalc?.realCostPerUnit || 0)} / {recipe.yield.unit}
              </span>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-blue-200/60">
              <span className="text-[11px] font-semibold tracking-wider text-blue-700 uppercase">
                {size.qty} {size.unit} of {recipe.name}
              </span>
              <span className="text-lg font-bold text-blue-700" style={{ fontFamily: tokens.fontMono }}>
                {money(calc.recipeCost)}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ComponentRow({ component, cost, label, recipes, purchasedItems }) {
  let detail = '';
  let icon = null;
  let bg = 'bg-slate-50';
  let border = 'border-slate-100';
  let dotColor = 'bg-slate-400';

  if (component.type === 'recipe') {
    const r = recipes.find((rr) => rr.id === component.recipeId);
    detail = r ? `Recipe: ${r.name}` : 'Recipe missing';
    icon = <BookOpen className="w-3.5 h-3.5" />;
    bg = 'bg-blue-50/40';
    border = 'border-blue-100';
    dotColor = 'bg-blue-500';
  } else if (component.type === 'purchased') {
    const p = purchasedItems.find((pp) => pp.id === component.purchasedItemId);
    detail = p ? `Purchased: ${p.name}` : 'Item missing';
    icon = <ShoppingBag className="w-3.5 h-3.5" />;
    bg = 'bg-violet-50/40';
    border = 'border-violet-100';
    dotColor = 'bg-violet-500';
  } else if (component.type === 'flat') {
    detail = 'Flat cost (sides, condiments, etc.)';
    icon = <DollarSign className="w-3.5 h-3.5" />;
    bg = 'bg-amber-50/40';
    border = 'border-amber-100';
    dotColor = 'bg-amber-500';
  }

  return (
    <div className={`p-3.5 rounded-xl border ${bg} ${border} flex items-start justify-between gap-3`}>
      <div className="flex items-start gap-2.5 min-w-0">
        <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-white ${dotColor}`}>
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-slate-900 truncate">{label}</div>
          <div className="text-[11px] text-slate-500">{detail}</div>
        </div>
      </div>
      <div className="text-[14px] font-bold text-slate-900 flex-shrink-0" style={{ fontFamily: tokens.fontMono }}>
        {money(cost)}
      </div>
    </div>
  );
}



// Scenario options for Price Optimization
const PRICE_SCENARIOS = [
  { id: 'plus50c', label: '+$0.50',  type: 'abs', value: 0.50 },
  { id: 'plus1',   label: '+$1.00',  type: 'abs', value: 1.00 },
  { id: 'plus5p',  label: '+5%',     type: 'pct', value: 0.05 },
  { id: 'plus10p', label: '+10%',    type: 'pct', value: 0.10 },
  { id: 'custom',  label: 'Custom',  type: 'custom' },
];

// Build a recommendation for a scenario applied to a recipe
const evaluateScenario = (c, newPrice) => {
  const newProfit = newPrice - c.realCostPerUnit;
  const newMargin = newPrice > 0 ? ((newPrice - c.realCostPerUnit) / newPrice) * 100 : 0;
  const profitDelta = newProfit - c.profitPerUnit;

  let verdict, label, tone, reason;

  // Below cost → never
  if (newPrice <= c.realCostPerUnit) {
    verdict = 'not_recommended';
    label = 'Not recommended';
    tone = 'rose';
    reason = 'Below cost threshold — would lose money on every unit';
  }
  // Below 25% margin → risky low
  else if (newMargin < 25) {
    verdict = 'not_recommended';
    label = 'Not recommended';
    tone = 'rose';
    reason = 'Price too low for a healthy margin — below 25%';
  }
  // Above competitor high → risky premium (only if competitor data exists)
  else if (c.competitorHigh > 0 && newPrice > c.competitorHigh * 1.15) {
    verdict = 'risky';
    label = 'Risky — above market';
    tone = 'violet';
    reason = `Significantly above competitor ceiling of ${money(c.competitorHigh)} — customers may resist`;
  }
  else if (c.competitorHigh > 0 && newPrice > c.competitorHigh) {
    verdict = 'risky';
    label = 'Premium pricing';
    tone = 'violet';
    reason = `Above competitor range — justified only if your quality/brand supports it`;
  }
  // Tight margin 25-35%
  else if (newMargin < 35) {
    verdict = 'marginal';
    label = 'Acceptable';
    tone = 'amber';
    reason = 'Works, but margin is still tight — consider a larger increase';
  }
  // Sweet spot
  else {
    verdict = 'recommended';
    label = 'Recommended';
    tone = 'emerald';
    reason = `Healthy ${newMargin.toFixed(1)}% margin${c.competitorHigh > 0 ? ` · within market range` : ''}`;
  }

  return { newPrice, newProfit, newMargin, profitDelta, verdict, label, tone, reason };
};

// ============================================================
// TAB · STEPS (Recipe Playbooks — clean view for kitchen staff)
// ============================================================

// Maps equipment names to emoji icons. Matches by keyword (case-insensitive, EN+ES).
const equipmentIcon = (text) => {
  const s = (text || '').toLowerCase();
  // Order matters — most specific first
  if (s.match(/tortilla.?press|prensa.?tortilla/)) return '⚙️';
  if (s.match(/blender|licuadora|procesador/)) return '🌀';
  if (s.match(/comal|griddle|plancha/)) return '🔥';
  if (s.match(/rotisserie|rosticero/)) return '🍗';
  if (s.match(/skillet|sartén|sarten|frying.?pan/)) return '🍳';
  if (s.match(/steam.?pot|olla.?vapor|stock.?pot|olla.?grande/)) return '🍲';
  if (s.match(/copper|cazo|pot|olla|cazuela/)) return '🍲';
  if (s.match(/fryer|freidora/)) return '♨️';
  if (s.match(/oven|horno/)) return '♨️';
  if (s.match(/tongs|pinzas/)) return '🥢';
  if (s.match(/wooden.?spoon|cuchara.?madera|wooden|madera/)) return '🥄';
  if (s.match(/slotted|spoon|cuchara|espumadera|whisk|batidor/)) return '🥄';
  if (s.match(/knife|cuchillo/)) return '🔪';
  if (s.match(/cutting.?board|tabla.?cortar|tabla/)) return '🪵';
  if (s.match(/strainer|colador|mesh/)) return '🕸️';
  if (s.match(/bowl|tazón|tazon|recipiente/)) return '🥣';
  if (s.match(/tray|charola|bandeja/)) return '🍽️';
  if (s.match(/rack|rejilla/)) return '🔲';
  if (s.match(/spatula|espátula|espatula/)) return '🥄';
  if (s.match(/banana.?leaves|hoja.?plátano|hoja.?platano/)) return '🌿';
  if (s.match(/plastic|plástico|plastico|bag|bolsa/)) return '🧴';
  if (s.match(/cloth|trapo|paper.?towel|toalla/)) return '🧻';
  if (s.match(/twine|hilo/)) return '🧵';
  if (s.match(/skewer|varilla/)) return '🪡';
  if (s.match(/container|recipiente|brining/)) return '🥡';
  return '🔧'; // default wrench
};

function StepsTab({ recipes, playbooks, onOpen }) {
  const { t } = useT();
  const [search, setSearch] = useState('');
  const filtered = useMemo(
    () => recipes.filter((r) => r.name.toLowerCase().includes(search.toLowerCase())),
    [recipes, search]
  );

  // Group by category
  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach((r) => {
      const cat = r.category || 'Otros';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(r);
    });
    return groups;
  }, [filtered]);

  return (
    <div>
      <ModuleHero module="steps" />

      <div className="mb-5 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder={t('recipe.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-[14px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition"
        />
      </div>

      {/* Total counter */}
      <div className="mb-3 flex items-center gap-2 text-[12px] text-slate-500">
        <span className="font-semibold text-slate-700">{filtered.length}</span>
        <span>{filtered.length === 1 ? t('analytics.recipe') : t('analytics.recipeP')}</span>
        {search && (
          <>
            <span className="text-slate-300">·</span>
            <span>{t('recipe.filteredFrom', { n: recipes.length })}</span>
          </>
        )}
      </div>

      {Object.entries(grouped).map(([category, recs]) => (
        <div key={category} className="mb-7">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-[13px] font-bold tracking-wide text-slate-500 uppercase">{category}</h3>
            <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-md text-[10px] font-bold">
              {recs.length} {t('misc.of')} {filtered.length}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recs.map((r) => {
              const playbook = playbooks[r.id];
              const stepCount = playbook?.steps?.length || 0;
              const hasPhoto = !!playbook?.photoUrl;
              return (
                <button
                  key={r.id}
                  onClick={() => onOpen(r.id)}
                  className="group text-left bg-white rounded-2xl border border-slate-200 hover:border-slate-300 hover:shadow-lg hover:shadow-slate-900/5 transition-all overflow-hidden"
                >
                  {hasPhoto && (
                    <div className="relative w-full h-32 bg-slate-100 overflow-hidden">
                      <img
                        src={playbook.photoUrl}
                        alt={r.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white shadow-sm">
                        <ChefHat className="w-5 h-5" strokeWidth={2} />
                      </div>
                      {stepCount > 0 ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold tracking-wide text-orange-700 bg-orange-50 uppercase">
                          {stepCount} STEPS
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold tracking-wide text-amber-700 bg-amber-50 uppercase">
                          No steps yet
                        </span>
                      )}
                    </div>

                    <h3 className="text-xl font-semibold text-slate-900 tracking-tight">{r.name}</h3>
                    <div className="text-[11px] text-slate-500 mt-1 mb-3">{r.category}</div>

                    {/* TIME / METHOD cards — matching Menu/Recipes style */}
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                        <div className="text-[9px] font-semibold tracking-wider text-slate-500 uppercase">Time</div>
                        <div className="text-[15px] font-bold text-slate-900 mt-0.5" style={{ fontFamily: tokens.fontMono }}>
                          {(r.prepHours || 0) + (r.cookHours || 0)} hrs
                        </div>
                      </div>
                      <div className="p-2.5 rounded-lg bg-blue-50 border border-blue-100">
                        <div className="text-[9px] font-semibold tracking-wider text-blue-700 uppercase">Method</div>
                        <div className="text-[15px] font-bold text-blue-700 mt-0.5">
                          {r.includeCookInLabor ? 'Active' : 'Passive'}
                        </div>
                      </div>
                    </div>

                    {/* Footer: equipment count + chevron */}
                    {playbook && (
                      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px]">
                        <span className="flex items-center gap-1.5 text-slate-500">
                          <Package className="w-3 h-3 text-slate-400" />
                          <span className="font-medium text-slate-700">{(playbook.equipment || []).length}</span>
                          <span>tools needed</span>
                        </span>
                        <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function StepsDetail({ recipe, playbook, onUpdatePlaybook, language, onChangeLanguage, onBack }) {
  const [trainingMode, setTrainingMode] = useState(false);
  const [editMode, setEditMode] = useState(false);

  if (!playbook) {
    return (
      <div>
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-[13px] font-medium text-slate-600 hover:text-slate-900 px-2 py-1 -ml-2 rounded-md hover:bg-slate-100 transition mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> All Recipes
        </button>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
          <ChefHat className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-amber-900">No steps yet for {recipe.name}</h3>
          <p className="text-[13px] text-amber-700 mt-1">Cooking instructions will be added here.</p>
        </div>
      </div>
    );
  }

  if (trainingMode) {
    return (
      <TrainingMode
        recipe={recipe}
        playbook={playbook}
        language={language}
        onChangeLanguage={onChangeLanguage}
        onExit={() => setTrainingMode(false)}
      />
    );
  }

  if (editMode) {
    return (
      <PlaybookEditor
        recipe={recipe}
        playbook={playbook}
        onSave={(updated) => { onUpdatePlaybook(updated); setEditMode(false); }}
        onCancel={() => setEditMode(false)}
      />
    );
  }

  const t = (obj) => obj?.[language] || obj?.en || '';
  const equipment = playbook.equipment || [];
  const steps = playbook.steps || [];
  const tips = t(playbook.tips);

  return (
    <div>
      {/* Top bar */}
      <div className="mb-5 flex items-center justify-between gap-2 flex-wrap">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-[13px] font-medium text-slate-600 hover:text-slate-900 px-2 py-1 -ml-2 rounded-md hover:bg-slate-100 transition"
        >
          <ArrowLeft className="w-4 h-4" /> All Recipes
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <LanguageToggle language={language} onChange={onChangeLanguage} />
          <button
            onClick={() => setEditMode(true)}
            className="px-3 py-2 text-[13px] font-semibold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition flex items-center gap-1.5"
          >
            <FileText className="w-4 h-4" />
            {language === 'es' ? 'Editar' : 'Edit'}
          </button>
          <button
            onClick={() => setTrainingMode(true)}
            className="px-4 py-2 text-[13px] font-semibold text-white bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl shadow-sm shadow-blue-600/30 hover:shadow-blue-600/50 transition flex items-center gap-1.5"
          >
            <Sparkles className="w-4 h-4" />
            {language === 'es' ? 'Modo Entrenamiento' : 'Training Mode'}
          </button>
        </div>
      </div>

      {/* Hero with photo */}
      <Card className="mb-5 overflow-hidden">
        {playbook.photoUrl ? (
          <div className="relative w-full h-48 sm:h-64 bg-slate-100">
            <img
              src={playbook.photoUrl}
              alt={recipe.name}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        ) : (
          <div className="w-full h-32 bg-gradient-to-br from-slate-100 to-slate-50 border-b border-slate-200 flex items-center justify-center">
            <div className="text-center text-slate-400">
              <ChefHat className="w-8 h-8 mx-auto mb-1" strokeWidth={1.5} />
              <div className="text-[11px] font-medium">
                {language === 'es' ? 'Sin foto · Edita para agregar' : 'No photo · Edit to add'}
              </div>
            </div>
          </div>
        )}
        <div className="p-4 sm:p-5 bg-gradient-to-br from-blue-50/60 to-white border-b border-slate-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white shadow-md">
              <ChefHat className="w-6 h-6" strokeWidth={2} />
            </div>
            <div>
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold tracking-wide text-blue-700 bg-blue-100 uppercase">
                {recipe.category}
              </span>
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight leading-none">{recipe.name}</h1>

          {/* The 4 stat cards — dark blue with white text (Liz's spec) */}
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <PlaybookStatDark
              label={language === 'es' ? 'Rinde' : 'Yield'}
              value={`${recipe.yield.value} ${recipe.yield.unit}`}
            />
            <PlaybookStatDark label="Prep" value={`${recipe.prepHours || 0} hrs`} />
            <PlaybookStatDark
              label={language === 'es' ? 'Cocción' : 'Cooking'}
              value={`${recipe.cookHours || 0} hrs`}
            />
            <PlaybookStatDark
              label={language === 'es' ? 'Método' : 'Method'}
              value={recipe.includeCookInLabor
                ? (language === 'es' ? 'Activa' : 'Active')
                : (language === 'es' ? 'Pasiva' : 'Passive')}
            />
          </div>
        </div>
      </Card>

      {/* Equipment with emoji icons — moved ABOVE Ingredients */}
      <Card className="mb-5">
        <div className="p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-4">
            <Package className="w-4 h-4 text-slate-500" />
            <h2 className="text-[13px] font-bold tracking-wider text-slate-700 uppercase">
              {language === 'es' ? 'Equipo Necesario' : 'Equipment Needed'}
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            {equipment.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-2.5 p-3 rounded-xl bg-gradient-to-br from-blue-900 to-blue-950 border border-blue-800 shadow-sm"
              >
                <span className="text-2xl flex-shrink-0">{equipmentIcon(item.en + ' ' + item.es)}</span>
                <span className="text-[12px] text-white font-semibold leading-tight">{t(item)}</span>
              </div>
            ))}
            {equipment.length === 0 && (
              <div className="col-span-full text-center text-[12px] text-slate-400 py-3">
                {language === 'es' ? 'Sin equipment definido' : 'No equipment defined'}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Ingredients (read-only from Layer 1) — moved BELOW Equipment, light gray theme */}
      <Card className="mb-5">
        <div className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-slate-500" />
              <h2 className="text-[13px] font-bold tracking-wider text-slate-700 uppercase">
                {language === 'es' ? 'Ingredientes' : 'Ingredients'}
              </h2>
            </div>
            <span className="text-[10px] text-slate-400 italic">
              {language === 'es' ? 'editar en pestaña Recipes' : 'edit in Recipes tab'}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(recipe.ingredients || []).map((ing) => (
              <div key={ing.id} className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-slate-50/60 border border-slate-100">
                <span className="text-[13px] text-slate-800 font-medium">{ing.name}</span>
                <span className="text-[12px] font-mono text-slate-600 flex-shrink-0">
                  {ing.qty} {ing.unit}
                </span>
              </div>
            ))}
            {(recipe.ingredients || []).length === 0 && (
              <div className="col-span-2 text-center text-[12px] text-slate-400 py-4">
                {language === 'es' ? 'Sin ingredientes definidos' : 'No ingredients defined'}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Steps */}
      <Card className="mb-5">
        <div className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-500" />
              <h2 className="text-[13px] font-bold tracking-wider text-slate-700 uppercase">
                {language === 'es' ? 'Pasos' : 'Steps'}
              </h2>
            </div>
            <span className="text-[12px] text-slate-500 font-medium">
              {steps.length} {language === 'es' ? 'pasos' : 'steps'}
            </span>
          </div>
          <div className="space-y-4">
            {steps.map((step, i) => {
              const stepEquipment = (step.equipmentIds || [])
                .map((eid) => equipment.find((e) => e.id === eid))
                .filter(Boolean);
              return (
                <div key={step.id || i} className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-[13px] font-bold shadow-sm shadow-blue-600/30">
                    {i + 1}
                  </div>
                  <div className="flex-1 pt-1 pb-4 border-b border-slate-100 last:border-0 last:pb-0">
                    <h3 className="text-[15px] font-bold text-slate-900 tracking-tight">
                      {t(step.title)}
                    </h3>
                    <p className="text-[14px] text-slate-700 leading-relaxed mt-1.5">
                      {t(step.description)}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {step.timerMinutes ? (
                        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-50 border border-amber-100 text-[11px] font-semibold text-amber-800">
                          <Activity className="w-3 h-3" />
                          {step.timerMinutes < 60
                            ? `${step.timerMinutes} min`
                            : `${(step.timerMinutes / 60).toFixed(1)} hrs`}
                        </div>
                      ) : null}
                      {stepEquipment.map((eq) => (
                        <div
                          key={eq.id}
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-100 border border-slate-200 text-[11px] font-medium text-slate-700"
                        >
                          <span className="text-base leading-none">{equipmentIcon(eq.en + ' ' + eq.es)}</span>
                          {t(eq)}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Tips */}
      {tips && (
        <Card className="mb-5 border-2 border-amber-100">
          <div className="p-4 sm:p-5 bg-amber-50/40">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-amber-600" />
              <h2 className="text-[13px] font-bold tracking-wider text-amber-800 uppercase">
                {language === 'es' ? 'Tips del Chef' : 'Chef Tips'}
              </h2>
            </div>
            <p className="text-[14px] text-amber-900 leading-relaxed">{tips}</p>
          </div>
        </Card>
      )}
    </div>
  );
}

// ============================================================
// PLAYBOOK EDITOR — full edit experience for cooking guides
// ============================================================
function PlaybookEditor({ recipe, playbook, onSave, onCancel }) {
  const { t } = useT();
  const confirm = useConfirm();
  const [draft, setDraft] = useState(JSON.parse(JSON.stringify(playbook))); // deep copy

  const patch = (changes) => setDraft({ ...draft, ...changes });

  // ─── Photo ───
  const setPhotoUrl = (url) => patch({ photoUrl: url });

  // ─── Equipment ───
  const addEquipment = () => {
    const newItem = { id: `e${Date.now()}`, en: '', es: '' };
    patch({ equipment: [...(draft.equipment || []), newItem] });
  };
  const updateEquipment = (id, field, value) => {
    patch({
      equipment: draft.equipment.map((e) => (e.id === id ? { ...e, [field]: value } : e)),
    });
  };
  const removeEquipment = async (id) => {
    const ok = await confirm({
      title: t('recipe.removeEquipmentConfirm'),
      confirmLabel: t('action.delete'),
      cancelLabel: t('action.cancel'),
      danger: true,
    });
    if (!ok) return;
    patch({
      equipment: draft.equipment.filter((e) => e.id !== id),
      // Also remove this id from any step's equipmentIds
      steps: draft.steps.map((s) => ({
        ...s,
        equipmentIds: (s.equipmentIds || []).filter((eid) => eid !== id),
      })),
    });
  };

  // ─── Steps ───
  const addStep = () => {
    const newStep = {
      id: `s${Date.now()}`,
      equipmentIds: [],
      title: { en: '', es: '' },
      description: { en: '', es: '' },
    };
    patch({ steps: [...(draft.steps || []), newStep] });
  };
  const updateStep = (id, changes) => {
    patch({
      steps: draft.steps.map((s) => (s.id === id ? { ...s, ...changes } : s)),
    });
  };
  const updateStepBilingual = (id, field, lang, value) => {
    patch({
      steps: draft.steps.map((s) =>
        s.id === id ? { ...s, [field]: { ...s[field], [lang]: value } } : s
      ),
    });
  };
  const removeStep = async (id) => {
    const ok = await confirm({
      title: t('recipe.removeStepConfirm'),
      confirmLabel: t('action.delete'),
      cancelLabel: t('action.cancel'),
      danger: true,
    });
    if (!ok) return;
    patch({ steps: draft.steps.filter((s) => s.id !== id) });
  };
  const moveStep = (id, dir) => {
    const idx = draft.steps.findIndex((s) => s.id === id);
    if (idx === -1) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= draft.steps.length) return;
    const newSteps = [...draft.steps];
    [newSteps[idx], newSteps[newIdx]] = [newSteps[newIdx], newSteps[idx]];
    patch({ steps: newSteps });
  };
  const toggleStepEquipment = (stepId, eqId) => {
    patch({
      steps: draft.steps.map((s) => {
        if (s.id !== stepId) return s;
        const ids = s.equipmentIds || [];
        const has = ids.includes(eqId);
        return {
          ...s,
          equipmentIds: has ? ids.filter((id) => id !== eqId) : [...ids, eqId],
        };
      }),
    });
  };

  // ─── Tips ───
  const updateTipsBilingual = (lang, value) => {
    patch({ tips: { ...(draft.tips || {}), [lang]: value } });
  };

  return (
    <div>
      {/* Top bar */}
      <div className="mb-5 flex items-center justify-between gap-2 sticky top-0 bg-slate-50 z-10 py-2 -mx-4 sm:-mx-6 px-4 sm:px-6 border-b border-slate-200">
        <button
          onClick={onCancel}
          className="flex items-center gap-1.5 text-[13px] font-medium text-slate-600 hover:text-slate-900 px-2 py-1 -ml-2 rounded-md hover:bg-slate-100 transition"
        >
          <X className="w-4 h-4" /> Cancel
        </button>
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-semibold text-slate-600">Editing playbook</span>
          <button
            onClick={() => onSave(draft)}
            className="px-4 py-2 text-[13px] font-semibold text-white bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-sm shadow-emerald-600/30 hover:shadow-emerald-600/50 transition flex items-center gap-1.5"
          >
            <CheckCircle2 className="w-4 h-4" />
            Save
          </button>
        </div>
      </div>

      <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-5">
        Edit playbook · {recipe.name}
      </h1>

      {/* Photo */}
      <Card className="mb-5">
        <div className="p-4 sm:p-5">
          <h2 className="text-[13px] font-bold tracking-wider text-slate-700 uppercase mb-3">Photo of finished dish</h2>

          {draft.photoUrl ? (
            <div className="mb-3 relative w-full h-48 rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
              <img
                src={draft.photoUrl}
                alt="Preview"
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <button
                onClick={() => setPhotoUrl(null)}
                className="absolute top-2 right-2 px-2.5 py-1.5 bg-white/95 backdrop-blur-sm rounded-lg text-[12px] font-semibold text-rose-600 hover:bg-rose-50 shadow-sm flex items-center gap-1 transition"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Remove
              </button>
            </div>
          ) : (
            <div className="mb-3 w-full h-40 rounded-xl bg-slate-50 border-2 border-dashed border-slate-200 flex items-center justify-center">
              <div className="text-center text-slate-400">
                <ChefHat className="w-10 h-10 mx-auto mb-2" strokeWidth={1.5} />
                <div className="text-[12px] font-medium">No photo yet</div>
              </div>
            </div>
          )}

          {/* Hidden inputs — one for camera, one for gallery */}
          <input
            id="playbook-camera-input"
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = (ev) => setPhotoUrl(ev.target?.result || null);
              reader.readAsDataURL(file);
              e.target.value = ''; // reset so same file can be selected again
            }}
          />
          <input
            id="playbook-gallery-input"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = (ev) => setPhotoUrl(ev.target?.result || null);
              reader.readAsDataURL(file);
              e.target.value = '';
            }}
          />

          <div className="grid grid-cols-2 gap-2.5">
            <button
              onClick={() => document.getElementById('playbook-camera-input')?.click()}
              className="px-4 py-3 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 text-white font-semibold text-[13px] shadow-sm shadow-blue-600/30 hover:shadow-blue-600/50 transition flex items-center justify-center gap-2"
            >
              <span className="text-base leading-none">📷</span>
              Take Photo
            </button>
            <button
              onClick={() => document.getElementById('playbook-gallery-input')?.click()}
              className="px-4 py-3 rounded-xl bg-white border border-slate-200 text-slate-700 font-semibold text-[13px] hover:bg-slate-50 hover:border-slate-300 transition flex items-center justify-center gap-2"
            >
              <span className="text-base leading-none">🖼️</span>
              Choose from Photos
            </button>
          </div>

          <div className="text-[10px] text-slate-500 mt-2 italic">
            Photos are stored locally for now. Will sync to cloud storage in production.
          </div>
        </div>
      </Card>

      {/* Equipment editor */}
      <Card className="mb-5">
        <div className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[13px] font-bold tracking-wider text-slate-700 uppercase">Equipment</h2>
            <button
              onClick={addEquipment}
              className="px-3 py-1.5 text-[12px] font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg flex items-center gap-1 transition"
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
          <div className="space-y-2">
            {(draft.equipment || []).map((eq) => (
              <div key={eq.id} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 p-3 rounded-xl border border-slate-200 bg-white">
                <div>
                  <div className="text-[10px] font-semibold text-slate-500 uppercase mb-1">EN</div>
                  <input
                    value={eq.en}
                    onChange={(e) => updateEquipment(eq.id, 'en', e.target.value)}
                    placeholder="e.g. Heavy pot"
                    className={inputBase}
                  />
                </div>
                <div>
                  <div className="text-[10px] font-semibold text-slate-500 uppercase mb-1">ES</div>
                  <input
                    value={eq.es}
                    onChange={(e) => updateEquipment(eq.id, 'es', e.target.value)}
                    placeholder="ej. Olla pesada"
                    className={inputBase}
                  />
                </div>
                <button
                  onClick={() => removeEquipment(eq.id)}
                  className="self-end px-2 py-2 text-rose-600 hover:bg-rose-50 rounded-lg transition"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {(draft.equipment || []).length === 0 && (
              <div className="text-center text-[12px] text-slate-400 py-6">No equipment yet. Click "Add" above.</div>
            )}
          </div>
        </div>
      </Card>

      {/* Steps editor */}
      <Card className="mb-5">
        <div className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[13px] font-bold tracking-wider text-slate-700 uppercase">Steps</h2>
            <button
              onClick={addStep}
              className="px-3 py-1.5 text-[12px] font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg flex items-center gap-1 transition"
            >
              <Plus className="w-3.5 h-3.5" /> Add Step
            </button>
          </div>
          <div className="space-y-3">
            {(draft.steps || []).map((step, i) => (
              <div key={step.id} className="p-4 rounded-xl border-2 border-slate-200 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-[12px] font-bold">
                      {i + 1}
                    </div>
                    <span className="text-[12px] font-semibold text-slate-600">Step {i + 1}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => moveStep(step.id, -1)}
                      disabled={i === 0}
                      className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-lg transition disabled:opacity-30"
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => moveStep(step.id, 1)}
                      disabled={i === (draft.steps.length - 1)}
                      className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-lg transition disabled:opacity-30"
                      title="Move down"
                    >
                      ↓
                    </button>
                    <button
                      onClick={() => removeStep(step.id)}
                      className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg transition"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                  <div>
                    <div className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Title · EN</div>
                    <input
                      value={step.title?.en || ''}
                      onChange={(e) => updateStepBilingual(step.id, 'title', 'en', e.target.value)}
                      placeholder="Short step title"
                      className={inputBase}
                    />
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Title · ES</div>
                    <input
                      value={step.title?.es || ''}
                      onChange={(e) => updateStepBilingual(step.id, 'title', 'es', e.target.value)}
                      placeholder="Título corto del paso"
                      className={inputBase}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                  <div>
                    <div className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Description · EN</div>
                    <textarea
                      value={step.description?.en || ''}
                      onChange={(e) => updateStepBilingual(step.id, 'description', 'en', e.target.value)}
                      placeholder="Detailed instructions"
                      rows={3}
                      className={inputBase}
                    />
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Description · ES</div>
                    <textarea
                      value={step.description?.es || ''}
                      onChange={(e) => updateStepBilingual(step.id, 'description', 'es', e.target.value)}
                      placeholder="Instrucciones detalladas"
                      rows={3}
                      className={inputBase}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <div>
                    <div className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Timer (minutes, optional)</div>
                    <input
                      type="number"
                      value={step.timerMinutes ?? ''}
                      onChange={(e) => updateStep(step.id, { timerMinutes: e.target.value === '' ? null : Number(e.target.value) })}
                      placeholder="e.g. 30"
                      className={inputBase}
                    />
                  </div>
                </div>

                <div>
                  <div className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Equipment used in this step</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(draft.equipment || []).map((eq) => {
                      const isSelected = (step.equipmentIds || []).includes(eq.id);
                      return (
                        <button
                          key={eq.id}
                          onClick={() => toggleStepEquipment(step.id, eq.id)}
                          className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition flex items-center gap-1 ${
                            isSelected
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'
                          }`}
                        >
                          {isSelected && <CheckCircle2 className="w-3 h-3" />}
                          {eq.en || eq.es || '(unnamed)'}
                        </button>
                      );
                    })}
                    {(draft.equipment || []).length === 0 && (
                      <span className="text-[11px] text-slate-400 italic">Add equipment above to tag it here</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {(draft.steps || []).length === 0 && (
              <div className="text-center text-[12px] text-slate-400 py-6">No steps yet. Click "Add Step" above.</div>
            )}
          </div>
        </div>
      </Card>

      {/* Tips editor */}
      <Card className="mb-5">
        <div className="p-4 sm:p-5">
          <h2 className="text-[13px] font-bold tracking-wider text-slate-700 uppercase mb-3">Chef Tips</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] font-semibold text-slate-500 uppercase mb-1">EN</div>
              <textarea
                value={draft.tips?.en || ''}
                onChange={(e) => updateTipsBilingual('en', e.target.value)}
                rows={3}
                className={inputBase}
              />
            </div>
            <div>
              <div className="text-[10px] font-semibold text-slate-500 uppercase mb-1">ES</div>
              <textarea
                value={draft.tips?.es || ''}
                onChange={(e) => updateTipsBilingual('es', e.target.value)}
                rows={3}
                className={inputBase}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Save bar at bottom too for long lists */}
      <div className="flex justify-end gap-2 mb-5">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-[13px] font-medium text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition"
        >
          Cancel
        </button>
        <button
          onClick={() => onSave(draft)}
          className="px-5 py-2 text-[13px] font-semibold text-white bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-sm shadow-emerald-600/30 hover:shadow-emerald-600/50 transition flex items-center gap-1.5"
        >
          <CheckCircle2 className="w-4 h-4" />
          Save Playbook
        </button>
      </div>
    </div>
  );
}

function LanguageToggle({ language, onChange }) {
  return (
    <div className="inline-flex items-center bg-white border border-slate-200 rounded-xl p-0.5 shadow-sm">
      <button
        onClick={() => onChange('en')}
        className={`px-2.5 py-1 rounded-lg text-[12px] font-semibold transition flex items-center gap-1 ${
          language === 'en' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'
        }`}
      >
        🇺🇸 EN
      </button>
      <button
        onClick={() => onChange('es')}
        className={`px-2.5 py-1 rounded-lg text-[12px] font-semibold transition flex items-center gap-1 ${
          language === 'es' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'
        }`}
      >
        🇲🇽 ES
      </button>
    </div>
  );
}

function PlaybookStat({ label, value }) {
  return (
    <div className="p-2.5 rounded-lg bg-white border border-slate-200">
      <div className="text-[10px] font-semibold tracking-wider text-slate-500 uppercase">{label}</div>
      <div className="text-[14px] font-bold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}

function PlaybookStatDark({ label, value }) {
  return (
    <div className="p-3 rounded-xl bg-gradient-to-br from-blue-900 to-blue-950 border border-blue-800 shadow-sm">
      <div className="text-[10px] font-semibold tracking-wider text-blue-300 uppercase">{label}</div>
      <div className="text-[14px] font-bold text-white mt-0.5">{value}</div>
    </div>
  );
}

// ============================================================
// TRAINING MODE — one step at a time, distraction-free
// ============================================================
function TrainingMode({ recipe, playbook, language, onChangeLanguage, onExit }) {
  const [stepIndex, setStepIndex] = useState(0);
  const steps = playbook.steps || [];
  const t = (obj) => obj?.[language] || obj?.en || '';
  const total = steps.length;
  const step = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === total - 1;
  const progress = ((stepIndex + 1) / total) * 100;

  if (!step) return null;

  // Portal to document.body so this full-screen overlay escapes the recipe-cost
  // subtree's stacking context — otherwise BeeNexa's sticky header / nav (z-index
  // up to 1000) render on top of it and hide the Exit / progress / language bar.
  // The wrapper re-declares id="ec-recipe-cost-root" so the scoped Tailwind
  // utilities still apply to the portaled DOM.
  return createPortal(
    <div id="ec-recipe-cost-root">
      <div className="fixed inset-0 bg-gradient-to-br from-slate-50 to-blue-50 z-[9999] overflow-y-auto" style={{ fontFamily: tokens.fontBody }}>
        {/* Top bar */}
        <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-slate-200 shadow-sm z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <button
            onClick={onExit}
            className="flex items-center gap-1.5 text-[13px] font-medium text-slate-600 hover:text-slate-900 px-2 py-1 -ml-2 rounded-md hover:bg-slate-100 transition"
          >
            <X className="w-4 h-4" /> {language === 'es' ? 'Salir' : 'Exit'}
          </button>
          <div className="flex-1 max-w-xs">
            <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="text-center text-[10px] font-semibold tracking-wider text-slate-500 uppercase mt-1">
              {language === 'es' ? 'Paso' : 'Step'} {stepIndex + 1} / {total}
            </div>
          </div>
          <LanguageToggle language={language} onChange={onChangeLanguage} />
        </div>
      </div>

      {/* Step content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white text-3xl font-bold shadow-xl shadow-blue-600/30 mb-5">
            {stepIndex + 1}
          </div>
          <div className="text-[11px] font-semibold tracking-wider text-blue-600 uppercase mb-2">
            {recipe.name}
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
            {t(step.title)}
          </h1>
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-10 shadow-sm">
          {/* Equipment used in this step — prominent for kitchen staff */}
          {(() => {
            const stepEq = (step.equipmentIds || [])
              .map((eid) => (playbook.equipment || []).find((e) => e.id === eid))
              .filter(Boolean);
            if (stepEq.length === 0) return null;
            return (
              <div className="mb-6 pb-6 border-b border-slate-200">
                <div className="text-[11px] font-bold tracking-wider text-blue-600 uppercase mb-3 flex items-center gap-1.5">
                  <Package className="w-3.5 h-3.5" />
                  {language === 'es' ? 'Para este paso necesitas' : 'For this step you need'}
                </div>
                <div className="flex flex-wrap gap-2">
                  {stepEq.map((eq) => (
                    <div
                      key={eq.id}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-50 border-2 border-blue-200 text-[14px] font-semibold text-blue-900"
                    >
                      <Package className="w-4 h-4 text-blue-600" />
                      {t(eq)}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          <p className="text-[18px] sm:text-[20px] text-slate-700 leading-relaxed">
            {t(step.description)}
          </p>
          {step.timerMinutes ? (
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-50 border border-amber-200 text-[14px] font-semibold text-amber-800">
              <Activity className="w-4 h-4" />
              {step.timerMinutes < 60
                ? `${step.timerMinutes} ${language === 'es' ? 'minutos' : 'minutes'}`
                : `${(step.timerMinutes / 60).toFixed(1)} ${language === 'es' ? 'horas' : 'hours'}`}
            </div>
          ) : null}
        </div>
      </div>

      {/* Bottom nav */}
      <div className="sticky bottom-0 bg-white/80 backdrop-blur-sm border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <button
            onClick={() => setStepIndex(Math.max(0, stepIndex - 1))}
            disabled={isFirst}
            className={`px-5 py-3 rounded-xl text-[14px] font-semibold flex items-center gap-2 transition ${
              isFirst
                ? 'text-slate-300 bg-slate-50 cursor-not-allowed'
                : 'text-slate-700 bg-white border border-slate-200 hover:bg-slate-50'
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            {language === 'es' ? 'Atrás' : 'Back'}
          </button>
          {isLast ? (
            <button
              onClick={onExit}
              className="flex-1 sm:flex-initial px-8 py-3 rounded-xl text-[14px] font-bold text-white bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-md shadow-emerald-600/30 hover:shadow-emerald-600/50 transition flex items-center justify-center gap-2"
            >
              <CheckCircle2 className="w-5 h-5" />
              {language === 'es' ? '¡Listo!' : 'Done!'}
            </button>
          ) : (
            <button
              onClick={() => setStepIndex(Math.min(total - 1, stepIndex + 1))}
              className="flex-1 sm:flex-initial px-8 py-3 rounded-xl text-[14px] font-bold text-white bg-gradient-to-br from-blue-600 to-blue-700 shadow-md shadow-blue-600/30 hover:shadow-blue-600/50 transition flex items-center justify-center gap-2"
            >
              {language === 'es' ? 'Siguiente Paso' : 'Next Step'}
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
    </div>,
    document.body
  );
}

// ============================================================
// INGREDIENTS TAB — Master List with CRUD
// ============================================================

function IngredientsTab({ ingredients, recipes, onCreate, onUpdate, onDelete, onDuplicate, onOpenMigration, hasBackup, onRevert }) {
  const { t } = useT();
  const confirm = useConfirm();
  const [search, setSearch] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return ingredients;
    return ingredients.filter((i) =>
      i.name.toLowerCase().includes(q) ||
      (i.nameEs || '').toLowerCase().includes(q) ||
      i.category.toLowerCase().includes(q)
    );
  }, [ingredients, search]);

  // Group by category, preserve INGREDIENT_CATEGORIES order
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((ing) => {
      const cat = ing.category || 'other';
      if (!map[cat]) map[cat] = [];
      map[cat].push(ing);
    });
    return INGREDIENT_CATEGORIES
      .map((c) => ({ ...c, items: map[c.id] || [] }))
      .filter((c) => c.items.length > 0);
  }, [filtered]);

  // Count how many recipes use each ingredient (best-effort name match)
  const recipeUsage = useMemo(() => {
    const usage = {};
    ingredients.forEach((ing) => {
      let count = 0;
      const ingNameLower = ing.name.toLowerCase();
      const ingNameEsLower = (ing.nameEs || '').toLowerCase();
      recipes.forEach((r) => {
        const match = r.ingredients.some((ri) => {
          const riLower = (ri.name || '').toLowerCase();
          return riLower === ingNameLower || riLower === ingNameEsLower ||
                 riLower.includes(ingNameLower) || (ingNameEsLower && riLower.includes(ingNameEsLower));
        });
        if (match) count++;
      });
      usage[ing.id] = count;
    });
    return usage;
  }, [ingredients, recipes]);

  const openEditor = (id) => {
    setEditingId(id);
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditingId(null);
  };

  const handleSave = (ing) => {
    if (editingId) onUpdate(ing);
    else onCreate(ing);
    closeEditor();
  };

  const handleDelete = async (ing) => {
    const usage = recipeUsage[ing.id] || 0;
    if (usage > 0) {
      const ok = await confirm({
        title: t('action.delete') + ' — ' + ing.name,
        message: t('ingredient.deleteUsageConfirm', { name: ing.name, n: usage }),
        confirmLabel: t('action.delete'),
        cancelLabel: t('action.cancel'),
        danger: true,
      });
      if (!ok) return;
    }
    onDelete(ing.id);
  };

  return (
    <div>
      <ModuleHero
        module="ingredients"
        rightSlot={
          <div className="flex items-center gap-2 flex-wrap justify-start sm:justify-end w-full sm:w-auto">
            {hasBackup && (
              <button
                onClick={async () => {
                  const ok = await confirm({
                    title: t('migration.revert'),
                    message: t('ingredient.revertConfirm'),
                    confirmLabel: t('action.revert'),
                    cancelLabel: t('action.cancel'),
                    danger: true,
                  });
                  if (ok) onRevert();
                }}
                className="flex items-center justify-center gap-1.5 px-3 py-2 bg-white text-amber-700 border border-amber-300 text-[12px] sm:text-[13px] font-semibold rounded-xl hover:bg-amber-50 transition"
                title={t('migration.revertTitle')}
              >
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.2} />
                <span className="hidden sm:inline">{t('migration.revert')}</span>
                <span className="sm:hidden">{t('migration.revertShort')}</span>
              </button>
            )}
            <button
              onClick={onOpenMigration}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 bg-white text-teal-700 border border-teal-300 text-[13px] sm:text-[14px] font-semibold rounded-xl hover:bg-teal-50 transition"
              title={t('migration.title')}
            >
              <Layers className="w-4 h-4" strokeWidth={2.2} />
              <span className="hidden sm:inline">{t('migration.migrateRecipes')}</span>
              <span className="sm:hidden">{t('migration.migrate')}</span>
              {hasBackup && (
                <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-md text-[9px] font-bold tracking-wide uppercase">
                  {t('migration.backupBadge')}
                </span>
              )}
            </button>
            <button
              onClick={() => openEditor(null)}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 bg-teal-600 text-white text-[13px] sm:text-[14px] font-medium rounded-xl hover:bg-teal-700 shadow-sm shadow-teal-600/20 transition"
            >
              <Plus className="w-4 h-4" strokeWidth={2.5} />
              <span className="hidden sm:inline">{t('ingredient.new')}</span>
              <span className="sm:hidden">{t('action.new')}</span>
            </button>
          </div>
        }
      />

      {/* Search */}
      <div className="mb-5 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder={t('ingredient.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-[14px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10 transition"
        />
      </div>

      {/* Total counter */}
      <div className="mb-3 flex items-center gap-2 text-[12px] text-slate-500">
        <span className="font-semibold text-slate-700">{filtered.length}</span>
        <span>{t('ingredient.count', { n: filtered.length }).replace(/^\d+\s+/, '')}</span>
        {search && (
          <>
            <span className="text-slate-300">·</span>
            <span>{t('recipe.filteredFrom', { n: ingredients.length })}</span>
          </>
        )}
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <Card>
          <div className="p-12 text-center">
            <Leaf className="w-10 h-10 text-teal-300 mx-auto mb-3" strokeWidth={1.5} />
            <div className="text-[14px] font-semibold text-slate-700">{t('ingredient.noneFound')}</div>
            <div className="text-[13px] text-slate-500 mt-1">
              {search ? t('ingredient.tryDifferent') : t('ingredient.addFirst')}
            </div>
          </div>
        </Card>
      )}

      {/* Grouped list by category */}
      {grouped.map((cat) => (
        <div key={cat.id} className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-base leading-none">{cat.emoji}</span>
            <h3 className="text-[13px] font-bold tracking-wide text-slate-500 uppercase">{t(cat.labelKey)}</h3>
            <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-md text-[10px] font-bold">
              {cat.items.length} {t('misc.of')} {filtered.length}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cat.items.map((ing) => (
              <IngredientCard
                key={ing.id}
                ingredient={ing}
                usage={recipeUsage[ing.id] || 0}
                onOpen={() => openEditor(ing.id)}
                onDuplicate={() => onDuplicate(ing.id)}
                onDelete={() => handleDelete(ing)}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Editor modal */}
      {showEditor && (
        <IngredientEditor
          ingredient={editingId ? ingredients.find((i) => i.id === editingId) : null}
          onSave={handleSave}
          onCancel={closeEditor}
        />
      )}
    </div>
  );
}

// Ingredient card in the list
function IngredientCard({ ingredient, usage, onOpen, onDuplicate, onDelete }) {
  const { t, lang } = useT();
  const { perUnit, perUnitWithMerma } = calcIngredientCost(ingredient);
  const hasMerma = (ingredient.mermaPercent || 0) > 0;
  const cat = INGREDIENT_CATEGORIES.find((c) => c.id === ingredient.category);
  const display = bilingualName(ingredient, lang);

  return (
    <div className="group bg-white rounded-2xl border border-slate-200 hover:border-slate-300 hover:shadow-lg hover:shadow-slate-900/5 transition-all overflow-hidden">
      <button onClick={onOpen} className="w-full p-4 text-left block">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="w-10 h-10 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center">
            <span className="text-xl leading-none">{cat?.emoji || '📦'}</span>
          </div>
          {usage > 0 ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold tracking-wide text-teal-700 bg-teal-50 uppercase">
              {usage} {usage === 1 ? t('analytics.recipe') : t('analytics.recipeP')}
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold tracking-wide text-slate-500 bg-slate-50 uppercase">
              {t('ingredient.unused')}
            </span>
          )}
        </div>

        <h3 className="text-[17px] font-semibold text-slate-900 tracking-tight">{display.primary}</h3>
        {display.secondary && (
          <div className="text-[11px] text-slate-500 mt-0.5">{display.secondary}</div>
        )}

        {/* Purchase / Use info */}
        <div className="mt-3 space-y-1.5 text-[12px]">
          <div className="flex items-center justify-between gap-2 text-slate-600">
            <span className="text-slate-400">{t('ingredient.buy')}</span>
            <span className="font-mono font-medium text-slate-700">
              {ingredient.purchaseUnitLabel} · ${ingredient.purchasePrice.toFixed(2)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2 text-slate-600">
            <span className="text-slate-400">{t('ingredient.use')}</span>
            <span className="font-mono font-medium text-slate-700">
              {fmtCostPerUnit(perUnit, ingredient.recipeUnit)}
            </span>
          </div>
          {hasMerma && (
            <div className="flex items-center justify-between gap-2 text-amber-700">
              <span>{t('ingredient.merma').split(' ')[0]} {ingredient.mermaPercent}%</span>
              <span className="font-mono font-semibold">
                {t('ingredient.realCostShort')}: {fmtCostPerUnit(perUnitWithMerma, ingredient.recipeUnit)}
              </span>
            </div>
          )}
        </div>
      </button>

      <div className="px-4 py-3 bg-slate-50/60 border-t border-slate-100 flex items-center justify-between">
        <button onClick={onOpen} className="text-[12px] font-medium text-teal-700 hover:text-teal-800 flex items-center gap-0.5">
          {t('action.edit')} <ChevronRight className="w-3.5 h-3.5" />
        </button>
        <div className="flex items-center gap-0.5">
          <IconButton onClick={onDuplicate} title={t('action.duplicate')}><Copy className="w-3.5 h-3.5" /></IconButton>
          <IconButton onClick={onDelete} title={t('action.delete')} danger><Trash2 className="w-3.5 h-3.5" /></IconButton>
        </div>
      </div>
    </div>
  );
}

// Editor modal — create / edit ingredient
function IngredientEditor({ ingredient, onSave, onCancel }) {
  const { t } = useT();
  const isNew = !ingredient;
  const [draft, setDraft] = useState(() => ingredient || {
    id: `ing_${Date.now()}`,
    name: '',
    nameEs: '',
    category: 'other',
    purchaseUnit: 'lb',
    purchaseUnitLabel: 'lb',
    purchasePrice: 0,
    purchaseQty: 1,
    purchaseQtyUnit: 'lb',
    recipeUnit: 'lb',
    mermaPercent: 0,
    notes: '',
  });

  const set = (key, val) => setDraft({ ...draft, [key]: val });

  const { perUnit, perUnitWithMerma } = calcIngredientCost(draft);
  const compatible = convertUnits(1, draft.purchaseQtyUnit, draft.recipeUnit) !== null;

  const canSave = draft.name.trim() && draft.purchasePrice >= 0 && draft.purchaseQty > 0;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div className="bg-white w-full sm:max-w-2xl rounded-2xl shadow-2xl max-h-[90vh] my-auto flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <Leaf className="w-5 h-5 text-teal-600" />
            <h2 className="text-[17px] font-bold text-slate-900">
              {isNew ? t('ingredient.newIngredient') : t('ingredient.editIngredient')}
            </h2>
          </div>
          <button
            onClick={onCancel}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-5 overflow-y-auto flex-1">
          {/* Names */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <FieldLabel>{t('ingredient.nameEn')} *</FieldLabel>
              <input
                type="text"
                value={draft.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="Beef Cheek"
                className={inputBase}
              />
            </div>
            <div>
              <FieldLabel>{t('ingredient.nameEs')}</FieldLabel>
              <input
                type="text"
                value={draft.nameEs || ''}
                onChange={(e) => set('nameEs', e.target.value)}
                placeholder="Cachete de Res"
                className={inputBase}
              />
            </div>
          </div>

          {/* Category */}
          <div>
            <FieldLabel>{t('ingredient.category')}</FieldLabel>
            <select
              value={draft.category}
              onChange={(e) => set('category', e.target.value)}
              className={inputBase}
            >
              {INGREDIENT_CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>{c.emoji} {t(c.labelKey)}</option>
              ))}
            </select>
          </div>

          {/* Purchase section */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <h3 className="text-[12px] font-bold tracking-wider text-slate-600 uppercase mb-3 flex items-center gap-1.5">
              <ShoppingBag className="w-3.5 h-3.5" /> {t('ingredient.purchaseInfo')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <FieldLabel>{t('ingredient.containsUnit').replace('Contains', 'Purchase').replace('Contiene', 'Compra')} Label</FieldLabel>
                <input
                  type="text"
                  value={draft.purchaseUnitLabel}
                  onChange={(e) => set('purchaseUnitLabel', e.target.value)}
                  placeholder="bag 50 lb"
                  className={inputBase}
                />
                <div className="text-[10px] text-slate-500 mt-1">{t('ingredient.example')} "bag 50 lb", "case", "lb"</div>
              </div>
              <div>
                <FieldLabel>{t('ingredient.purchasePrice')} (USD)</FieldLabel>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={draft.purchasePrice}
                  onChange={(e) => set('purchasePrice', parseFloat(e.target.value) || 0)}
                  className={inputBase}
                />
              </div>
              <div>
                <FieldLabel>{t('ingredient.purchaseQty')}</FieldLabel>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={draft.purchaseQty}
                  onChange={(e) => set('purchaseQty', parseFloat(e.target.value) || 0)}
                  className={inputBase}
                />
                <div className="text-[10px] text-slate-500 mt-1">{t('ingredient.example')} {t('ingredient.bagContains')} 50</div>
              </div>
              <div>
                <FieldLabel>{t('ingredient.containsUnit')}</FieldLabel>
                <select
                  value={draft.purchaseQtyUnit}
                  onChange={(e) => set('purchaseQtyUnit', e.target.value)}
                  className={inputBase}
                >
                  <optgroup label="Weight">
                    <option value="lb">lb (pound)</option>
                    <option value="kg">kg (kilo)</option>
                    <option value="oz">oz (ounce)</option>
                    <option value="g">g (gram)</option>
                  </optgroup>
                  <optgroup label="Volume">
                    <option value="gal">gallon</option>
                    <option value="qt">quart</option>
                    <option value="l">liter</option>
                    <option value="cup">cup</option>
                    <option value="fl_oz">fl oz</option>
                    <option value="tbsp">tbsp</option>
                    <option value="tsp">tsp</option>
                    <option value="ml">ml</option>
                  </optgroup>
                  <optgroup label="Count">
                    <option value="each">each</option>
                    <option value="dozen">dozen</option>
                  </optgroup>
                </select>
                <div className="text-[10px] text-slate-500 mt-1">{t('ingredient.example')} 50 <strong>lb</strong></div>
              </div>
            </div>
          </div>

          {/* Recipe unit section */}
          <div className="bg-teal-50/50 rounded-xl p-4 border border-teal-200">
            <h3 className="text-[12px] font-bold tracking-wider text-teal-700 uppercase mb-3 flex items-center gap-1.5">
              <ChefHat className="w-3.5 h-3.5" /> {t('ingredient.howRecipesUseIt')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <FieldLabel>{t('ingredient.recipeUnit')}</FieldLabel>
                <select
                  value={draft.recipeUnit}
                  onChange={(e) => set('recipeUnit', e.target.value)}
                  className={inputBase}
                >
                  <optgroup label="Weight">
                    <option value="g">g (gram)</option>
                    <option value="kg">kg (kilo)</option>
                    <option value="oz">oz (ounce)</option>
                    <option value="lb">lb (pound)</option>
                  </optgroup>
                  <optgroup label="Volume">
                    <option value="ml">ml</option>
                    <option value="tsp">tsp</option>
                    <option value="tbsp">tbsp</option>
                    <option value="cup">cup</option>
                    <option value="fl_oz">fl oz</option>
                    <option value="qt">quart</option>
                    <option value="gal">gallon</option>
                    <option value="l">liter</option>
                  </optgroup>
                  <optgroup label="Count">
                    <option value="each">each</option>
                    <option value="dozen">dozen</option>
                  </optgroup>
                </select>
                <div className="text-[10px] text-slate-500 mt-1">{t('ingredient.recipeUnitHelp')}</div>
              </div>
              <div>
                <FieldLabel>{t('ingredient.merma')}</FieldLabel>
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={draft.mermaPercent || 0}
                  onChange={(e) => set('mermaPercent', parseFloat(e.target.value) || 0)}
                  className={inputBase}
                />
                <div className="text-[10px] text-slate-500 mt-1">{t('ingredient.mermaHelp')}</div>
              </div>
            </div>

            {/* Live preview */}
            <div className="mt-4 pt-4 border-t border-teal-200">
              {!compatible ? (
                <div className="flex items-center gap-2 text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span>{t('migration.unitMismatch', { from: draft.purchaseQtyUnit, to: draft.recipeUnit })}</span>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <div className="text-[10px] font-semibold tracking-wider text-teal-700 uppercase">{t('ingredient.autoCalculated')}</div>
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="text-slate-600">{t('ingredient.costPer')} {draft.recipeUnit}</span>
                    <span className="font-mono font-bold text-slate-900">
                      {fmtCostPerUnit(perUnit, draft.recipeUnit)}
                    </span>
                  </div>
                  {(draft.mermaPercent || 0) > 0 && (
                    <div className="flex items-center justify-between text-[13px]">
                      <span className="text-amber-700">{t('ingredient.realCostShort')} ({t('ingredient.merma').split(' ')[0]})</span>
                      <span className="font-mono font-bold text-amber-700">
                        {fmtCostPerUnit(perUnitWithMerma, draft.recipeUnit)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <FieldLabel>{t('ingredient.notes')}</FieldLabel>
            <textarea
              value={draft.notes || ''}
              onChange={(e) => set('notes', e.target.value)}
              placeholder={t('ingredient.notesPlaceholder')}
              rows={2}
              className={`${inputBase} resize-none`}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white border-t border-slate-200 px-5 py-3 flex items-center justify-between gap-2 flex-shrink-0">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition"
          >
            {t('action.cancel')}
          </button>
          <button
            onClick={() => canSave && onSave(draft)}
            disabled={!canSave}
            className={`px-5 py-2.5 text-[13px] font-semibold text-white rounded-xl shadow-sm transition flex items-center gap-1.5 ${
              canSave
                ? 'bg-teal-600 hover:bg-teal-700 shadow-teal-600/30 hover:shadow-teal-600/50'
                : 'bg-slate-300 cursor-not-allowed'
            }`}
          >
            <CheckCircle2 className="w-4 h-4" />
            {isNew ? t('action.create') : t('action.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MIGRATION MATCHING ENGINE
// Maps recipe ingredients to Master List entries by name similarity.
// READ-ONLY for Step 1 — does not modify recipes.
// ============================================================

// Normalize for matching: lowercase, remove accents, common stop words
function normalizeForMatch(s) {
  if (!s) return '';
  return String(s)
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // strip accents
    .replace(/\b(de|el|la|los|las|con|sin|fresca?|fresco|molido|molida|en|tiras|granulado|granulada|crudo|cruda|natural|para freir|prorrateada?)\b/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Token-set similarity (Jaccard-like)
function nameSimilarity(a, b) {
  const ta = new Set(normalizeForMatch(a).split(' ').filter(Boolean));
  const tb = new Set(normalizeForMatch(b).split(' ').filter(Boolean));
  if (ta.size === 0 || tb.size === 0) return 0;
  let intersection = 0;
  ta.forEach((t) => { if (tb.has(t)) intersection++; });
  const union = ta.size + tb.size - intersection;
  return intersection / union;
}

// For each recipe ingredient, find the best Master List match.
// Returns: { score, ingredientId, confidence: 'exact' | 'high' | 'medium' | 'low' | 'none' }
function findBestMatch(recipeIng, masterList) {
  const candidates = masterList.map((m) => {
    const scoreEn = nameSimilarity(recipeIng.name, m.name);
    const scoreEs = nameSimilarity(recipeIng.name, m.nameEs || '');
    return { ing: m, score: Math.max(scoreEn, scoreEs) };
  });
  candidates.sort((a, b) => b.score - a.score);
  const best = candidates[0];
  if (!best || best.score === 0) return { score: 0, ingredientId: null, confidence: 'none' };

  let confidence = 'low';
  if (best.score >= 0.95) confidence = 'exact';
  else if (best.score >= 0.65) confidence = 'high';
  else if (best.score >= 0.4)  confidence = 'medium';
  else if (best.score >= 0.2)  confidence = 'low';
  else                         confidence = 'none';

  return { score: best.score, ingredientId: best.ing.id, confidence };
}

const CONFIDENCE_STYLES = {
  exact:  { bg: 'bg-emerald-50',  border: 'border-emerald-200',  text: 'text-emerald-700',  dot: 'bg-emerald-500',  labelKey: 'confidence.exact' },
  high:   { bg: 'bg-emerald-50',  border: 'border-emerald-200',  text: 'text-emerald-700',  dot: 'bg-emerald-500',  labelKey: 'confidence.high' },
  medium: { bg: 'bg-amber-50',    border: 'border-amber-200',    text: 'text-amber-700',    dot: 'bg-amber-500',    labelKey: 'confidence.medium' },
  low:    { bg: 'bg-rose-50',     border: 'border-rose-200',     text: 'text-rose-700',     dot: 'bg-rose-500',     labelKey: 'confidence.low' },
  none:   { bg: 'bg-slate-50',    border: 'border-slate-200',    text: 'text-slate-600',    dot: 'bg-slate-400',    labelKey: 'confidence.none' },
};

// ============================================================
// MIGRATION REVIEW SCREEN
// Full-screen overlay. Shows recipe-by-recipe ingredient mapping proposals.
// Does NOT modify recipes — user reviews and approves manually.
// ============================================================

function MigrationReview({ recipes, ingredients, onClose, onAddIngredient, onApply }) {
  const { t, lang } = useT();
  // Decisions: { [`${recipeId}::${ingredientIdx}`]: { action, ingredientId } }
  // action: 'accept' | 'change' | 'skip' | 'create'
  const [decisions, setDecisions] = useState(() => {
    const initial = {};
    recipes.forEach((r) => {
      r.ingredients.forEach((ing, idx) => {
        const match = findBestMatch(ing, ingredients);
        const key = `${r.id}::${idx}`;
        // Auto-accept exact/high; needs-review for medium/low/none
        const action = match.confidence === 'exact' || match.confidence === 'high'
          ? 'accept'
          : 'pending';
        const dec = { action, ingredientId: match.ingredientId, score: match.score, confidence: match.confidence };
        // If auto-accepted, default rename to Master English name
        if (action === 'accept' && match.ingredientId) {
          const matched = ingredients.find((i) => i.id === match.ingredientId);
          if (matched) dec.nameOverride = matched.name;
        }
        initial[key] = dec;
      });
    });
    return initial;
  });

  const [filterRecipeId, setFilterRecipeId] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, accepted, needs-review
  const [showConfirm, setShowConfirm] = useState(false);

  // Aggregate stats
  const stats = useMemo(() => {
    let total = 0, accepted = 0, pending = 0, skipped = 0, willCreate = 0, noMatch = 0;
    Object.values(decisions).forEach((d) => {
      total++;
      if (d.action === 'accept')  accepted++;
      else if (d.action === 'pending') pending++;
      else if (d.action === 'skip') skipped++;
      else if (d.action === 'create') willCreate++;
      if (d.confidence === 'none') noMatch++;
    });
    return { total, accepted, pending, skipped, willCreate, noMatch };
  }, [decisions]);

  const updateDecision = (key, partial) => {
    setDecisions((prev) => {
      const current = prev[key] || {};
      const next = { ...current, ...partial };
      // When accepting, default nameOverride to the Master English name
      // (only if user hasn't set a custom override yet)
      if (next.action === 'accept' && next.ingredientId && !next.nameOverride) {
        const matched = ingredients.find((i) => i.id === next.ingredientId);
        if (matched) next.nameOverride = matched.name;
      }
      // When un-accepting (skip/pending), clear the override so it doesn't persist
      if (next.action !== 'accept') {
        delete next.nameOverride;
      }
      return { ...prev, [key]: next };
    });
  };

  const filteredRecipes = filterRecipeId === 'all'
    ? recipes
    : recipes.filter((r) => r.id === filterRecipeId);

  // Filter function for individual rows
  const passesStatusFilter = (decision) => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'accepted') return decision.action === 'accept';
    if (filterStatus === 'pending')  return decision.action === 'pending';
    if (filterStatus === 'needs-review') return decision.confidence === 'medium' || decision.confidence === 'low' || decision.confidence === 'none';
    return true;
  };

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-stretch justify-center p-0 sm:p-4">
      <div className="bg-slate-50 w-full sm:max-w-7xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden max-h-screen sm:max-h-[95vh]">

        {/* HEADER */}
        <div className="bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center text-white shadow-md shadow-teal-600/30">
              <Layers className="w-5 h-5" strokeWidth={2.2} />
            </div>
            <div>
              <h2 className="text-[17px] sm:text-[19px] font-bold text-slate-900 leading-tight">{t('migration.title')}</h2>
              <p className="text-[12px] text-slate-500">{t('migration.subtitle')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* BACKUP NOTICE */}
        <div className="bg-amber-50 border-b border-amber-200 px-5 py-2.5">
          <div className="flex items-start gap-2 max-w-7xl mx-auto">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-[12px] text-amber-800 leading-snug">
              <strong>{t('migration.backupCreated')}</strong> {t('migration.backupNotice')}
            </div>
          </div>
        </div>

        {/* STATS BAR + PROGRESS + FOCUS */}
        <div className="bg-white border-b border-slate-200 px-5 py-3">
          {/* Progress bar (visual indication of completion) */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[11px] font-bold tracking-wider text-slate-600 uppercase">{t('migration.progress')}</div>
              <div className="text-[11px] text-slate-500">
                {t('migration.progressOf', { resolved: stats.total - stats.pending, total: stats.total })}
              </div>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden flex">
              <div
                className="bg-emerald-500 h-full transition-all duration-300"
                style={{ width: `${stats.total > 0 ? (stats.accepted / stats.total) * 100 : 0}%` }}
                title={`${stats.accepted} ${t('migration.accepted')}`}
              ></div>
              <div
                className="bg-slate-400 h-full transition-all duration-300"
                style={{ width: `${stats.total > 0 ? (stats.skipped / stats.total) * 100 : 0}%` }}
                title={`${stats.skipped} ${t('migration.skipped')}`}
              ></div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-[12px]">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                <span className="text-emerald-700 font-semibold">{stats.accepted}</span>
                <span className="text-slate-500">{t('migration.accepted')}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                <span className="text-amber-700 font-semibold">{stats.pending}</span>
                <span className="text-slate-500">{t('migration.pending')}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                <span className="text-slate-700 font-semibold">{stats.skipped}</span>
                <span className="text-slate-500">{t('migration.skipped')}</span>
              </div>
              {stats.willCreate > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                  <span className="text-blue-700 font-semibold">{stats.willCreate}</span>
                  <span className="text-slate-500">{t('migration.new')}</span>
                </div>
              )}
            </div>

            {/* Focus on pending button — appears when there are pendings AND not already filtering */}
            {stats.pending > 0 && filterStatus !== 'pending' && (
              <button
                onClick={() => setFilterStatus('pending')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg text-[12px] font-bold transition shadow-sm"
                title={t('migration.skipTooltip')}
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                {t('migration.focusPending', { n: stats.pending })}
              </button>
            )}
          </div>
        </div>

        {/* FILTERS */}
        <div className="bg-white border-b border-slate-200 px-5 py-3 flex flex-wrap items-center gap-2">
          <select
            value={filterRecipeId}
            onChange={(e) => setFilterRecipeId(e.target.value)}
            className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-[12px] text-slate-700 focus:outline-none focus:border-teal-500"
          >
            <option value="all">{t('migration.allRecipes', { n: recipes.length })}</option>
            {recipes.map((r) => (
              <option key={r.id} value={r.id}>{r.name} ({r.ingredients.length})</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-[12px] text-slate-700 focus:outline-none focus:border-teal-500"
          >
            <option value="all">{t('migration.allStatuses')}</option>
            <option value="accepted">{t('migration.acceptedOnly')}</option>
            <option value="pending">{t('migration.pendingOnly')}</option>
            <option value="needs-review">{t('migration.needsReview')}</option>
          </select>
        </div>

        {/* RECIPES + MAPPING ROWS */}
        <div className="flex-1 overflow-y-auto px-3 sm:px-5 py-4">
          {(() => {
            // Pre-calculate visible rows count to know if list is empty
            let visibleRows = 0;
            filteredRecipes.forEach((recipe) => {
              recipe.ingredients.forEach((ing, idx) => {
                const dec = decisions[`${recipe.id}::${idx}`];
                if (passesStatusFilter(dec)) visibleRows++;
              });
            });

            // All-resolved celebration when filtering pending and 0 left
            if (visibleRows === 0 && filterStatus === 'pending') {
              return (
                <div className="text-center py-16 px-6">
                  <div className="inline-flex w-16 h-16 rounded-full bg-emerald-100 items-center justify-center mb-4">
                    <CheckCircle2 className="w-9 h-9 text-emerald-600" strokeWidth={2.2} />
                  </div>
                  <h3 className="text-[20px] font-bold text-slate-900 mb-1">{t('migration.allDone.title')}</h3>
                  <p className="text-[13px] text-slate-500 mb-5 max-w-md mx-auto"
                     dangerouslySetInnerHTML={{ __html: t('migration.allDone.body') }} />
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    <button
                      onClick={() => setFilterStatus('all')}
                      className="px-4 py-2 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-lg text-[13px] font-semibold transition"
                    >
                      {t('migration.allDone.viewAll')}
                    </button>
                    <button
                      onClick={() => setFilterStatus('accepted')}
                      className="px-4 py-2 bg-white border border-slate-200 hover:border-slate-300 text-emerald-700 rounded-lg text-[13px] font-semibold transition"
                    >
                      {t('migration.allDone.reviewAccepted')}
                    </button>
                  </div>
                </div>
              );
            }

            // Empty for other filters
            if (visibleRows === 0) {
              return (
                <div className="text-center py-16 px-6">
                  <div className="text-[14px] text-slate-500">{t('migration.noMappingsFilter')}</div>
                  <button onClick={() => { setFilterStatus('all'); setFilterRecipeId('all'); }} className="mt-3 text-[12px] text-teal-700 hover:underline font-semibold">{t('migration.clearFilters')}</button>
                </div>
              );
            }

            return filteredRecipes.map((recipe) => {
              const rows = recipe.ingredients
                .map((ing, idx) => ({ ing, idx, key: `${recipe.id}::${idx}`, decision: decisions[`${recipe.id}::${idx}`] }))
                .filter(({ decision }) => passesStatusFilter(decision));
              if (rows.length === 0) return null;

              return (
                <div key={recipe.id} className="mb-6 bg-white rounded-2xl border border-slate-200 overflow-hidden">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <BookOpen className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <h3 className="font-bold text-slate-900 text-[14px] truncate">{recipe.name}</h3>
                      <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-md text-[10px] font-bold flex-shrink-0">{rows.length} {t('misc.of')} {recipe.ingredients.length}</span>
                    </div>
                    <span className="text-[11px] text-slate-500 flex-shrink-0">{recipe.category}</span>
                  </div>

                  <div className="divide-y divide-slate-100">
                    {rows.map(({ ing, idx, key, decision }) => (
                      <MigrationRow
                        key={key}
                        recipeIng={ing}
                        decision={decision}
                        ingredients={ingredients}
                        onUpdate={(partial) => updateDecision(key, partial)}
                        onAddIngredient={onAddIngredient}
                      />
                    ))}
                  </div>
                </div>
              );
            });
          })()}
        </div>

        {/* FOOTER */}
        <div className="bg-white border-t border-slate-200 px-5 py-3 sticky bottom-0 flex flex-wrap items-center justify-between gap-2">
          <div className="text-[12px] text-slate-500">
            {stats.pending > 0 ? (
              <>
                <strong className="text-amber-700">{t('migration.pendingFooter', { n: stats.pending })}</strong>{' '}
                {t('migration.pendingFooterAction')}
              </>
            ) : (
              <>
                <strong className="text-emerald-700">{t('migration.readyFooter')}</strong>{' '}
                {t('migration.readyFooterDetails', { accepted: stats.accepted, skipped: stats.skipped })}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition"
            >
              {t('action.close')}
            </button>
            <button
              onClick={() => stats.pending === 0 && setShowConfirm(true)}
              disabled={stats.pending > 0}
              title={stats.pending > 0 ? t('migration.pendingMustResolve', { n: stats.pending }) : t('migration.applyTooltip')}
              className={`px-5 py-2.5 text-[13px] font-semibold text-white rounded-xl flex items-center gap-1.5 transition ${
                stats.pending > 0
                  ? 'bg-slate-300 cursor-not-allowed'
                  : 'bg-teal-600 hover:bg-teal-700 shadow-sm shadow-teal-600/30 hover:shadow-teal-600/50'
              }`}
            >
              <CheckCircle2 className="w-4 h-4" />
              {t('migration.applyMigration')}
            </button>
          </div>
        </div>

        {/* CONFIRMATION MODAL */}
        {showConfirm && (
          <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
              <div className="p-5 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white shadow-md">
                    <AlertTriangle className="w-5 h-5" strokeWidth={2.2} />
                  </div>
                  <h3 className="text-[17px] font-bold text-slate-900">{t('migration.confirmTitle')}</h3>
                </div>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-[13px] text-slate-700 leading-relaxed"
                   dangerouslySetInnerHTML={{ __html: t('migration.confirmIntro', { n: stats.accepted, s: stats.accepted === 1 ? '' : (lang === 'es' ? 'es' : 's') }) }} />
                <div className="bg-slate-50 rounded-xl p-3 space-y-1.5 text-[12px]">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">{t('migration.confirmCounters.accepted')}</span>
                    <span className="font-bold text-emerald-700">{stats.accepted}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">{t('migration.confirmCounters.skipped')}</span>
                    <span className="font-bold text-slate-700">{stats.skipped}</span>
                  </div>
                  {stats.willCreate > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">{t('migration.confirmCounters.created')}</span>
                      <span className="font-bold text-blue-700">{stats.willCreate}</span>
                    </div>
                  )}
                </div>
                <p className="text-[12px] text-slate-600 leading-relaxed"
                   dangerouslySetInnerHTML={{ __html: t('migration.confirmHelp') }} />
              </div>
              <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-white rounded-lg transition"
                >
                  {t('action.cancel')}
                </button>
                <button
                  onClick={() => {
                    setShowConfirm(false);
                    onApply(decisions);
                  }}
                  className="px-5 py-2.5 text-[13px] font-semibold text-white bg-teal-600 hover:bg-teal-700 shadow-sm shadow-teal-600/30 rounded-xl flex items-center gap-1.5 transition"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {t('migration.confirmYes')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// One row per recipe ingredient → master list mapping
function MigrationRow({ recipeIng, decision, ingredients, onUpdate, onAddIngredient }) {
  const { t, lang } = useT();
  const [showPicker, setShowPicker] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');

  const matchedIng = decision.ingredientId ? ingredients.find((i) => i.id === decision.ingredientId) : null;
  const conf = CONFIDENCE_STYLES[decision.confidence] || CONFIDENCE_STYLES.none;
  const matchedDisplay = matchedIng ? bilingualName(matchedIng, lang) : null;

  // Calculate the "after" cost (what it'd cost using Master List entry)
  const recipeUnitNorm = normalizeUnit(recipeIng.unit);
  let afterUnitCost = null;
  let afterTotal = null;
  let converted = null;
  let unitsCompatible = true;

  if (matchedIng && decision.action !== 'skip') {
    const { perUnit, perUnitWithMerma } = calcIngredientCost(matchedIng);
    // Convert recipe qty into master list's recipe unit
    converted = convertUnits(recipeIng.qty, recipeUnitNorm, matchedIng.recipeUnit);
    if (converted == null) {
      unitsCompatible = false;
    } else {
      afterUnitCost = perUnitWithMerma;
      afterTotal = perUnitWithMerma * converted;
    }
  }

  const beforeTotal = recipeIng.totalCost || (recipeIng.qty * (recipeIng.unitCost || 0));
  const delta = afterTotal != null ? afterTotal - beforeTotal : null;
  const deltaPct = beforeTotal > 0 && delta != null ? (delta / beforeTotal) * 100 : null;

  // Create a new ingredient from this recipe ingredient and accept the migration
  const createNewFromRecipeIng = () => {
    const newId = `ing_${Date.now()}`;
    const newIng = {
      id: newId,
      name: recipeIng.name,
      nameEs: recipeIng.name,
      category: 'other',
      purchaseUnit: recipeUnitNorm,
      purchaseUnitLabel: recipeIng.unit || 'each',
      purchasePrice: recipeIng.unitCost || 0,
      purchaseQty: 1,
      purchaseQtyUnit: recipeUnitNorm,
      recipeUnit: recipeUnitNorm,
      mermaPercent: 0,
      notes: `Auto-created during migration from "${recipeIng.name}"`,
    };
    onAddIngredient(newIng);
    onUpdate({ ingredientId: newId, action: 'accept', confidence: 'exact' });
    setShowPicker(false);
  };

  const isNoMatch = decision.confidence === 'none' || !matchedIng;

  return (
    <div className="p-3 sm:p-4 hover:bg-slate-50/50 transition">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* COLUMN 1 — Recipe Ingredient (current) */}
        <div className="lg:col-span-4">
          <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase mb-1">{t('migration.currentInRecipe')}</div>
          <div className="font-semibold text-slate-900 text-[14px] leading-tight">{recipeIng.name}</div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500">
            <span className="font-mono text-slate-700 font-medium">{recipeIng.qty} {recipeIng.unit}</span>
            <span className="text-slate-300">·</span>
            <span className="font-mono">${(recipeIng.unitCost || 0).toFixed(2)}/{recipeIng.unit}</span>
            <span className="text-slate-300">·</span>
            <span className="font-mono font-bold text-slate-700">{t('recipe.totalCost')} ${beforeTotal.toFixed(2)}</span>
          </div>
        </div>

        {/* COLUMN 2 — Arrow + Confidence */}
        <div className="lg:col-span-1 flex lg:flex-col items-center justify-center gap-1">
          <ChevronRight className="w-5 h-5 text-slate-300" />
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wide ${conf.text} ${conf.bg}`}>
            <span className={`w-1 h-1 rounded-full ${conf.dot}`}></span>
            {t(conf.labelKey)}
          </span>
        </div>

        {/* COLUMN 3 — Master List Match (proposed) */}
        <div className="lg:col-span-4">
          <div className="text-[10px] font-bold tracking-wider text-teal-700 uppercase mb-1">{t('migration.masterListMatch')}</div>
          {decision.action === 'skip' ? (
            <div className="text-[13px] text-slate-400 italic">{t('migration.skipped.kept')}</div>
          ) : matchedIng ? (
            <>
              <div className="font-semibold text-slate-900 text-[14px] leading-tight">
                {matchedDisplay.primary}
                {matchedDisplay.secondary && (
                  <span className="text-slate-400 font-normal text-[11px]"> · {matchedDisplay.secondary}</span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500">
                {!unitsCompatible ? (
                  <span className="text-rose-700 font-semibold">{t('migration.unitMismatch', { from: recipeUnitNorm, to: matchedIng.recipeUnit })}</span>
                ) : (
                  <>
                    <span className="font-mono">{converted != null ? `${converted.toFixed(2)} ${matchedIng.recipeUnit}` : '—'}</span>
                    <span className="text-slate-300">·</span>
                    <span className="font-mono">{fmtCostPerUnit(afterUnitCost || 0, matchedIng.recipeUnit)}</span>
                    {(matchedIng.mermaPercent || 0) > 0 && (
                      <>
                        <span className="text-slate-300">·</span>
                        <span className="text-amber-700 font-semibold">{t('migration.inclMerma', { n: matchedIng.mermaPercent })}</span>
                      </>
                    )}
                    <span className="text-slate-300">·</span>
                    <span className="font-mono font-bold text-slate-700">{t('recipe.totalCost')} ${afterTotal != null ? afterTotal.toFixed(2) : '—'}</span>
                  </>
                )}
              </div>

              {/* RENAME CHIP — show when accepted */}
              {decision.action === 'accept' && (
                <div className="mt-2">
                  {editingName ? (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] font-bold text-slate-500 uppercase">{t('migration.renameToHeader')}</span>
                      <input
                        type="text"
                        autoFocus
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const trimmed = nameInput.trim();
                            if (trimmed) {
                              onUpdate({ nameOverride: trimmed });
                              setEditingName(false);
                            }
                          } else if (e.key === 'Escape') {
                            setEditingName(false);
                          }
                        }}
                        className="px-2 py-1 bg-white border border-teal-300 rounded text-[12px] text-slate-900 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/15 max-w-[180px]"
                      />
                      <button
                        onClick={() => {
                          const trimmed = nameInput.trim();
                          if (trimmed) {
                            onUpdate({ nameOverride: trimmed });
                            setEditingName(false);
                          }
                        }}
                        className="px-2 py-1 bg-teal-600 hover:bg-teal-700 text-white rounded text-[10px] font-bold uppercase transition"
                      >
                        {t('action.save')}
                      </button>
                      <button
                        onClick={() => setEditingName(false)}
                        className="px-2 py-1 bg-white border border-slate-200 text-slate-600 rounded text-[10px] font-bold uppercase transition"
                      >
                        {t('action.cancel')}
                      </button>
                      {/* Quick presets */}
                      <div className="flex items-center gap-1 mt-1 sm:mt-0 w-full sm:w-auto">
                        <span className="text-[10px] text-slate-400">{t('migration.quickPresets')}</span>
                        <button
                          onClick={() => setNameInput(matchedIng.name)}
                          className="px-1.5 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-[10px] font-semibold transition"
                          title={t('migration.useMasterEn')}
                        >{t('migration.quickEN')}</button>
                        {matchedIng.nameEs && (
                          <button
                            onClick={() => setNameInput(matchedIng.nameEs)}
                            className="px-1.5 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-[10px] font-semibold transition"
                            title={t('migration.useMasterEs')}
                          >{t('migration.quickES')}</button>
                        )}
                        <button
                          onClick={() => setNameInput(recipeIng.name)}
                          className="px-1.5 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-[10px] font-semibold transition"
                          title={t('migration.useOriginal')}
                        >{t('migration.quickOrig')}</button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setNameInput(decision.nameOverride || matchedIng.name);
                        setEditingName(true);
                      }}
                      className="inline-flex items-center gap-1.5 px-2 py-1 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-md text-[11px] text-teal-800 transition group/rename"
                      title={t('migration.useMasterEn')}
                    >
                      <span className="text-[9px] font-bold text-teal-600 uppercase tracking-wide">{t('migration.renamesTo')}</span>
                      <span className="font-semibold">"{decision.nameOverride || matchedIng.name}"</span>
                      <span className="text-teal-500 opacity-60 group-hover/rename:opacity-100 transition">✏️</span>
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-1">
              <div className="text-[13px] text-rose-700 font-semibold">{t('migration.noMatchFound')}</div>
              <div className="text-[11px] text-slate-500 leading-snug"
                   dangerouslySetInnerHTML={{ __html: t('migration.noMatchHelp', { name: recipeIng.name }) }} />
            </div>
          )}
        </div>

        {/* COLUMN 4 — Cost Delta */}
        <div className="lg:col-span-1 flex flex-col items-end justify-center text-right">
          <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase mb-1">{t('migration.diff')}</div>
          {delta != null && unitsCompatible ? (
            <>
              <div className={`font-mono font-bold text-[13px] ${delta > 0.01 ? 'text-rose-700' : delta < -0.01 ? 'text-emerald-700' : 'text-slate-500'}`}>
                {delta > 0 ? '+' : ''}${delta.toFixed(2)}
              </div>
              {deltaPct != null && Math.abs(deltaPct) > 0.5 && (
                <div className={`text-[10px] ${delta > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {deltaPct > 0 ? '+' : ''}{deltaPct.toFixed(1)}%
                </div>
              )}
            </>
          ) : (
            <span className="text-slate-300 text-[12px]">—</span>
          )}
        </div>

        {/* COLUMN 5 — Actions */}
        <div className="lg:col-span-2 flex flex-wrap items-center justify-end gap-1.5">
          {decision.action === 'accept' ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-md text-[10px] font-bold uppercase tracking-wide">
              <CheckCircle2 className="w-3 h-3" /> {t('migration.acceptedBadge')}
            </span>
          ) : decision.action === 'skip' ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 rounded-md text-[10px] font-bold uppercase tracking-wide">
              {t('migration.skippedBadge')}
            </span>
          ) : null}

          {/* Direct Create New — always visible when pending */}
          {decision.action !== 'accept' && decision.action !== 'skip' && (
            <button
              onClick={createNewFromRecipeIng}
              className="px-2.5 py-1 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-[10px] font-bold uppercase tracking-wide transition flex items-center gap-1 shadow-sm"
              title={t('migration.createNewTitle', { name: recipeIng.name })}
            >
              <Plus className="w-3 h-3" strokeWidth={3} />
              {t('migration.createNew')}
            </button>
          )}

          {decision.action !== 'accept' && matchedIng && unitsCompatible && (
            <button
              onClick={() => onUpdate({ action: 'accept' })}
              className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-[10px] font-bold uppercase tracking-wide transition"
            >
              {t('action.accept')}
            </button>
          )}

          <button
            onClick={() => setShowPicker(!showPicker)}
            className="px-2 py-1 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-md text-[10px] font-bold uppercase tracking-wide transition"
          >
            {isNoMatch ? t('action.search') : t('action.change')}
          </button>

          {decision.action !== 'skip' && (
            <button
              onClick={() => onUpdate({ action: 'skip' })}
              className="px-2 py-1 bg-white border border-slate-200 hover:border-slate-300 text-slate-600 rounded-md text-[10px] font-bold uppercase tracking-wide transition"
              title={t('migration.skipTooltip')}
            >
              {t('action.skip')}
            </button>
          )}
        </div>
      </div>

      {/* Picker drawer */}
      {showPicker && (
        <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-xl">
          <div className="text-[11px] font-bold tracking-wider text-slate-600 uppercase mb-2">
            {t('migration.searchOrCreate')}
          </div>
          <IngredientPicker
            value={decision.ingredientId}
            ingredients={ingredients}
            recipeIngName={recipeIng.name}
            onPick={(id) => {
              onUpdate({ ingredientId: id, action: 'accept', confidence: 'high' });
              setShowPicker(false);
            }}
            onCreateNew={createNewFromRecipeIng}
          />
        </div>
      )}
    </div>
  );
}

// Searchable dropdown for picking a Master List ingredient
function IngredientPicker({ value, ingredients, onPick, onCreateNew, recipeIngName }) {
  const { t, lang } = useT();
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    if (!ql) return ingredients.slice(0, 30);
    return ingredients.filter((i) =>
      i.name.toLowerCase().includes(ql) ||
      (i.nameEs || '').toLowerCase().includes(ql)
    ).slice(0, 30);
  }, [q, ingredients]);

  const createLabel = recipeIngName ? t('migration.createAsNew', { name: recipeIngName }) : t('migration.createGeneric');

  return (
    <div>
      {/* Create New — promoted to the top so it's the obvious action */}
      <button
        onClick={onCreateNew}
        className="mb-3 w-full px-3 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-[13px] font-bold transition flex items-center justify-center gap-2 shadow-sm shadow-teal-600/30"
      >
        <Plus className="w-4 h-4" strokeWidth={2.5} />
        {createLabel}
      </button>

      {/* OR divider */}
      <div className="flex items-center gap-2 my-2">
        <div className="flex-1 h-px bg-slate-200"></div>
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t('migration.orPickExisting')}</span>
        <div className="flex-1 h-px bg-slate-200"></div>
      </div>

      <div className="relative mb-2">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('migration.searchMasterList')}
          className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/15"
        />
      </div>
      <div className="max-h-48 overflow-y-auto space-y-1">
        {filtered.map((i) => {
          const cat = INGREDIENT_CATEGORIES.find((c) => c.id === i.category);
          const display = bilingualName(i, lang);
          return (
            <button
              key={i.id}
              onClick={() => onPick(i.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-[12px] transition flex items-center gap-2 ${
                value === i.id
                  ? 'bg-teal-50 border border-teal-300'
                  : 'bg-white border border-slate-100 hover:border-slate-300'
              }`}
            >
              <span className="text-base">{cat?.emoji || '📦'}</span>
              <span className="font-semibold text-slate-900 flex-1 truncate">{display.primary}</span>
              <span className="font-mono text-[10px] text-slate-500">${i.purchasePrice.toFixed(2)}/{i.purchaseUnitLabel}</span>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center text-[12px] text-slate-500 py-4 px-3 bg-slate-50 rounded-lg border border-dashed border-slate-200">
            <div className="font-semibold mb-1">{t('migration.noMatchesFor', { q })}</div>
            <div className="text-[11px] text-slate-400">{t('migration.useGreenButton')}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function AnalyticsTab({ recipes }) {
  const { t, lang } = useT();
  const calcs = recipes.map((r) => ({ r, c: calculateRecipe(r) })).filter((x) => x.c.currentPrice > 0);
  const sorted = [...calcs].sort((a, b) => b.c.actualMargin - a.c.actualMargin);

  const [scenarioId, setScenarioId] = useState('plus1');
  const [customAmount, setCustomAmount] = useState(1.50);
  const [customMode, setCustomMode] = useState('abs'); // 'abs' or 'pct'
  const [showPdf, setShowPdf] = useState(false);

  const applyScenario = (currentPrice) => {
    if (scenarioId === 'custom') {
      return customMode === 'abs'
        ? currentPrice + Number(customAmount || 0)
        : currentPrice * (1 + Number(customAmount || 0) / 100);
    }
    const s = PRICE_SCENARIOS.find((x) => x.id === scenarioId);
    if (!s) return currentPrice;
    return s.type === 'abs' ? currentPrice + s.value : currentPrice * (1 + s.value);
  };

  const scenarioResults = calcs.map(({ r, c }) => {
    const newPrice = applyScenario(c.currentPrice);
    return { r, c, result: evaluateScenario(c, newPrice) };
  });

  // Totals for selected scenario
  const totalAddedProfit = scenarioResults.reduce((s, x) => s + x.result.profitDelta, 0);
  const recommendedCount = scenarioResults.filter((x) => x.result.verdict === 'recommended').length;
  const riskyCount = scenarioResults.filter((x) => x.result.verdict === 'risky').length;
  const notRecommendedCount = scenarioResults.filter((x) => x.result.verdict === 'not_recommended').length;

  // KPI calculations
  const avgMargin = calcs.length > 0
    ? calcs.reduce((s, x) => s + x.c.actualMargin, 0) / calcs.length
    : 0;
  const losingMoney = calcs.filter((x) => x.c.healthStatus === 'losing');
  const tightMargin = calcs.filter((x) => x.c.healthStatus === 'tight');
  const healthyRecipes = calcs.filter((x) => x.c.healthStatus === 'healthy');
  const topProfit = calcs.length > 0
    ? [...calcs].sort((a, b) => b.c.profitPerUnit - a.c.profitPerUnit)[0]
    : null;
  const totalProfitPotential = calcs.reduce((s, x) => s + x.c.profitPerUnit, 0);

  // Alerts — ordered by urgency
  const alerts = [];
  losingMoney.forEach(({ r, c }) => {
    alerts.push({
      severity: 'critical',
      icon: '🔴',
      title: r.name,
      message: t('analytics.alert.losing', { m: c.actualMargin.toFixed(1) }),
      detail: t('analytics.alert.losingDetail', { sign: c.profitPerUnit < 0 ? '-' : '', amt: Math.abs(c.profitPerUnit).toFixed(2) }),
    });
  });
  tightMargin.forEach(({ r, c }) => {
    alerts.push({
      severity: 'warning',
      icon: '🟡',
      title: r.name,
      message: t('analytics.alert.tight', { m: c.actualMargin.toFixed(1) }),
      detail: t('analytics.alert.tightDetail', { amt: c.profitPerUnit.toFixed(2) }),
    });
  });
  // Recipes without a current price set
  const noPrice = recipes.filter((r) => {
    const c = calculateRecipe(r);
    return c.realCostPerUnit > 0 && (!c.currentPrice || c.currentPrice === 0);
  });
  noPrice.forEach((r) => {
    alerts.push({
      severity: 'info',
      icon: '🔵',
      title: r.name,
      message: t('analytics.alert.noPrice'),
      detail: t('analytics.alert.noPriceDetail'),
    });
  });

  return (
    <div>
      <ModuleHero
        module="analytics"
        rightSlot={
          <button
            onClick={() => setShowPdf(true)}
            className="flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2.5 bg-white text-slate-700 border border-slate-200 hover:border-slate-300 text-[13px] sm:text-[14px] font-semibold rounded-xl transition"
            title={t('pdf.printGenerate')}
          >
            <Download className="w-4 h-4" strokeWidth={2.2} />
            <span className="hidden sm:inline">{t('action.download')}</span>
            <span className="sm:hidden">{t('action.download')}</span>
          </button>
        }
      />

      {showPdf && (
        <PDFPreviewModal
          docSpec={buildAnalyticsDocSpec(recipes, t)}
          onClose={() => setShowPdf(false)}
        />
      )}

      {calcs.length === 0 && noPrice.length === 0 ? (
        <Card>
          <div className="p-12 text-center">
            <BarChart3 className="w-10 h-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
            <div className="text-[14px] font-semibold text-slate-700">{t('analytics.noneYet')}</div>
            <div className="text-[13px] text-slate-500 mt-1">{t('analytics.addPrices')}</div>
          </div>
        </Card>
      ) : (
        <>
          {/* KPI CARDS — Business overview */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            {/* KPI 1 — Avg Margin */}
            <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
              <div className="flex items-center gap-1.5 mb-2">
                <TrendingUp className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-[10px] font-bold tracking-wider text-slate-500 uppercase">{t('analytics.avgMargin')}</span>
              </div>
              <div className={`text-[26px] font-bold leading-none ${
                avgMargin >= 35 ? 'text-emerald-600' :
                avgMargin >= 25 ? 'text-amber-600' :
                'text-rose-600'
              }`} style={{ fontFamily: tokens.fontMono }}>
                {avgMargin.toFixed(1)}<span className="text-[16px]">%</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                {t('analytics.acrossPriced', { n: calcs.length, label: calcs.length === 1 ? t('analytics.recipe') : t('analytics.recipeP') })}
              </div>
            </div>

            {/* KPI 2 — Total recipes / health breakdown */}
            <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
              <div className="flex items-center gap-1.5 mb-2">
                <BookOpen className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-[10px] font-bold tracking-wider text-slate-500 uppercase">{t('analytics.recipes')}</span>
              </div>
              <div className="text-[26px] font-bold text-slate-900 leading-none" style={{ fontFamily: tokens.fontMono }}>
                {recipes.length}
              </div>
              <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1.5 flex-wrap">
                <span className="text-emerald-600 font-semibold">{t('analytics.healthy', { n: healthyRecipes.length })}</span>
                {tightMargin.length > 0 && <><span className="text-slate-300">·</span><span className="text-amber-600 font-semibold">{t('analytics.tight', { n: tightMargin.length })}</span></>}
              </div>
            </div>

            {/* KPI 3 — Items Losing Money */}
            <div className={`rounded-2xl border p-4 shadow-sm ${
              losingMoney.length > 0
                ? 'bg-rose-50 border-rose-200'
                : 'bg-white border-slate-200'
            }`}>
              <div className="flex items-center gap-1.5 mb-2">
                <AlertTriangle className={`w-3.5 h-3.5 ${losingMoney.length > 0 ? 'text-rose-500' : 'text-slate-400'}`} />
                <span className={`text-[10px] font-bold tracking-wider uppercase ${
                  losingMoney.length > 0 ? 'text-rose-700' : 'text-slate-500'
                }`}>{t('analytics.losingMoney')}</span>
              </div>
              <div className={`text-[26px] font-bold leading-none ${
                losingMoney.length > 0 ? 'text-rose-700' : 'text-slate-900'
              }`} style={{ fontFamily: tokens.fontMono }}>
                {losingMoney.length}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                {losingMoney.length === 0 ? t('analytics.noIssues') : t('analytics.marginBelow')}
              </div>
            </div>

            {/* KPI 4 — Top Profit Maker */}
            <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
              <div className="flex items-center gap-1.5 mb-2">
                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-[10px] font-bold tracking-wider text-slate-500 uppercase">{t('analytics.topEarner')}</span>
              </div>
              {topProfit && topProfit.c.profitPerUnit > 0 ? (
                <>
                  <div className="text-[16px] font-bold text-slate-900 leading-tight truncate">
                    {topProfit.r.name}
                  </div>
                  <div className="text-[11px] text-emerald-700 font-semibold mt-1" style={{ fontFamily: tokens.fontMono }}>
                    +${topProfit.c.profitPerUnit.toFixed(2)}/unit · {topProfit.c.actualMargin.toFixed(1)}%
                  </div>
                </>
              ) : (
                <div className="text-[13px] text-slate-400 italic">{t('analytics.noData')}</div>
              )}
            </div>
          </div>

          {/* ALERTS PANEL — Needs Attention */}
          {alerts.length > 0 && (
            <Card className="mb-5">
              <div className="p-4 sm:p-5 pb-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-rose-100 flex items-center justify-center">
                    <AlertTriangle className="w-4 h-4 text-rose-600" strokeWidth={2.2} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-[14px] font-bold text-slate-900">{t('analytics.needsAttention')}</h3>
                    <p className="text-[11px] text-slate-500">{alerts.length === 1 ? t('analytics.itemRequires', { n: alerts.length }) : t('analytics.itemsRequire', { n: alerts.length })}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide ${
                    losingMoney.length > 0
                      ? 'bg-rose-100 text-rose-700'
                      : tightMargin.length > 0
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {losingMoney.length > 0 ? t('analytics.critical') : tightMargin.length > 0 ? t('analytics.warning') : t('analytics.info')}
                  </span>
                </div>
              </div>
              <div className="px-4 sm:px-5 pb-4 sm:pb-5 space-y-2">
                {alerts.slice(0, 6).map((alert, idx) => (
                  <div
                    key={idx}
                    className={`flex items-start gap-2.5 p-3 rounded-xl border ${
                      alert.severity === 'critical' ? 'bg-rose-50/60 border-rose-200' :
                      alert.severity === 'warning' ? 'bg-amber-50/60 border-amber-200' :
                      'bg-blue-50/60 border-blue-200'
                    }`}
                  >
                    <span className="text-base flex-shrink-0 leading-none mt-0.5">{alert.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 text-[13px] truncate">{alert.title}</div>
                      <div className="text-[12px] text-slate-600 mt-0.5">{alert.message}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5 font-mono">{alert.detail}</div>
                    </div>
                  </div>
                ))}
                {alerts.length > 6 && (
                  <div className="text-[12px] text-slate-500 text-center pt-2">
                    {alerts.length - 6 === 1
                      ? t('analytics.moreAlert', { n: alerts.length - 6 })
                      : t('analytics.moreAlerts', { n: alerts.length - 6 })}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* All-clear celebration if no alerts */}
          {alerts.length === 0 && calcs.length > 0 && (
            <Card className="mb-5">
              <div className="p-4 flex items-center gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" strokeWidth={2.2} />
                </div>
                <div>
                  <div className="font-bold text-slate-900 text-[14px]">{t('analytics.allHealthy')}</div>
                  <div className="text-[12px] text-slate-500">{t('analytics.allHealthyBody')}</div>
                </div>
              </div>
            </Card>
          )}

          {/* A1 · MARGIN RANKING */}
          <Card className="mb-5">
            <SectionHeader number="A1" title={t('analytics.marginRanking')} subtitle={lang === 'es' ? 'Recetas ordenadas por margen actual — de mayor a menor' : 'Recipes sorted by actual margin — highest to lowest'} />
            <div className="px-4 sm:px-5 pb-4 sm:pb-5 space-y-2">
              {sorted.map(({ r, c }, idx) => {
                const h = HEALTH_TONE[c.healthStatus];
                const maxMargin = Math.max(...sorted.map((x) => x.c.actualMargin));
                const barWidth = maxMargin > 0 ? Math.max(2, (c.actualMargin / maxMargin) * 100) : 0;
                return (
                  <div key={r.id} className="flex items-center gap-3">
                    <div className="w-6 text-[11px] font-bold text-slate-400 text-right flex-shrink-0" style={{ fontFamily: tokens.fontMono }}>
                      #{idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[13px] font-semibold text-slate-900 truncate">{r.name}</span>
                        <span className={`text-[13px] font-bold ${h.text}`} style={{ fontFamily: tokens.fontMono }}>
                          {pct(c.actualMargin)}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            c.healthStatus === 'healthy' ? 'bg-emerald-500' :
                            c.healthStatus === 'tight' ? 'bg-amber-500' :
                            'bg-rose-500'
                          }`}
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* A2 · PRICE OPTIMIZATION (replaces Margin Optimization) */}
          <Card className="mb-5">
            <SectionHeader
              number="A2"
              title={t('analytics.priceOptimization')}
              subtitle={lang === 'es' ? 'Simula aumentos de precio y ve recomendaciones por receta' : 'Simulate price increases and see per-recipe recommendations'}
            />

            <div className="px-4 sm:px-5 pb-4 sm:pb-5">
              {/* Scenario selector */}
              <div className="mb-5">
                <FieldLabel>{lang === 'es' ? 'Escenario de Aumento' : 'Price Increase Scenario'}</FieldLabel>
                <div className="flex flex-wrap gap-2">
                  {PRICE_SCENARIOS.map((s) => {
                    const isActive = scenarioId === s.id;
                    return (
                      <button
                        key={s.id}
                        onClick={() => setScenarioId(s.id)}
                        className={`px-4 py-2 rounded-xl text-[13px] font-semibold transition ${
                          isActive
                            ? 'bg-blue-600 text-white shadow-sm shadow-blue-500/20'
                            : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>

                {/* Custom input */}
                {scenarioId === 'custom' && (
                  <div className="mt-3 p-4 rounded-xl bg-blue-50/50 border border-blue-100 flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                    <div className="flex-1 w-full">
                      <FieldLabel>Custom Amount</FieldLabel>
                      <div className="flex items-center gap-2">
                        <div className="flex bg-white border border-slate-200 rounded-xl p-1">
                          <button
                            onClick={() => setCustomMode('abs')}
                            className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition ${
                              customMode === 'abs' ? 'bg-blue-600 text-white' : 'text-slate-600'
                            }`}
                          >
                            $
                          </button>
                          <button
                            onClick={() => setCustomMode('pct')}
                            className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition ${
                              customMode === 'pct' ? 'bg-blue-600 text-white' : 'text-slate-600'
                            }`}
                          >
                            %
                          </button>
                        </div>
                        <NumberInput value={customAmount} onChange={setCustomAmount} suffix={customMode === 'pct' ? '%' : undefined} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Scenario summary bar */}
              <div className="mb-5 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3.5 rounded-xl bg-emerald-50 border border-emerald-200">
                  <div className="text-[10px] font-semibold tracking-wider text-emerald-700 uppercase">Recommended</div>
                  <div className="text-2xl font-bold text-emerald-700 mt-0.5" style={{ fontFamily: tokens.fontMono }}>
                    {recommendedCount}
                  </div>
                </div>
                <div className="p-3.5 rounded-xl bg-violet-50 border border-violet-200">
                  <div className="text-[10px] font-semibold tracking-wider text-violet-700 uppercase">Risky</div>
                  <div className="text-2xl font-bold text-violet-700 mt-0.5" style={{ fontFamily: tokens.fontMono }}>
                    {riskyCount}
                  </div>
                </div>
                <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200">
                  <div className="text-[10px] font-semibold tracking-wider text-rose-700 uppercase">Not Recommended</div>
                  <div className="text-2xl font-bold text-rose-700 mt-0.5" style={{ fontFamily: tokens.fontMono }}>
                    {notRecommendedCount}
                  </div>
                </div>
                <div className="p-3.5 rounded-xl bg-slate-900 text-white min-w-0">
                  <div className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase">Total Profit Gain</div>
                  <div className="text-xl sm:text-2xl font-bold mt-0.5 break-words" style={{ fontFamily: tokens.fontMono }}>
                    {totalAddedProfit >= 0 ? '+' : ''}{money(totalAddedProfit)}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">per unit, summed</div>
                </div>
              </div>

              {/* Per-recipe scenario cards */}
              <div className="space-y-2.5">
                {scenarioResults.map(({ r, c, result }) => (
                  <ScenarioResultRow key={r.id} recipe={r} calc={c} result={result} />
                ))}
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// Scenario result row — per recipe, shows new price + verdict
function ScenarioResultRow({ recipe, calc, result }) {
  const toneMap = {
    emerald: { bg: 'bg-emerald-50', ring: 'ring-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700' },
    amber:   { bg: 'bg-amber-50',   ring: 'ring-amber-200',   text: 'text-amber-800',   dot: 'bg-amber-500',   badge: 'bg-amber-100 text-amber-800' },
    violet:  { bg: 'bg-violet-50',  ring: 'ring-violet-200',  text: 'text-violet-700',  dot: 'bg-violet-500',  badge: 'bg-violet-100 text-violet-700' },
    rose:    { bg: 'bg-rose-50',    ring: 'ring-rose-200',    text: 'text-rose-700',    dot: 'bg-rose-500',    badge: 'bg-rose-100 text-rose-700' },
  };
  const t = toneMap[result.tone];
  const safeToShowProfit = result.verdict !== 'not_recommended';

  return (
    <div className={`p-4 rounded-xl ring-1 ${t.bg} ${t.ring}`}>
      <div className="flex items-start justify-between gap-3 mb-2.5">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${t.dot}`}></span>
          <div className="min-w-0">
            <div className="text-[14px] font-semibold text-slate-900 truncate">{recipe.name}</div>
            <div className="text-[11px] text-slate-500">Current {money(calc.currentPrice)} · {pct(calc.actualMargin)} margin</div>
          </div>
        </div>
        <span className={`inline-flex items-center px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide ${t.badge} flex-shrink-0`}>
          {result.label}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-200/50">
        <div>
          <div className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase">New Price</div>
          <div className="text-[15px] font-bold text-slate-900 mt-0.5" style={{ fontFamily: tokens.fontMono }}>
            {money(result.newPrice)}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase">New Margin</div>
          <div className={`text-[15px] font-bold mt-0.5 ${t.text}`} style={{ fontFamily: tokens.fontMono }}>
            {pct(result.newMargin)}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase">Profit Impact</div>
          {safeToShowProfit ? (
            <div className={`text-[15px] font-bold mt-0.5 ${result.profitDelta >= 0 ? 'text-emerald-700' : 'text-slate-400'}`} style={{ fontFamily: tokens.fontMono }}>
              {result.profitDelta >= 0 ? '+' : ''}{money(result.profitDelta)}
            </div>
          ) : (
            <div className="text-[12px] text-slate-400 mt-1 italic">—</div>
          )}
        </div>
      </div>

      <div className={`mt-2.5 text-[12px] ${t.text}`}>
        {result.reason}
      </div>
    </div>
  );
}


// ============================================================
// PREMIUM PRIMITIVES
// ============================================================
function Card({ children, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl border border-slate-200 shadow-sm shadow-slate-900/[0.02] ${className}`}>
      {children}
    </div>
  );
}

function SectionHeader({ number, title, subtitle, action, inline }) {
  return (
    <div className={`${inline ? '' : 'px-4 sm:px-5 pt-4 sm:pt-5 pb-3'} flex items-start justify-between gap-3`}>
      <div className="flex items-start gap-3">
        {number && (
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center text-[11px] font-bold tracking-wide">
            {number}
          </div>
        )}
        <div>
          <h3 className="text-lg font-bold text-slate-900 tracking-tight">{title}</h3>
          <p className="text-[12px] text-slate-500 mt-0.5">{subtitle}</p>
        </div>
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}

function FieldLabel({ children, className = '' }) {
  return (
    <div className={`text-[11px] font-semibold tracking-wide text-slate-500 mb-1.5 ${className}`}>
      {children}
    </div>
  );
}

const inputBase = 'w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-[14px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 hover:border-slate-300 transition-all';
const inputSm = 'w-full px-2.5 py-2 bg-white border border-slate-200 rounded-lg text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 hover:border-slate-300 transition-all';
const inputXl = 'w-full px-3.5 py-3 bg-white border border-slate-200 rounded-xl text-xl sm:text-2xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 hover:border-slate-300 transition-all';

function Input({ value, onChange, placeholder, size, className = '' }) {
  const cls = size === 'xl' ? inputXl : size === 'sm' ? inputSm : inputBase;
  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`${cls} ${className}`}
    />
  );
}

function Textarea({ value, onChange, placeholder }) {
  return (
    <textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={2}
      className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-[13px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 hover:border-slate-300 resize-none transition-all"
    />
  );
}

function NumberInput({ value, onChange, suffix, size, align = 'left' }) {
  const cls = size === 'sm' ? inputSm : inputBase;
  const display = value === 0 || value === undefined || value === null ? '' : value;
  return (
    <div className="relative">
      <input
        type="number" step="any"
        value={display}
        onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
        placeholder="0"
        className={`${cls} ${align === 'right' ? 'text-right pr-3' : ''} ${suffix ? 'pr-10' : ''}`}
        style={{ fontFamily: tokens.fontMono }}
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] font-medium text-slate-400 pointer-events-none">
          {suffix}
        </span>
      )}
    </div>
  );
}

function CurrencyInput({ value, onChange, size, emphasis }) {
  const cls = size === 'sm' ? inputSm : inputBase;
  const display = value === 0 || value === undefined || value === null ? '' : value;
  return (
    <div className="relative">
      <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-[13px] font-semibold ${emphasis ? 'text-slate-900' : 'text-slate-400'} pointer-events-none`}>$</span>
      <input
        type="number" step="0.01"
        value={display}
        onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
        placeholder="0.00"
        className={`${cls} pl-7 text-right ${emphasis ? 'font-semibold text-slate-900' : ''}`}
        style={{ fontFamily: tokens.fontMono }}
      />
    </div>
  );
}

// Auto-calculated unit cost — read-only display (Total Cost ÷ Quantity)
function ComputedUnitCost({ value, unit }) {
  return (
    <div
      className="flex-1 px-2.5 py-2 bg-slate-50 border border-slate-100 rounded-lg flex items-baseline justify-end gap-1"
      title="Auto-calculated: Total Cost ÷ Quantity"
    >
      <span className="text-[13px] font-semibold text-slate-500" style={{ fontFamily: tokens.fontMono }}>
        {money(value)}
      </span>
      {unit && <span className="text-[10px] text-slate-400 truncate max-w-[60px]">/ {unit}</span>}
    </div>
  );
}

// Production Yield — replaces confusing "Ready to Sell" with a clear two-step flow:
// "Raw input → Ready to sell" with auto-calculated yield % and merma
function ProductionYieldEditor({ rawQty, rawUnit, sellableQty, sellableUnit, onRawQtyChange, onRawUnitChange, onSellableQtyChange, onSellableUnitChange }) {
  const raw = Number(rawQty) || 0;
  const sellable = Number(sellableQty) || 0;
  const yieldPct = raw > 0 ? (sellable / raw) * 100 : 0;
  const merma = raw > sellable ? raw - sellable : 0;
  const sameUnit = rawUnit && sellableUnit && rawUnit.toLowerCase().trim() === sellableUnit.toLowerCase().trim();

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 pt-3.5 pb-2 bg-slate-50/60 border-b border-slate-100">
        <div className="text-[10px] font-bold tracking-wider text-slate-500 uppercase">Production Yield</div>
        <div className="text-[11px] text-slate-500 mt-0.5">From raw input to sellable output</div>
      </div>

      <div className="p-4 space-y-3">
        {/* Step 1 — Raw input */}
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="w-5 h-5 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center text-[10px] font-bold">1</span>
            <span className="text-[12px] font-semibold text-slate-700">Raw input</span>
            <span className="text-[10px] text-slate-400">— what you start with</span>
          </div>
          <YieldRow
            qty={rawQty}
            unit={rawUnit}
            onQtyChange={onRawQtyChange}
            onUnitChange={onRawUnitChange}
            placeholder="0"
          />
        </div>

        {/* Arrow connector */}
        <div className="flex justify-center -my-1">
          <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center">
            <svg className="w-3.5 h-3.5 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <polyline points="19 12 12 19 5 12"></polyline>
            </svg>
          </div>
        </div>

        {/* Step 2 — Ready to sell */}
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="w-5 h-5 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center text-[10px] font-bold">2</span>
            <span className="text-[12px] font-semibold text-slate-700">Ready to sell</span>
            <span className="text-[10px] text-slate-400">— what comes out</span>
          </div>
          <YieldRow
            qty={sellableQty}
            unit={sellableUnit}
            onQtyChange={onSellableQtyChange}
            onUnitChange={onSellableUnitChange}
            placeholder="0"
            emphasis
          />
        </div>
      </div>

      {/* Auto-calculated yield % + merma */}
      {raw > 0 && sellable > 0 && (
        <div className="px-4 py-3 bg-gradient-to-r from-emerald-50 to-emerald-50/40 border-t border-emerald-100 grid grid-cols-2 gap-2">
          <div>
            <div className="text-[9px] font-bold tracking-wider text-emerald-700 uppercase">Yield</div>
            <div className="text-base font-bold text-emerald-700" style={{ fontFamily: tokens.fontMono }}>
              {yieldPct.toFixed(1)}%
            </div>
          </div>
          {sameUnit && merma > 0 && (
            <div>
              <div className="text-[9px] font-bold tracking-wider text-rose-700 uppercase">Merma</div>
              <div className="text-base font-bold text-rose-700" style={{ fontFamily: tokens.fontMono }}>
                {merma.toFixed(2)} {rawUnit}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Single yield row — qty input + unit DROPDOWN (Libras/Kilos/Onzas/etc)
function YieldRow({ qty, unit, onQtyChange, onUnitChange, placeholder, emphasis }) {
  const display = qty === 0 || qty === undefined || qty === null ? '' : qty;
  return (
    <div className="flex items-stretch bg-white border border-slate-200 rounded-xl hover:border-slate-300 focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-500/10 transition-all overflow-hidden">
      <input
        type="number"
        step="any"
        value={display}
        onChange={(e) => onQtyChange(e.target.value === '' ? 0 : Number(e.target.value))}
        placeholder={placeholder || '0'}
        className={`flex-1 min-w-0 px-3 py-2.5 bg-transparent text-[15px] ${emphasis ? 'font-bold' : 'font-semibold'} text-slate-900 placeholder:text-slate-400 focus:outline-none`}
        style={{ fontFamily: tokens.fontMono }}
      />
      <div className="flex items-center pr-1.5 pl-1 border-l border-slate-100 min-w-[110px]">
        <UnitSelect value={unit || 'Libras'} onChange={onUnitChange} />
      </div>
    </div>
  );
}

function UnitSelect({ value, onChange }) {
  // Resolve aliases — if value is "Libra", show "Libras" as selected
  const resolved = resolveUnit(value);
  const canonical = resolved ? resolved.name : value;
  const isImprecise = resolved && resolved.imprecise;

  return (
    <div className="w-full">
      <select
        value={canonical || 'Libras'}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputSm} cursor-pointer bg-white`}
      >
        {UNIT_OPTIONS.map((u) => (
          <option key={u} value={u}>
            {UNITS[u].label}{UNITS[u].imprecise ? ' ⚠' : ''}
          </option>
        ))}
      </select>
      {isImprecise && (
        <div className="text-[10px] text-amber-600 mt-0.5 leading-tight">
          ⚠ Imprecise — consider weight
        </div>
      )}
    </div>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <button
      onClick={onChange}
      className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-slate-200'}`}
    >
      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-all ${checked ? 'left-[22px]' : 'left-0.5'}`} />
    </button>
  );
}

function CookingModeSelector({ mode, onChange }) {
  const options = [
    {
      id: 'passive',
      label: 'Passive',
      hint: 'Cooking does not require active employee labor',
      accent: { border: 'border-slate-900', bg: 'bg-slate-900', text: 'text-white', subtext: 'text-slate-300' },
    },
    {
      id: 'active',
      label: 'Active',
      hint: 'Employee must work during cooking',
      accent: { border: 'border-blue-600', bg: 'bg-blue-600', text: 'text-white', subtext: 'text-blue-100' },
    },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {options.map((o) => {
        const isActive = mode === o.id;
        return (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            className={`group text-left p-3 rounded-xl border-2 transition-all ${
              isActive
                ? `${o.accent.border} ${o.accent.bg} shadow-sm`
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                isActive ? 'border-white bg-white' : 'border-slate-300 bg-white'
              }`}>
                {isActive && <div className={`w-1.5 h-1.5 rounded-full ${o.id === 'active' ? 'bg-blue-600' : 'bg-slate-900'}`}></div>}
              </div>
              <span className={`text-[13px] font-bold ${isActive ? o.accent.text : 'text-slate-900'}`}>
                {o.label} Cooking
              </span>
            </div>
            <div className={`text-[11px] leading-snug ${isActive ? o.accent.subtext : 'text-slate-500'}`}>
              {o.hint}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// Compute months elapsed since a given ISO date string
function monthsSince(isoDate) {
  if (!isoDate) return null;
  const last = new Date(isoDate);
  if (isNaN(last.getTime())) return null;
  const now = new Date();
  return (now.getFullYear() - last.getFullYear()) * 12 + (now.getMonth() - last.getMonth());
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function formatDate(isoDate) {
  if (!isoDate) return 'Never';
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return 'Never';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// OverheadBlock — industry-standard overhead percentage for gas/electricity/utilities
// Default 12% of ingredient cost. User can adjust if their bills vary significantly.
function OverheadBlock({ overheadPct, ingredientCostPerUnit, utilitiesPerUnit, utilitiesTotal, ingredientsTotal, cookHours, onChange }) {
  const pct = overheadPct !== undefined ? Number(overheadPct) : 12;
  const presets = [10, 12, 15];
  const isPreset = presets.includes(pct);

  return (
    <div className="space-y-3">
      {/* Overhead percentage input */}
      <div>
        <FieldLabel>Overhead %</FieldLabel>
        <div className="grid grid-cols-4 gap-2">
          {presets.map((p) => (
            <button
              key={p}
              onClick={() => onChange(p)}
              className={`px-2 py-2.5 rounded-xl text-[13px] font-bold transition ${
                pct === p
                  ? 'bg-orange-500 text-white shadow-sm shadow-orange-500/20'
                  : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-300'
              }`}
              style={{ fontFamily: tokens.fontMono }}
            >
              {p}%
            </button>
          ))}
          <div className="relative">
            <input
              type="number"
              step="0.5"
              value={isPreset ? '' : pct}
              onChange={(e) => onChange(e.target.value === '' ? 12 : Number(e.target.value))}
              placeholder="Custom"
              className={`w-full px-2 py-2.5 rounded-xl text-[13px] font-bold text-center transition ${
                isPreset
                  ? 'bg-white border border-slate-200 text-slate-400'
                  : 'bg-orange-500 text-white shadow-sm shadow-orange-500/20'
              }`}
              style={{ fontFamily: tokens.fontMono }}
            />
            {!isPreset && pct > 0 && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-white text-[12px] font-bold pointer-events-none">%</span>
            )}
          </div>
        </div>
        <div className="text-[11px] text-slate-500 mt-1.5 leading-snug">
          Industry standard is 10–15%. This covers gas, electricity, water, and miscellaneous kitchen supplies.
        </div>
      </div>

      {/* Calculation breakdown */}
      {ingredientsTotal > 0 ? (
        <div className="p-3 rounded-xl bg-orange-50/80 border border-orange-100 space-y-1.5">
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-orange-800">
              Ingredients · {money(ingredientsTotal)} × {pct}%
            </span>
            <span className="font-semibold text-orange-900" style={{ fontFamily: tokens.fontMono }}>
              {money(utilitiesTotal)}
            </span>
          </div>
          <div className="flex items-center justify-between pt-2 mt-2 border-t border-orange-200/60">
            <span className="text-[11px] font-semibold tracking-wider text-orange-700 uppercase">Per Unit</span>
            <span className="text-lg font-bold text-orange-700" style={{ fontFamily: tokens.fontMono }}>
              {money(utilitiesPerUnit)}
            </span>
          </div>
          {cookHours > 0 && utilitiesTotal > 0 && (
            <div className="flex items-center justify-between text-[10px] text-orange-700/80 pt-1">
              <span>≈ {money(utilitiesTotal / cookHours)} per cook hour</span>
              <span className="italic">sanity check</span>
            </div>
          )}
        </div>
      ) : (
        <div className="p-3 rounded-xl bg-slate-50 border border-dashed border-slate-200 text-center">
          <div className="text-[11px] text-slate-500 leading-relaxed">
            Add ingredients above to see the overhead calculation
          </div>
        </div>
      )}
    </div>
  );
}

function AutoSaveBadge() {
  const { t } = useT();
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-[11px] font-medium">
      <span className="relative flex w-1.5 h-1.5">
        <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping"></span>
        <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
      </span>
      <span className="hidden sm:inline">{t('action.autoSaved')}</span>
    </div>
  );
}

function BreakdownRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-50 transition">
      <span className="text-[13px] text-slate-600">{label}</span>
      <span className="text-[14px] font-semibold text-slate-900" style={{ fontFamily: tokens.fontMono }}>{value}</span>
    </div>
  );
}